import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ShieldCheck, LogOut, Search, Trash2, Star, Eye, CheckCircle2,
  Clock, Building2, Users, Sparkles, Lock, User as UserIcon, Loader2, Filter, Pencil, Shuffle, RefreshCw, Crown, Heart, Download,
  LayoutDashboard, Settings as SettingsIcon, Menu as MenuIcon, Megaphone, CreditCard, Tag, X, BadgeCheck, MoreHorizontal, ChevronDown, MessageCircle, Mail, Wallet, StickyNote, Newspaper, ImagePlus, Plus, Save, GripVertical, MousePointerClick, Send,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { useToast } from "../hooks/use-toast";
import { formatPrice, CITIES } from "../mock/mockData";
import { useCategories } from "../context/CategoriesContext";
import { DeleteAllFirms } from "../components/DeleteAllFirms";
import { BulkFirmImport } from "../components/BulkFirmImport";
import { BulkApproveBar } from "../components/BulkApproveBar";
import { SiteVisits } from "../components/SiteVisits";
import { SmsReport } from "../components/SmsReport";
import { AdminAddFirm } from "../components/AdminAddFirm";
import { FileSpreadsheet, AlertTriangle, TrendingUp, MessageSquare } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ icon: Icon, label, value, color, onClick, active, badge }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={!onClick}
    data-testid={onClick ? `stat-card-${label}` : undefined}
    className={`text-left bg-white rounded-2xl border p-5 flex items-center gap-4 w-full transition-all ${onClick ? "hover:shadow-md hover:-translate-y-0.5 cursor-pointer" : "cursor-default"} ${active ? "border-[#9E2A3B] ring-2 ring-[#9E2A3B]/15" : "border-[#F0DDE0]"}`}
  >
    <div className="relative w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${color}1a` }}>
      <Icon className="w-6 h-6" style={{ color }} />
      {badge > 0 && <span className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1 rounded-full bg-[#9E2A3B] text-white text-[11px] font-semibold flex items-center justify-center">{badge}</span>}
    </div>
    <div className="min-w-0">
      <p className="font-display text-2xl text-[#2A2224]">{value}</p>
      <p className="text-sm text-[#7C6E70] leading-tight">{label}</p>
    </div>
  </button>
);

const AdminPanel = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { categories: siteCategories, reloadCategories } = useCategories();
  const catName = (slug) => siteCategories.find((c) => c.slug === slug)?.name || slug;
  const [catForm, setCatForm] = useState({ id: null, name: "", group: "firma", image: "", order: 0 });
  const [catBusy, setCatBusy] = useState(false);
  const [token, setToken] = useState(() => localStorage.getItem("ds_admin_token"));
  const [role, setRole] = useState(() => localStorage.getItem("ds_admin_role") || "full");
  const [meName, setMeName] = useState(() => localStorage.getItem("ds_admin_name") || "Yönetici");
  const [creds, setCreds] = useState({ username: "", password: "" });
  const [logging, setLogging] = useState(false);
  const [adPrices, setAdPrices] = useState({ "1-ay": "", "3-ay": "", "6-ay": "", "12-ay": "" });
  const [savingAd, setSavingAd] = useState(false);
  const [hlPrices, setHlPrices] = useState({ "1-ay": "", "3-ay": "", "6-ay": "", "12-ay": "" });
  const [savingHl, setSavingHl] = useState(false);
  const [campaigns, setCampaigns] = useState({ featured: { enabled: false, start: "", end: "", percent: "" }, highlight: { enabled: false, start: "", end: "", percent: "" } });
  const [savingCampaign, setSavingCampaign] = useState("");
  const [banners, setBanners] = useState([]);
  const [bannerForm, setBannerForm] = useState({ title: "", link: "", image: "", active: true, city: "", start: "", end: "" });
  const [shuffleSecs, setShuffleSecs] = useState(10);
  const [savingShuffle, setSavingShuffle] = useState(false);
  const [bannerUploading, setBannerUploading] = useState(false);
  const [bannerSaving, setBannerSaving] = useState(false);
  const bannerFileRef = useRef(null);
  const [membership, setMembership] = useState({ price: "", priceMekan: "", priceFirma: "", durationMonths: "12", premiumImageLimit: "20", extraCategoryPrice: "", extraCategoryPrice2: "", extraCategoryPrice3: "" });
  const [savingMem, setSavingMem] = useState(false);
  const [iyzico, setIyzico] = useState({ environment: "sandbox", apiKey: "", secretKey: "", configured: false, apiKeyMasked: "" });
  const [sms, setSms] = useState({ usercode: "", password: "", msgheader: "", enabled: true, configured: false, passwordMasked: "", template: "" });
  const [savingSms, setSavingSms] = useState(false);
  const [smsTestPhone, setSmsTestPhone] = useState("");
  const [savingIyz, setSavingIyz] = useState(false);
  const [payments, setPayments] = useState([]);
  const [payLinkForm, setPayLinkForm] = useState({ name: "", phone: "", email: "", amount: "", description: "" });
  const [creatingPayLink, setCreatingPayLink] = useState(false);
  const [paymentLinks, setPaymentLinks] = useState([]);
  const [lastPayLink, setLastPayLink] = useState(null);
  const [adRequests, setAdRequests] = useState([]);
  const [adReqFilter, setAdReqFilter] = useState("pending");
  const [adReqKind, setAdReqKind] = useState("all");
  const [openMenu, setOpenMenu] = useState("uyeler");
  const [notesFirm, setNotesFirm] = useState(null);
  const [notesList, setNotesList] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [notesBusy, setNotesBusy] = useState(false);
  const [discountCodes, setDiscountCodes] = useState([]);
  const [dcReport, setDcReport] = useState(null);
  const [dcReportOpen, setDcReportOpen] = useState({});
  const [dcForm, setDcForm] = useState({ code: "", type: "percent", value: "", maxUses: "", expiresAt: "", appliesTo: [] });
  const [dcBusy, setDcBusy] = useState(false);
  const [articles, setArticles] = useState([]);
  const [artForm, setArtForm] = useState({ id: null, title: "", slug: "", image: "", excerpt: "", content: "", linkUrl: "", order: 0, active: true });
  const [artBusy, setArtBusy] = useState(false);
  const [claimRequests, setClaimRequests] = useState([]);
  const [claimFilter, setClaimFilter] = useState("pending");
  const [pendingClaims, setPendingClaims] = useState([]);
  const [homePanel, setHomePanel] = useState(null);
  const prevClaimCount = useRef(null);
  const prevAdReqCount = useRef(null);

  const loadPendingClaims = useCallback(() => {
    if (!token) return;
    axios.get(`${API}/admin/claim-requests`, { headers, params: { status: "pending" } })
      .then((r) => {
        const list = r.data || [];
        if (prevClaimCount.current !== null && list.length > prevClaimCount.current) {
          toast({ title: "Yeni sahiplenme talebi", description: `${list.length - prevClaimCount.current} yeni sahiplenme talebi geldi.` });
        }
        prevClaimCount.current = list.length;
        setPendingClaims(list);
      })
      .catch(() => {});
    // eslint-disable-next-line
  }, [token]);

  useEffect(() => {
    if (!token) return;
    loadPendingClaims();
    const id = setInterval(loadPendingClaims, 30000);
    return () => clearInterval(id);
  }, [token, loadPendingClaims]);

  const loadClaims = useCallback((status) => {
    axios.get(`${API}/admin/claim-requests`, { headers, params: status && status !== "all" ? { status } : {} })
      .then((r) => setClaimRequests(r.data || []))
      .catch(() => {});
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (token) loadClaims(claimFilter);
    // eslint-disable-next-line
  }, [claimFilter]);
  const [featureOptions, setFeatureOptions] = useState([]);
  const [featureOptionsFirma, setFeatureOptionsFirma] = useState([]);
  const [newFeature, setNewFeature] = useState("");
  const [newFeatureFirma, setNewFeatureFirma] = useState("");
  const [savingFeat, setSavingFeat] = useState(false);
  const [savingFeatFirma, setSavingFeatFirma] = useState(false);
  const [payFrom, setPayFrom] = useState("");
  const [payTo, setPayTo] = useState("");
  const [activity, setActivity] = useState([]);

  const [stats, setStats] = useState({ total: 0, approved: 0, pending: 0, featured: 0, requests: 0 });
  const [firms, setFirms] = useState([]);
  const [requests, setRequests] = useState([]);
  const [featureAddRequests, setFeatureAddRequests] = useState([]);
  const [featReqFilter, setFeatReqFilter] = useState("pending");
  const [featureEdits, setFeatureEdits] = useState({});
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("hepsi");
  const [selectedFirms, setSelectedFirms] = useState([]);
  const [rotationHours, setRotationHours] = useState("24");
  const [savingRot, setSavingRot] = useState(false);
  const [shuffling, setShuffling] = useState(false);
  const [editPriceId, setEditPriceId] = useState(null);
  const [priceInput, setPriceInput] = useState("");
  const [section, setSection] = useState("anasayfa");
  const [catFilter, setCatFilter] = useState("hepsi");
  const [cityFilterA, setCityFilterA] = useState("hepsi");
  const [featFilter, setFeatFilter] = useState("hepsi");
  const [adminUsers, setAdminUsers] = useState([]);
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [expandedMember, setExpandedMember] = useState(null);
  const [memberDetail, setMemberDetail] = useState(null);
  const [memberDetailOpen, setMemberDetailOpen] = useState(false);
  const [memberDetailLoading, setMemberDetailLoading] = useState(false);
  const [memberQuery, setMemberQuery] = useState("");
  const [memberCity, setMemberCity] = useState("hepsi");
  const [newUser, setNewUser] = useState({ name: "", username: "", password: "", role: "full" });
  const [creatingUser, setCreatingUser] = useState(false);

  const loadAdminUsers = useCallback(async () => {
    if (!token) return;
    try {
      const r = await axios.get(`${API}/admin/users`, { headers });
      setAdminUsers(r.data || []);
    } catch { setAdminUsers([]); }
    // eslint-disable-next-line
  }, [token]);

  const createAdminUser = async () => {
    if (!newUser.username.trim() || !newUser.password.trim()) {
      toast({ title: "Eksik bilgi", description: "Kullanıcı adı ve şifre gerekli." });
      return;
    }
    setCreatingUser(true);
    try {
      await axios.post(`${API}/admin/users`, newUser, { headers });
      toast({ title: "Kullanıcı oluşturuldu", description: newUser.username });
      setNewUser({ name: "", username: "", password: "", role: "full" });
      loadAdminUsers();
    } catch (e) {
      toast({ title: "Hata", description: e?.response?.data?.detail || "Oluşturulamadı." });
    } finally { setCreatingUser(false); }
  };

  const deleteAdminUser = async (u) => {
    if (!window.confirm(`"${u.username}" kullanıcısını silmek istediğinize emin misiniz?`)) return;
    try {
      await axios.delete(`${API}/admin/users/${u.id}`, { headers });
      toast({ title: "Silindi", description: u.username });
      loadAdminUsers();
    } catch { toast({ title: "Hata", description: "Silinemedi." }); }
  };

  const updateUserPassword = async (u) => {
    const pw = window.prompt(`"${u.username}" için yeni şifre girin:`);
    if (!pw) return;
    try {
      await axios.put(`${API}/admin/users/${u.id}/password`, { password: pw }, { headers });
      toast({ title: "Şifre güncellendi", description: u.username });
    } catch { toast({ title: "Hata", description: "Şifre güncellenemedi." }); }
  };

  const loadAdPrices = useCallback(async () => {
    if (!token) return;
    try {
      const r = await axios.get(`${API}/admin/ad-packages`, { headers });
      setAdPrices({ "1-ay": String(r.data["1-ay"] || ""), "3-ay": String(r.data["3-ay"] || ""), "6-ay": String(r.data["6-ay"] || ""), "12-ay": String(r.data["12-ay"] || "") });
    } catch {}
    try {
      const r2 = await axios.get(`${API}/admin/highlight-packages`, { headers });
      setHlPrices({ "1-ay": String(r2.data["1-ay"] || ""), "3-ay": String(r2.data["3-ay"] || ""), "6-ay": String(r2.data["6-ay"] || ""), "12-ay": String(r2.data["12-ay"] || "") });
    } catch {}
    // eslint-disable-next-line
  }, [token]);

  const saveHlPrices = async () => {
    setSavingHl(true);
    try {
      const prices = Object.fromEntries(Object.entries(hlPrices).map(([k, v]) => [k, Number(v) || 0]));
      await axios.put(`${API}/admin/highlight-packages`, { prices }, { headers });
      toast({ title: "Kaydedildi", description: "Vurgulu ilan fiyatları güncellendi." });
    } catch (e) { toast({ title: "Hata", description: e?.response?.data?.detail || "Kaydedilemedi." }); }
    finally { setSavingHl(false); }
  };

  const saveAdPrices = async () => {
    setSavingAd(true);
    try {
      const prices = Object.fromEntries(Object.entries(adPrices).map(([k, v]) => [k, Number(v) || 0]));
      await axios.put(`${API}/admin/ad-packages`, { prices }, { headers });
      toast({ title: "Kaydedildi", description: "Paket fiyatları güncellendi ve üye panellerinde görünecek." });
    } catch (e) { toast({ title: "Hata", description: e?.response?.data?.detail || "Kaydedilemedi." }); }
    finally { setSavingAd(false); }
  };

  const loadCampaigns = useCallback(async () => {
    if (!token) return;
    try {
      const r = await axios.get(`${API}/admin/campaigns`, { headers });
      const norm = (c) => ({ enabled: !!c?.enabled, start: c?.start || "", end: c?.end || "", percent: c?.percent ? String(c.percent) : "" });
      setCampaigns({ featured: norm(r.data?.featured), highlight: norm(r.data?.highlight) });
    } catch {}
    // eslint-disable-next-line
  }, [token]);

  const saveCampaign = async (kind) => {
    setSavingCampaign(kind);
    try {
      const c = campaigns[kind];
      const payload = { [kind]: { enabled: c.enabled, start: c.start, end: c.end, percent: Number(c.percent) || 0 } };
      const r = await axios.put(`${API}/admin/campaigns`, payload, { headers });
      const norm = (x) => ({ enabled: !!x?.enabled, start: x?.start || "", end: x?.end || "", percent: x?.percent ? String(x.percent) : "" });
      setCampaigns({ featured: norm(r.data?.featured), highlight: norm(r.data?.highlight) });
      toast({ title: "Kaydedildi", description: "Kampanya güncellendi." });
    } catch (e) { toast({ title: "Hata", description: e?.response?.data?.detail || "Kaydedilemedi." }); }
    finally { setSavingCampaign(""); }
  };

  const loadBanners = useCallback(async () => {
    if (!token) return;
    try { const r = await axios.get(`${API}/admin/banners`, { headers }); setBanners(r.data || []); } catch {}
    try { const s = await axios.get(`${API}/banner-settings`); setShuffleSecs(Number(s.data?.shuffleSeconds) || 10); } catch {}
    // eslint-disable-next-line
  }, [token]);

  const saveShuffleSecs = async (secs) => {
    setSavingShuffle(true);
    try {
      const r = await axios.put(`${API}/admin/banner-settings`, { shuffleSeconds: secs }, { headers });
      setShuffleSecs(Number(r.data?.shuffleSeconds) || secs);
      toast({ title: "Kaydedildi", description: `Reklamlar üye panelinde ${secs} saniyede bir karışacak.` });
    } catch (e) { toast({ title: "Hata", description: e?.response?.data?.detail || "Kaydedilemedi." }); }
    finally { setSavingShuffle(false); }
  };

  const uploadBannerImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBannerUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/admin/banners/upload`, fd, { headers: { ...headers, "Content-Type": "multipart/form-data" } });
      setBannerForm((p) => ({ ...p, image: r.data.url }));
    } catch (err) { toast({ title: "Hata", description: "Görsel yüklenemedi." }); }
    finally { setBannerUploading(false); if (bannerFileRef.current) bannerFileRef.current.value = ""; }
  };

  const createBanner = async () => {
    if (!bannerForm.image) { toast({ title: "Görsel gerekli", description: "Lütfen bir banner görseli yükleyin." }); return; }
    setBannerSaving(true);
    try {
      await axios.post(`${API}/admin/banners`, { ...bannerForm, order: banners.length }, { headers });
      toast({ title: "Eklendi", description: "Reklam banner'ı üye panelinde yayınlandı." });
      setBannerForm({ title: "", link: "", image: "", active: true, city: "", start: "", end: "" });
      loadBanners();
    } catch (e) { toast({ title: "Hata", description: e?.response?.data?.detail || "Eklenemedi." }); }
    finally { setBannerSaving(false); }
  };

  const toggleBanner = async (b) => {
    try { await axios.put(`${API}/admin/banners/${b.id}`, { active: !b.active }, { headers }); loadBanners(); }
    catch { toast({ title: "Hata", description: "Güncellenemedi." }); }
  };

  const deleteBanner = async (b) => {
    try { await axios.delete(`${API}/admin/banners/${b.id}`, { headers }); setBanners((p) => p.filter((x) => x.id !== b.id)); }
    catch { toast({ title: "Hata", description: "Silinemedi." }); }
  };


  const loadActivity = useCallback(async () => {
    if (!token) return;
    try {
      const r = await axios.get(`${API}/admin/activity`, { headers });
      setActivity(r.data || []);
    } catch {}
    // eslint-disable-next-line
  }, [token]);

  const setFeaturedDays = async (f, days) => {
    try {
      await axios.patch(`${API}/admin/firms/${f.id}/featured`, { featured: true, days: days || null }, { headers });
      toast({ title: "Güncellendi", description: days ? `${f.name} · ${days} gün öne çıkarıldı` : `${f.name} · süresiz öne çıkarıldı` });
      load();
    } catch { toast({ title: "Hata", description: "İşlem yapılamadı." }); }
  };

  const startEditPrice = (f) => { setEditPriceId(f.id); setPriceInput(f.priceFrom ? String(f.priceFrom) : ""); };

  const savePrice = async (f) => {
    try {
      await axios.put(`${API}/admin/firms/${f.id}`, { priceFrom: Number(priceInput) || 0 }, { headers });
      setEditPriceId(null);
      toast({ title: "Fiyat güncellendi", description: f.name });
      load();
    } catch { toast({ title: "Hata", description: "Fiyat kaydedilemedi." }); }
  };

  const headers = { Authorization: `Bearer ${token}` };

  const deleteMember = async (m) => {
    if (!window.confirm(`"${m.name || m.phone}" üyesini silmek istediğinize emin misiniz? Hesabı, favorileri, ajandası, bütçesi ve davetli listesi kalıcı olarak silinir.`)) return;
    try {
      await axios.delete(`${API}/admin/members/${m.id}`, { headers });
      setMembers((prev) => prev.filter((x) => x.id !== m.id));
      toast({ title: "Üye silindi", description: m.name || m.phone });
    } catch (e) {
      toast({ title: "Silinemedi", description: e.response?.data?.detail || "Hata", variant: "destructive" });
    }
  };

  const loadMembers = useCallback(async () => {
    if (!token) return;
    setMembersLoading(true);
    try {
      const r = await axios.get(`${API}/admin/members`, { headers: { Authorization: `Bearer ${token}` } });
      setMembers(r.data || []);
    } catch { setMembers([]); }
    finally { setMembersLoading(false); }
  }, [token]);

  useEffect(() => {
    if (section === "uyeler" || section === "uyeler-yeni") loadMembers();
  }, [section, loadMembers]);

  const isYeniMembers = section === "uyeler-yeni";
  const exportMembersCsv = () => {
    const rows = [["Ad Soyad", "Telefon", "Şehir", "Kayıt Tarihi", "Favori Sayısı", "Favoriler"]];
    filteredMembers.forEach((m) => {
      const favs = (m.favorites || []).map((f) => f.name).join("; ");
      const d = m.createdAt ? new Date(m.createdAt).toLocaleDateString("tr-TR") : "";
      rows.push([m.name || "", m.phone || "", m.city || "", d, String(m.favoritesCount || 0), favs]);
    });
    const csv = "\ufeff" + rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `uyeler-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const memberCityOptions = Array.from(new Set(members.map((m) => m.city).filter(Boolean))).sort((a, b) => a.localeCompare(b, "tr"));
  const NEW_MEMBER_DAYS = 30;
  const newCutoff = Date.now() - NEW_MEMBER_DAYS * 24 * 60 * 60 * 1000;
  const filteredMembers = members.filter((m) => {
    const q = memberQuery.trim().toLowerCase();
    const okQ = !q || (m.name || "").toLowerCase().includes(q) || (m.phone || "").includes(q);
    const okC = memberCity === "hepsi" || m.city === memberCity;
    const okNew = !isYeniMembers || (m.createdAt && new Date(m.createdAt).getTime() >= newCutoff);
    return okQ && okC && okNew;
  });

  const openMemberDetail = async (m) => {
    setMemberDetailOpen(true);
    setMemberDetail(null);
    setMemberDetailLoading(true);
    try {
      const r = await axios.get(`${API}/admin/members/${m.id}/detail`, { headers });
      setMemberDetail(r.data);
    } catch (e) { toast({ title: "Hata", description: "Üye bilgisi alınamadı." }); }
    finally { setMemberDetailLoading(false); }
  };

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (statusFilter !== "hepsi") params.status = statusFilter;
      if (catFilter !== "hepsi") params.category = catFilter;
      if (cityFilterA !== "hepsi") params.city = cityFilterA;
      const [s, f, r] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/firms`, { headers, params }),
        axios.get(`${API}/admin/feature-requests`, { headers }),
      ]);
      setStats(s.data);
      setFirms(f.data);
      setRequests(r.data);
      axios.get(`${API}/admin/claim-requests`, { headers, params: claimFilter && claimFilter !== "all" ? { status: claimFilter } : {} }).then((cr) => setClaimRequests(cr.data || [])).catch(() => {});
      axios.get(`${API}/admin/feature-add-requests`, { headers }).then((fr) => setFeatureAddRequests(fr.data || [])).catch(() => {});
    } catch (e) {
      if (e?.response?.status === 401 || e?.response?.status === 403) {
        localStorage.removeItem("ds_admin_token");
        setToken(null);
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, q, statusFilter, catFilter, cityFilterA]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/admin/settings`, { headers })
      .then((r) => setRotationHours(String(r.data.rotationHours)))
      .catch(() => {});
    loadAdminUsers();
    loadAdPrices();
    loadCampaigns();
    loadBanners();
    loadActivity();
    loadMembership();
    loadIyzico();
    loadPayments();
    loadPaymentLinks();
    loadAdRequests();
    loadFeatures();
    loadDiscountCodes();
    loadArticles();
    // eslint-disable-next-line
  }, [token]);

  const saveRotation = async () => {
    setSavingRot(true);
    try {
      await axios.put(`${API}/admin/settings`, { rotationHours: Number(rotationHours) }, { headers });
      toast({ title: "Kaydedildi", description: "Sıralama aralığı güncellendi." });
    } catch { toast({ title: "Hata", description: "Kaydedilemedi." }); }
    finally { setSavingRot(false); }
  };

  const [advisorWa, setAdvisorWa] = useState("");
  const [advisorBusy, setAdvisorBusy] = useState(false);
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/admin/advisor`, { headers })
      .then((r) => setAdvisorWa(r.data.whatsapp || ""))
      .catch(() => {});
    // eslint-disable-next-line
  }, [token]);
  const saveAdvisor = async () => {
    setAdvisorBusy(true);
    try {
      await axios.put(`${API}/admin/advisor`, { whatsapp: advisorWa.trim() }, { headers });
      toast({ title: "Kaydedildi", description: "Danışman WhatsApp numarası güncellendi." });
    } catch (e) { toast({ title: "Hata", description: e?.response?.data?.detail || "Kaydedilemedi." }); }
    finally { setAdvisorBusy(false); }
  };

  const loadMembership = useCallback(async () => {
    if (!token) return;
    try {
      const r = await axios.get(`${API}/admin/membership-settings`, { headers });
      setMembership({ price: String(r.data.price || ""), priceMekan: String(r.data.priceMekan || ""), priceFirma: String(r.data.priceFirma || ""), durationMonths: String(r.data.durationMonths || "12"), premiumImageLimit: String(r.data.premiumImageLimit || "20"), extraCategoryPrice: String(r.data.extraCategoryPrice || ""), extraCategoryPrice2: String(r.data.extraCategoryPrice2 || ""), extraCategoryPrice3: String(r.data.extraCategoryPrice3 || "") });
    } catch {}
    // eslint-disable-next-line
  }, [token]);

  const loadIyzico = useCallback(async () => {
    if (!token) return;
    try {
      const r = await axios.get(`${API}/admin/iyzico-settings`, { headers });
      setIyzico((p) => ({ ...p, environment: r.data.environment || "sandbox", configured: !!r.data.configured, apiKeyMasked: r.data.apiKeyMasked || "" }));
    } catch {}
    try {
      const s = await axios.get(`${API}/admin/sms-settings`, { headers });
      setSms((p) => ({ ...p, usercode: s.data.usercode || "", msgheader: s.data.msgheader || "", enabled: !!s.data.enabled, configured: !!s.data.configured, passwordMasked: s.data.passwordMasked || "", template: s.data.template || "" }));
    } catch {}
    // eslint-disable-next-line
  }, [token]);

  const saveSms = async () => {
    if (!sms.usercode || !sms.msgheader || (!sms.configured && !sms.password)) {
      toast({ title: "Eksik bilgi", description: "Kullanıcı kodu, şifre ve başlık (msgheader) girin." });
      return;
    }
    setSavingSms(true);
    try {
      await axios.put(`${API}/admin/sms-settings`, { usercode: sms.usercode, password: sms.password || "", msgheader: sms.msgheader, enabled: sms.enabled, template: sms.template }, { headers });
      toast({ title: "Kaydedildi", description: "Netgsm SMS ayarları güncellendi." });
      setSms((p) => ({ ...p, password: "", configured: true }));
      loadIyzico();
    } catch (e) { toast({ title: "Hata", description: e?.response?.data?.detail || "Kaydedilemedi." }); }
    finally { setSavingSms(false); }
  };

  const testSms = async () => {
    if (!smsTestPhone.trim()) { toast({ title: "Numara girin", description: "Test için bir numara girin (05XX...)." }); return; }
    try {
      await axios.post(`${API}/admin/sms-settings/test`, { phone: smsTestPhone.trim() }, { headers });
      toast({ title: "Test SMS gönderildi", description: `${smsTestPhone} numarasına test SMS iletildi.` });
    } catch (e) { toast({ title: "Gönderilemedi", description: e?.response?.data?.detail || "SMS gönderilemedi." }); }
  };

  const loadPayments = useCallback(async () => {
    if (!token) return;
    try {
      const r = await axios.get(`${API}/admin/payments`, { headers });
      setPayments(r.data || []);
    } catch {}
    // eslint-disable-next-line
  }, [token]);

  const loadPaymentLinks = useCallback(async () => {
    if (!token) return;
    try {
      const r = await axios.get(`${API}/admin/payment-links`, { headers });
      setPaymentLinks(r.data || []);
    } catch {}
    // eslint-disable-next-line
  }, [token]);

  const loadAdRequests = useCallback(async () => {
    if (!token) return;
    try {
      const r = await axios.get(`${API}/admin/ad-requests`, { headers });
      const list = r.data || [];
      const pending = list.filter((x) => (x.status || "pending") === "pending").length;
      if (prevAdReqCount.current !== null && pending > prevAdReqCount.current) {
        toast({ title: "Yeni reklam talebi", description: `${pending - prevAdReqCount.current} yeni reklam talebi geldi.` });
      }
      prevAdReqCount.current = pending;
      setAdRequests(list);
    } catch {}
    // eslint-disable-next-line
  }, [token]);

  useEffect(() => {
    if (!token) return;
    const id = setInterval(loadAdRequests, 30000);
    return () => clearInterval(id);
  }, [token, loadAdRequests]);

  const markAdRequest = async (req, status) => {
    try {
      await axios.post(`${API}/admin/ad-requests/${req.id}/status`, { status }, { headers });
      toast({ title: "Güncellendi", description: status === "done" ? "Talep tamamlandı olarak işaretlendi." : status === "rejected" ? "Talep reddedildi." : "Talep bekliyor." });
      loadAdRequests();
    } catch { toast({ title: "Hata", description: "İşlem yapılamadı." }); }
  };

  const openNotes = async (f) => {
    setNotesFirm(f);
    setNotesList([]);
    setNewNote("");
    try {
      const r = await axios.get(`${API}/admin/firms/${f.id}/notes`, { headers });
      setNotesList(r.data || []);
    } catch {}
  };

  const addNote = async () => {
    if (!newNote.trim() || !notesFirm) return;
    setNotesBusy(true);
    try {
      const r = await axios.post(`${API}/admin/firms/${notesFirm.id}/notes`, { text: newNote.trim() }, { headers });
      setNotesList((p) => [r.data, ...p]);
      setNewNote("");
      setFirms((prev) => prev.map((x) => x.id === notesFirm.id ? { ...x, noteCount: (x.noteCount || 0) + 1 } : x));
      toast({ title: "Not eklendi" });
    } catch (e) { toast({ title: "Hata", description: e?.response?.data?.detail || "Not eklenemedi." }); }
    finally { setNotesBusy(false); }
  };

  const deleteNote = async (noteId) => {
    if (!notesFirm) return;
    try {
      await axios.delete(`${API}/admin/firms/${notesFirm.id}/notes/${noteId}`, { headers });
      setNotesList((p) => p.filter((n) => n.id !== noteId));
      setFirms((prev) => prev.map((x) => x.id === notesFirm.id ? { ...x, noteCount: Math.max(0, (x.noteCount || 1) - 1) } : x));
    } catch { toast({ title: "Hata", description: "Not silinemedi." }); }
  };

  const loadDiscountCodes = useCallback(async () => {
    if (!token) return;
    try {
      const r = await axios.get(`${API}/admin/discount-codes`, { headers });
      setDiscountCodes(r.data || []);
    } catch {}
    try {
      const rep = await axios.get(`${API}/admin/discount-codes/report`, { headers });
      setDcReport(rep.data || null);
    } catch {}
    // eslint-disable-next-line
  }, [token]);

  const createDiscount = async () => {
    if (!dcForm.code.trim() || !(Number(dcForm.value) > 0)) {
      toast({ title: "Eksik bilgi", description: "Kod ve geçerli bir indirim değeri girin." });
      return;
    }
    setDcBusy(true);
    try {
      await axios.post(`${API}/admin/discount-codes`, {
        code: dcForm.code.trim(),
        type: dcForm.type,
        value: Number(dcForm.value),
        active: true,
        maxUses: dcForm.maxUses ? Number(dcForm.maxUses) : null,
        expiresAt: dcForm.expiresAt ? new Date(dcForm.expiresAt).toISOString() : null,
        appliesTo: dcForm.appliesTo,
      }, { headers });
      toast({ title: "Kod oluşturuldu" });
      setDcForm({ code: "", type: "percent", value: "", maxUses: "", expiresAt: "", appliesTo: [] });
      loadDiscountCodes();
    } catch (e) { toast({ title: "Hata", description: e?.response?.data?.detail || "Oluşturulamadı." }); }
    finally { setDcBusy(false); }
  };

  const toggleDiscount = async (d) => {
    try {
      await axios.put(`${API}/admin/discount-codes/${d.id}`, {
        code: d.code, type: d.type, value: d.value, active: !d.active,
        maxUses: d.maxUses || null, expiresAt: d.expiresAt || null, appliesTo: d.appliesTo || [],
      }, { headers });
      loadDiscountCodes();
    } catch (e) { toast({ title: "Hata", description: e?.response?.data?.detail || "Güncellenemedi." }); }
  };

  const deleteDiscount = async (id) => {
    try {
      await axios.delete(`${API}/admin/discount-codes/${id}`, { headers });
      loadDiscountCodes();
    } catch { toast({ title: "Hata", description: "Silinemedi." }); }
  };

  const loadArticles = useCallback(async () => {
    if (!token) return;
    try {
      const r = await axios.get(`${API}/admin/articles`, { headers });
      setArticles(r.data || []);
    } catch {}
    // eslint-disable-next-line
  }, [token]);

  const resetArtForm = () => setArtForm({ id: null, title: "", slug: "", image: "", excerpt: "", content: "", linkUrl: "", order: 0, active: true });

  const artImageUpload = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const maxW = 900;
        const scale = Math.min(1, maxW / img.width);
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        setArtForm((f) => ({ ...f, image: canvas.toDataURL("image/jpeg", 0.75) }));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const saveArticle = async () => {
    if (!artForm.title.trim()) {
      toast({ title: "Eksik bilgi", description: "Başlık zorunludur." });
      return;
    }
    setArtBusy(true);
    try {
      const payload = {
        title: artForm.title.trim(),
        image: artForm.image || "",
        excerpt: artForm.excerpt || "",
        content: artForm.content || "",
        linkUrl: artForm.linkUrl || "",
        order: Number(artForm.order) || 0,
        active: !!artForm.active,
      };
      if (artForm.id) {
        await axios.put(`${API}/admin/articles/${artForm.id}`, payload, { headers });
        toast({ title: "Güncellendi" });
      } else {
        await axios.post(`${API}/admin/articles`, payload, { headers });
        toast({ title: "Yazı eklendi" });
      }
      resetArtForm();
      loadArticles();
    } catch (e) { toast({ title: "Hata", description: e?.response?.data?.detail || "Kaydedilemedi." }); }
    finally { setArtBusy(false); }
  };

  const editArticle = (a) => setArtForm({ id: a.id, title: a.title, slug: a.slug || "", image: a.image || "", excerpt: a.excerpt || "", content: a.content || "", linkUrl: a.linkUrl || "", order: a.order || 0, active: a.active });

  const deleteArticle = async (id) => {
    try {
      await axios.delete(`${API}/admin/articles/${id}`, { headers });
      if (artForm.id === id) resetArtForm();
      loadArticles();
    } catch { toast({ title: "Hata", description: "Silinemedi." }); }
  };

  const createPaymentLink = async () => {
    if (!payLinkForm.name.trim() || !payLinkForm.description.trim() || !(Number(payLinkForm.amount) > 0)) {
      toast({ title: "Eksik bilgi", description: "Ad, tutar ve açıklama zorunlu." });
      return;
    }
    setCreatingPayLink(true);
    try {
      const r = await axios.post(`${API}/admin/payment-links`, {
        name: payLinkForm.name.trim(),
        phone: payLinkForm.phone.trim(),
        email: payLinkForm.email.trim(),
        amount: Number(payLinkForm.amount),
        description: payLinkForm.description.trim(),
        returnBase: window.location.origin,
      }, { headers });
      setLastPayLink({ url: r.data.paymentPageUrl, phone: payLinkForm.phone.trim(), name: payLinkForm.name.trim(), amount: payLinkForm.amount });
      toast({ title: "Ödeme linki oluşturuldu", description: "Linki kopyalayıp firmaya gönderebilirsiniz." });
      setPayLinkForm({ name: "", phone: "", email: "", amount: "", description: "" });
      loadPaymentLinks();
    } catch (e) {
      toast({ title: "Hata", description: e?.response?.data?.detail || "Ödeme linki oluşturulamadı." });
    } finally { setCreatingPayLink(false); }
  };

  const copyPayLink = (url) => {
    navigator.clipboard?.writeText(url).then(
      () => toast({ title: "Kopyalandı", description: "Ödeme linki panoya kopyalandı." }),
      () => toast({ title: "Kopyalanamadı", description: "Linki elle kopyalayın." })
    );
  };

  const waPayLink = (phone, url, name, amount) => {
    const digits = (phone || "").replace(/\D/g, "");
    const msg = `Merhaba${name ? " " + name : ""}, ${amount ? amount + " TL " : ""}ödemenizi şu güvenli link üzerinden yapabilirsiniz: ${url}`;
    const base = digits ? `https://wa.me/${digits.startsWith("90") ? digits : "90" + digits.replace(/^0/, "")}` : "https://wa.me/";
    window.open(`${base}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const loadFeatures = useCallback(async () => {
    if (!token) return;
    try {
      const [m, f] = await Promise.all([
        axios.get(`${API}/admin/feature-options`, { headers, params: { group: "mekan" } }),
        axios.get(`${API}/admin/feature-options`, { headers, params: { group: "firma" } }),
      ]);
      setFeatureOptions(m.data?.options || []);
      setFeatureOptionsFirma(f.data?.options || []);
    } catch {}
    // eslint-disable-next-line
  }, [token]);

  const persistFeatures = async (list, group = "mekan") => {
    const setBusy = group === "firma" ? setSavingFeatFirma : setSavingFeat;
    const setList = group === "firma" ? setFeatureOptionsFirma : setFeatureOptions;
    setBusy(true);
    try {
      const r = await axios.put(`${API}/admin/feature-options`, { options: list }, { headers, params: { group } });
      setList(r.data?.options || []);
      toast({ title: "Kaydedildi", description: "Özellik listesi güncellendi." });
    } catch (e) { toast({ title: "Hata", description: e?.response?.data?.detail || "Kaydedilemedi." }); }
    finally { setBusy(false); }
  };
  const addFeature = () => {
    const v = newFeature.trim();
    if (!v || featureOptions.includes(v)) { setNewFeature(""); return; }
    persistFeatures([...featureOptions, v], "mekan");
    setNewFeature("");
  };
  const removeFeature = (f) => persistFeatures(featureOptions.filter((x) => x !== f), "mekan");
  const addFeatureFirma = () => {
    const v = newFeatureFirma.trim();
    if (!v || featureOptionsFirma.includes(v)) { setNewFeatureFirma(""); return; }
    persistFeatures([...featureOptionsFirma, v], "firma");
    setNewFeatureFirma("");
  };
  const removeFeatureFirma = (f) => persistFeatures(featureOptionsFirma.filter((x) => x !== f), "firma");

  const saveMembership = async () => {
    setSavingMem(true);
    try {
      await axios.put(`${API}/admin/membership-settings`, { price: Number(membership.price) || 0, priceMekan: Number(membership.priceMekan) || 0, priceFirma: Number(membership.priceFirma) || 0, durationMonths: Number(membership.durationMonths) || 12, premiumImageLimit: Number(membership.premiumImageLimit) || 20, extraCategoryPrice: Number(membership.extraCategoryPrice) || 0, extraCategoryPrice2: Number(membership.extraCategoryPrice2) || 0, extraCategoryPrice3: Number(membership.extraCategoryPrice3) || 0 }, { headers });
      toast({ title: "Kaydedildi", description: "Üyelik ayarları güncellendi ve firma panellerinde görünecek." });
    } catch (e) { toast({ title: "Hata", description: e?.response?.data?.detail || "Kaydedilemedi." }); }
    finally { setSavingMem(false); }
  };

  const saveIyzico = async () => {
    if (!iyzico.apiKey || !iyzico.secretKey) { toast({ title: "Eksik bilgi", description: "API Key ve Secret Key girin." }); return; }
    setSavingIyz(true);
    try {
      await axios.put(`${API}/admin/iyzico-settings`, { environment: iyzico.environment, apiKey: iyzico.apiKey, secretKey: iyzico.secretKey }, { headers });
      toast({ title: "Kaydedildi", description: "iyzico ödeme ayarları güncellendi." });
      setIyzico((p) => ({ ...p, apiKey: "", secretKey: "", configured: true }));
      loadIyzico();
    } catch (e) { toast({ title: "Hata", description: e?.response?.data?.detail || "Kaydedilemedi." }); }
    finally { setSavingIyz(false); }
  };

  const shuffleNow = async () => {
    setShuffling(true);
    try {
      await axios.post(`${API}/admin/settings/shuffle`, {}, { headers });
      toast({ title: "Sıralama karıştırıldı", description: "İşletmeler yeni bir sıraya alındı." });
      load();
    } catch { toast({ title: "Hata", description: "İşlem yapılamadı." }); }
    finally { setShuffling(false); }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLogging(true);
    try {
      const res = await axios.post(`${API}/admin/login`, creds);
      localStorage.setItem("ds_admin_token", res.data.token);
      localStorage.setItem("ds_admin_role", res.data.role || "full");
      localStorage.setItem("ds_admin_name", res.data.name || "Yönetici");
      setRole(res.data.role || "full");
      setMeName(res.data.name || "Yönetici");
      setToken(res.data.token);
    } catch (err) {
      toast({ title: "Giriş başarısız", description: err?.response?.data?.detail || "Bilgileri kontrol edin." });
    } finally {
      setLogging(false);
    }
  };

  const logout = () => { localStorage.removeItem("ds_admin_token"); localStorage.removeItem("ds_admin_role"); localStorage.removeItem("ds_admin_name"); setToken(null); };

  const isViewer = role === "viewer";

  const resetCatForm = () => setCatForm({ id: null, name: "", group: "firma", image: "", order: 0 });

  const editCategory = (c) => setCatForm({ id: c.id, name: c.name, group: c.group || "firma", image: c.image || "", order: c.order || 0 });

  const saveCategory = async () => {
    if (!catForm.name.trim()) { toast({ title: "Eksik bilgi", description: "Kategori adı zorunludur." }); return; }
    setCatBusy(true);
    try {
      const payload = { name: catForm.name.trim(), group: catForm.group, image: (catForm.image || "").trim(), order: Number(catForm.order) || 0 };
      if (catForm.id) {
        await axios.put(`${API}/admin/categories/${catForm.id}`, payload, { headers });
        toast({ title: "Güncellendi", description: catForm.name.trim() });
      } else {
        await axios.post(`${API}/admin/categories`, payload, { headers });
        toast({ title: "Kategori eklendi", description: catForm.name.trim() });
      }
      resetCatForm();
      reloadCategories();
    } catch (e) { toast({ title: "Hata", description: e?.response?.data?.detail || "Kaydedilemedi." }); }
    finally { setCatBusy(false); }
  };

  const deleteCategory = async (c) => {
    if (!window.confirm(`"${c.name}" kategorisini silmek istediğinize emin misiniz? Bu kategorideki firmalar silinmez, yalnızca menülerden kaldırılır.`)) return;
    try {
      await axios.delete(`${API}/admin/categories/${c.id}`, { headers });
      if (catForm.id === c.id) resetCatForm();
      toast({ title: "Silindi", description: c.name });
      reloadCategories();
    } catch (e) { toast({ title: "Hata", description: e?.response?.data?.detail || "Silinemedi." }); }
  };

  const setStatus = async (firm, status) => {
    try {
      await axios.patch(`${API}/admin/firms/${firm.id}/status`, { status }, { headers });
      toast({ title: status === "approved" ? "Onaylandı" : "Yayından kaldırıldı", description: firm.name });
      load();
    } catch { toast({ title: "Hata", description: "İşlem yapılamadı." }); }
  };

  const toggleFeatured = async (firm) => {
    try {
      await axios.patch(`${API}/admin/firms/${firm.id}/featured`, { featured: !firm.featured }, { headers });
      load();
    } catch { toast({ title: "Hata", description: "İşlem yapılamadı." }); }
  };

  const PLAN_DAYS = { "1-ay": 30, "3-ay": 90, "6-ay": 180, "12-ay": 365 };

  const approveFeature = async (firm) => {
    const days = PLAN_DAYS[firm.featurePlan] || 30;
    try {
      await axios.patch(`${API}/admin/firms/${firm.id}/featured`, { featured: true, days }, { headers });
      toast({ title: "Öne çıkarıldı", description: `${firm.name} · ${days} gün` });
      load();
    } catch { toast({ title: "Hata", description: "İşlem yapılamadı." }); }
  };

  const rejectRequest = async (firm) => {
    try {
      await axios.patch(`${API}/admin/firms/${firm.id}/featured`, { featured: false }, { headers });
      toast({ title: "Talep reddedildi", description: firm.name });
      load();
    } catch { toast({ title: "Hata", description: "İşlem yapılamadı." }); }
  };

  const approveClaim = async (req) => {
    try {
      await axios.post(`${API}/admin/claim-requests/${req.id}/approve`, {}, { headers });
      toast({ title: "Sahiplenme onaylandı", description: `${req.firm_name} → ${req.venue_name}` });
      loadClaims(claimFilter);
      loadPendingClaims();
      load();
    } catch (e) { toast({ title: "Hata", description: e?.response?.data?.detail || "İşlem yapılamadı." }); }
  };

  const rejectClaim = async (req) => {
    try {
      await axios.post(`${API}/admin/claim-requests/${req.id}/reject`, {}, { headers });
      toast({ title: "Sahiplenme reddedildi", description: `${req.firm_name} → ${req.venue_name}` });
      loadClaims(claimFilter);
      loadPendingClaims();
    } catch (e) { toast({ title: "Hata", description: e?.response?.data?.detail || "İşlem yapılamadı." }); }
  };

  const loadFeatureAddRequests = async () => {
    try {
      const res = await axios.get(`${API}/admin/feature-add-requests`, { headers });
      setFeatureAddRequests(res.data || []);
    } catch { /* ignore */ }
  };

  const approveFeatureAddRequest = async (req) => {
    const edited = (featureEdits[req.id] ?? req.feature ?? "").trim();
    if (!edited) { toast({ title: "Geçersiz", description: "Özellik adı boş olamaz." }); return; }
    try {
      await axios.post(`${API}/admin/feature-add-requests/${req.id}/approve`, { feature: edited }, { headers });
      toast({ title: "Özellik onaylandı", description: `"${edited}" özellik listesine eklendi.` });
      setFeatureEdits((p) => { const n = { ...p }; delete n[req.id]; return n; });
      loadFeatureAddRequests();
    } catch (e) { toast({ title: "Hata", description: e?.response?.data?.detail || "İşlem yapılamadı." }); }
  };

  const rejectFeatureAddRequest = async (req) => {
    try {
      await axios.post(`${API}/admin/feature-add-requests/${req.id}/reject`, {}, { headers });
      toast({ title: "Özellik reddedildi", description: `"${req.feature}" talebi reddedildi.` });
      loadFeatureAddRequests();
    } catch (e) { toast({ title: "Hata", description: e?.response?.data?.detail || "İşlem yapılamadı." }); }
  };

  const pendingFeatureAddCount = featureAddRequests.filter((r) => (r.status || "pending") === "pending").length;

  const removeFirm = async (firm) => {
    if (!window.confirm(`"${firm.name}" firmasını silmek istediğinize emin misiniz?`)) return;
    try {
      await axios.delete(`${API}/admin/firms/${firm.id}`, { headers });
      toast({ title: "Silindi", description: firm.name });
      load();
    } catch { toast({ title: "Hata", description: "Silinemedi." }); }
  };

  // ---------- LOGIN SCREEN ----------
  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#2A2224] to-[#9E2A3B] px-5">
        <form onSubmit={handleLogin} className="w-full max-w-sm bg-white rounded-2xl overflow-hidden shadow-2xl">
          <div className="bg-gradient-to-br from-[#C15B6B] to-[#9E2A3B] px-6 py-7 text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-white/15 flex items-center justify-center mb-3">
              <ShieldCheck className="w-7 h-7 text-white" />
            </div>
            <h1 className="font-display text-2xl text-white">Patron Paneli</h1>
            <p className="text-white/85 text-sm">Yönetici girişi</p>
          </div>
          <div className="p-6 space-y-4">
            <div className="space-y-1.5">
              <label className="text-sm text-[#2A2224]">Kullanıcı Adı</label>
              <div className="relative">
                <UserIcon className="w-4 h-4 text-[#C15B6B] absolute left-3 top-1/2 -translate-y-1/2" />
                <Input value={creds.username} onChange={(e) => setCreds({ ...creds, username: e.target.value })} placeholder="patron" className="pl-9 border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm text-[#2A2224]">Şifre</label>
              <div className="relative">
                <Lock className="w-4 h-4 text-[#C15B6B] absolute left-3 top-1/2 -translate-y-1/2" />
                <Input type="password" value={creds.password} onChange={(e) => setCreds({ ...creds, password: e.target.value })} placeholder="••••••••" className="pl-9 border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
              </div>
            </div>
            <button type="submit" disabled={logging} className="w-full flex items-center justify-center gap-2 h-12 rounded-xl bg-gradient-to-r from-[#C15B6B] to-[#9E2A3B] text-white font-medium shadow-md hover:shadow-lg transition-all disabled:opacity-70">
              {logging ? <Loader2 className="w-5 h-5 animate-spin" /> : <ShieldCheck className="w-5 h-5" />}
              {logging ? "Giriş yapılıyor..." : "Giriş Yap"}
            </button>
            <Link to="/" className="block text-center text-sm text-[#7C6E70] hover:text-[#9E2A3B]">← Siteye dön</Link>
          </div>
        </form>
      </div>
    );
  }

  // Reusable firm table
  const FirmsTable = ({ list }) => (
    <div className="bg-white rounded-2xl border border-[#F0DDE0] overflow-hidden">
      {loading ? (
        <div className="py-20 flex justify-center"><Loader2 className="w-7 h-7 text-[#9E2A3B] animate-spin" /></div>
      ) : list.length === 0 ? (
        <div className="py-20 text-center text-[#7C6E70]">
          <Building2 className="w-10 h-10 mx-auto mb-3 text-[#E7A9B0]" />
          Kayıtlı işletme bulunamadı.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#FBF0F2] text-[#7C6E70] text-left">
                <th className="px-4 py-3 font-medium">İşletme</th>
                <th className="px-4 py-3 font-medium hidden md:table-cell">Kategori</th>
                <th className="px-4 py-3 font-medium hidden lg:table-cell">Konum</th>
                <th className="px-4 py-3 font-medium hidden lg:table-cell">Telefon</th>
                <th className="px-4 py-3 font-medium hidden xl:table-cell">Üyelik Tarihi</th>
                <th className="px-4 py-3 font-medium">Durum</th>
                <th className="px-4 py-3 font-medium text-right">İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {list.map((f) => (
                <tr key={f.id} className="border-t border-[#F0DDE0] hover:bg-[#FDFAF8]">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-lg bg-[#F6E4E6] overflow-hidden shrink-0">
                        {f.cover ? <img src={f.cover} alt="" loading="lazy" className="w-full h-full object-cover" /> : <Building2 className="w-5 h-5 text-[#C15B6B] m-3" />}
                      </div>
                      <div>
                        <p className="font-medium text-[#2A2224] flex items-center gap-1.5">
                          {f.name}
                          {f.featured && <Star className="w-3.5 h-3.5 text-[#C6A15B]" fill="#C6A15B" />}
                        </p>
                        <p className="text-xs text-[#a8999b]">{f.imageCount} foto{f.priceFrom ? ` • ${formatPrice(f.priceFrom)}` : ""}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell text-[#5b4d4f]">{catName(f.category)}</td>
                  <td className="px-4 py-3 hidden lg:table-cell text-[#5b4d4f]">{[f.district, f.city].filter(Boolean).join(", ")}</td>
                  <td className="px-4 py-3 hidden lg:table-cell text-[#5b4d4f]">{f.phone}</td>
                  <td className="px-4 py-3 hidden xl:table-cell text-[#5b4d4f]" data-testid={`firm-created-${f.id}`}>{f.createdAt ? new Date(f.createdAt).toLocaleDateString("tr-TR") : "—"}</td>
                  <td className="px-4 py-3">
                    {f.status === "pending" ? (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#FBF3E3] text-[#9a7b2e] text-xs"><Clock className="w-3 h-3" /> Beklemede</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#E7F3EC] text-[#2E7D5B] text-xs"><CheckCircle2 className="w-3 h-3" /> Yayında</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <FirmActionsMenu f={f} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const FirmActionsMenu = ({ f }) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid={`firm-actions-trigger-${f.id}`}
          className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg bg-[#F6E4E6] text-[#9E2A3B] text-sm font-medium hover:bg-[#f0d3d6] transition-colors"
        >
          İşlemler <MoreHorizontal className="w-4 h-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48" data-testid={`firm-actions-menu-${f.id}`}>
        {f.status === "pending" ? (
          <DropdownMenuItem onClick={() => setStatus(f, "approved")} className="cursor-pointer text-[#2E7D5B]">
            <CheckCircle2 className="w-4 h-4 mr-2" /> Onayla
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem onClick={() => setStatus(f, "pending")} className="cursor-pointer text-[#9a7b2e]">
            <Clock className="w-4 h-4 mr-2" /> Yayından kaldır
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={() => toggleFeatured(f)} className="cursor-pointer text-[#C6A15B]">
          <Star className="w-4 h-4 mr-2" fill={f.featured ? "#C6A15B" : "none"} /> {f.featured ? "Öne çıkarmayı kaldır" : "Öne çıkar"}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => openNotes(f)} className="cursor-pointer text-[#8a6d1f]" data-testid={`firm-notes-${f.id}`}>
          <StickyNote className="w-4 h-4 mr-2" /> Notlar{f.noteCount ? ` (${f.noteCount})` : ""}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate(`/patron/firma/${f.id}`)} className="cursor-pointer text-[#3b5bdb]">
          <Pencil className="w-4 h-4 mr-2" /> Firmayı düzenle
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate(`/mekan/${f.slug}`)} className="cursor-pointer text-[#9E2A3B]">
          <Eye className="w-4 h-4 mr-2" /> Sitede gör
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => removeFirm(f)} className="cursor-pointer text-[#c0392b] focus:text-[#c0392b]">
          <Trash2 className="w-4 h-4 mr-2" /> Sil
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );


  const NAV = [
    { key: "anasayfa", label: "Ana Sayfa", icon: LayoutDashboard },
    { key: "ziyaretler", label: "Site Ziyaretleri", icon: TrendingUp },
    { key: "uyeler", label: "Üye Yönetimi", icon: Users },
    { key: "isletmeler", label: "İşletme Yönetimi", icon: Building2 },
    { key: "reklam", label: "Reklam Yönetimi", icon: Megaphone },
    { key: "icerik", label: "İçerik Yönetimi", icon: Newspaper },
    { key: "finans", label: "Finans Yönetimi", icon: Wallet },
    { key: "ayarlar", label: "Ayarlar", icon: SettingsIcon },
    { key: "sms-raporu", label: "SMS Yönetimi", icon: MessageSquare },
  ];

  const adminCityOptions = Array.from(new Set(firms.map((f) => f.city).filter(Boolean))).sort((a, b) => a.localeCompare(b, "tr"));
  const adReqKindOf = (r) => (r.kind === "member" || r.kind === "instagram") ? r.kind : "google";
  const filteredAdReqs = adRequests
    .filter((r) => adReqFilter === "all" ? true : (r.status || "pending") === adReqFilter)
    .filter((r) => adReqKind === "all" ? true : adReqKindOf(r) === adReqKind);

  const filteredPayments = payments.filter((p) => {
    if (!p.paidAt) return !payFrom && !payTo;
    const d = new Date(p.paidAt);
    if (payFrom && d < new Date(payFrom + "T00:00:00")) return false;
    if (payTo && d > new Date(payTo + "T23:59:59")) return false;
    return true;
  });
  const totalRevenue = filteredPayments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const visibleFirms = firms.filter(
    (f) =>
      (catFilter === "hepsi" || f.category === catFilter) &&
      (cityFilterA === "hepsi" || f.city === cityFilterA) &&
      (featFilter === "hepsi" || (featFilter === "eksik" ? f.missing?.length > 0 : featFilter === "featured" ? f.featured : !f.featured))
  );
  const featuredFirms = firms.filter((f) => f.featured);
  const pendingVisibleIds = visibleFirms.filter((f) => f.status === "pending").map((f) => f.id);
  const toggleSel = (id) => setSelectedFirms((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const allVisibleSelected = visibleFirms.length > 0 && visibleFirms.every((f) => selectedFirms.includes(f.id));

  // ---------- DASHBOARD ----------
  return (
    <div className="min-h-screen bg-[#FBF5F0]">
      <div className="bg-gradient-to-br from-[#2A2224] to-[#9E2A3B] py-6">
        <div className="max-w-7xl mx-auto px-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-white/15 flex items-center justify-center">
              <ShieldCheck className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-display text-2xl text-white">Patron Paneli</h1>
              <p className="text-white/80 text-sm">İşletme yönetimi</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/" className="hidden sm:flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/10 text-white text-sm hover:bg-white/20 transition-colors">Siteyi Gör</Link>
            <button onClick={logout} className="flex items-center gap-2 px-4 py-2.5 rounded-full border border-white/60 text-white text-sm hover:bg-white/10 transition-colors">
              <LogOut className="w-4 h-4" /> Çıkış
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 py-8">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* SIDEBAR */}
          <aside className="lg:w-60 shrink-0">
            <div className="bg-white rounded-2xl border border-[#F0DDE0] p-2 lg:sticky lg:top-6 flex lg:flex-col gap-1 overflow-x-auto">
              {NAV.map((n) => {
                const Icon = n.icon;
                const active = section === n.key;
                if (n.key === "sms-raporu") {
                  const smsSubs = [["sms-raporu", "SMS Raporu", MessageSquare], ["sms-toplu", "Toplu Firma SMS'i", Send], ["sms-ayarlar", "SMS Ayarları", SettingsIcon]];
                  const parentActive = smsSubs.some(([k]) => k === section);
                  return (
                    <div key={n.key} className="flex flex-col gap-1">
                      <button
                        onClick={() => setOpenMenu((p) => p === "sms" ? null : "sms")}
                        data-testid="admin-nav-sms"
                        className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium whitespace-nowrap transition-colors ${parentActive ? "bg-gradient-to-r from-[#C15B6B] to-[#9E2A3B] text-white shadow" : "text-[#5b4d4f] hover:bg-[#FBF5F0]"}`}
                      >
                        <Icon className="w-4.5 h-4.5" /> {n.label}
                        <ChevronDown className={`w-4 h-4 ml-auto transition-transform ${openMenu === "sms" ? "rotate-180" : ""}`} />
                      </button>
                      {openMenu === "sms" && (
                        <div className="flex flex-col gap-1 pl-4 ml-4 border-l border-[#F0DDE0]" data-testid="sms-subnav">
                          {smsSubs.map(([k, l, SubIcon]) => (
                            <button key={k} onClick={() => setSection(k)} data-testid={`admin-nav-${k}`}
                              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left transition-all ${section === k ? "bg-[#FBEEF0] text-[#9E2A3B] font-medium" : "text-[#7C6E70] hover:bg-[#FBF0F2] hover:text-[#9E2A3B]"}`}>
                              <SubIcon className="w-3.5 h-3.5" /> {l}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                }
                if (n.key === "uyeler") {
                  const parentActive = section === "uyeler" || section === "uyeler-yeni";
                  return (
                    <div key={n.key} className="flex flex-col gap-1">
                      <button
                        onClick={() => { setOpenMenu((p) => p === "uyeler" ? null : "uyeler"); }}
                        data-testid="admin-nav-uyeler"
                        className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium whitespace-nowrap transition-colors ${parentActive ? "bg-gradient-to-r from-[#C15B6B] to-[#9E2A3B] text-white shadow" : "text-[#5b4d4f] hover:bg-[#FBF5F0]"}`}
                      >
                        <Icon className="w-4.5 h-4.5" /> {n.label}
                        <ChevronDown className={`w-4 h-4 ml-auto transition-transform ${(openMenu === "uyeler") ? "rotate-180" : ""}`} />
                      </button>
                      {(openMenu === "uyeler") && (
                        <div className="flex flex-col gap-1 pl-4 ml-4 border-l border-[#F0DDE0]" data-testid="uyeler-subnav">
                          <button
                            onClick={() => setSection("uyeler")}
                            data-testid="admin-nav-uyeler-all"
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left transition-all ${section === "uyeler" ? "bg-[#FBEEF0] text-[#9E2A3B] font-medium" : "text-[#7C6E70] hover:bg-[#FBF0F2] hover:text-[#9E2A3B]"}`}
                          >
                            <Users className="w-3.5 h-3.5" /> Üyeler
                          </button>
                          <button
                            onClick={() => setSection("uyeler-yeni")}
                            data-testid="admin-nav-uyeler-yeni"
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left transition-all ${section === "uyeler-yeni" ? "bg-[#FBEEF0] text-[#9E2A3B] font-medium" : "text-[#7C6E70] hover:bg-[#FBF0F2] hover:text-[#9E2A3B]"}`}
                          >
                            <Sparkles className="w-3.5 h-3.5" /> Yeni Eklenenler
                          </button>
                        </div>
                      )}
                    </div>
                  );
                }
                if (n.key === "isletmeler") {
                  const parentActive = section === "isletmeler" || section === "firma-panel-ayarlari" || section === "yeni" || section === "sahiplenme" || section === "toplu-yukleme" || section === "ozellik-talepleri";
                  return (
                    <div key={n.key} className="flex flex-col gap-1">
                      <button
                        onClick={() => {
                          setOpenMenu((p) => p === "isletmeler" ? null : "isletmeler");
                        }}
                        data-testid="admin-nav-isletmeler"
                        className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium whitespace-nowrap transition-colors ${parentActive ? "bg-gradient-to-r from-[#C15B6B] to-[#9E2A3B] text-white shadow" : "text-[#5b4d4f] hover:bg-[#FBF5F0]"}`}
                      >
                        <Icon className="w-4.5 h-4.5" /> {n.label}
                        {pendingClaims.length > 0 && !parentActive && <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-[#2E8B57] text-white">{pendingClaims.length}</span>}
                        <ChevronDown className={`w-4 h-4 ml-auto transition-transform ${(openMenu === "isletmeler") ? "rotate-180" : ""}`} />
                      </button>
                      {(openMenu === "isletmeler") && (
                        <div className="flex flex-col gap-1 pl-4 ml-4 border-l border-[#F0DDE0]" data-testid="isletmeler-subnav">
                          <button
                            onClick={() => setSection("isletmeler")}
                            data-testid="admin-nav-isletme-yonetimi"
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left transition-all ${section === "isletmeler" ? "bg-[#FBEEF0] text-[#9E2A3B] font-medium" : "text-[#7C6E70] hover:bg-[#FBF0F2] hover:text-[#9E2A3B]"}`}
                          >
                            <LayoutDashboard className="w-3.5 h-3.5" /> İşletmeler
                          </button>
                          <button
                            onClick={() => setSection("yeni")}
                            data-testid="admin-nav-yeni"
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left transition-all ${section === "yeni" ? "bg-[#FBEEF0] text-[#9E2A3B] font-medium" : "text-[#7C6E70] hover:bg-[#FBF0F2] hover:text-[#9E2A3B]"}`}
                          >
                            <Sparkles className="w-3.5 h-3.5" /> Yeni Eklenenler
                          </button>
                          {!isViewer && (
                            <button
                              onClick={() => setSection("toplu-yukleme")}
                              data-testid="admin-nav-toplu-yukleme"
                              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left transition-all ${section === "toplu-yukleme" ? "bg-[#FBEEF0] text-[#9E2A3B] font-medium" : "text-[#7C6E70] hover:bg-[#FBF0F2] hover:text-[#9E2A3B]"}`}
                            >
                              <FileSpreadsheet className="w-3.5 h-3.5" /> Excel ile Toplu Yükleme
                            </button>
                          )}
                          <button
                            onClick={() => setSection("sahiplenme")}
                            data-testid="admin-nav-sahiplenme"
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left transition-all ${section === "sahiplenme" ? "bg-[#FBEEF0] text-[#9E2A3B] font-medium" : "text-[#7C6E70] hover:bg-[#FBF0F2] hover:text-[#9E2A3B]"}`}
                          >
                            <BadgeCheck className="w-3.5 h-3.5" /> Sahiplenme Talepleri
                            {pendingClaims.length > 0 && <span className="ml-auto text-[11px] px-1.5 py-0.5 rounded-full bg-[#2E8B57] text-white">{pendingClaims.length}</span>}
                          </button>
                          <button
                            onClick={() => setSection("ozellik-talepleri")}
                            data-testid="admin-nav-ozellik-talepleri"
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left transition-all ${section === "ozellik-talepleri" ? "bg-[#FBEEF0] text-[#9E2A3B] font-medium" : "text-[#7C6E70] hover:bg-[#FBF0F2] hover:text-[#9E2A3B]"}`}
                          >
                            <Tag className="w-3.5 h-3.5" /> Özellik Ekleme Talebi
                            {pendingFeatureAddCount > 0 && <span className="ml-auto text-[11px] px-1.5 py-0.5 rounded-full bg-[#2E8B57] text-white">{pendingFeatureAddCount}</span>}
                          </button>
                          <button
                            onClick={() => setSection("firma-panel-ayarlari")}
                            data-testid="admin-nav-firma-panel-ayarlari"
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left transition-all ${section === "firma-panel-ayarlari" ? "bg-[#FBEEF0] text-[#9E2A3B] font-medium" : "text-[#7C6E70] hover:bg-[#FBF0F2] hover:text-[#9E2A3B]"}`}
                          >
                            <SettingsIcon className="w-3.5 h-3.5" /> Firma Panel Ayarları
                          </button>
                        </div>
                      )}
                    </div>
                  );
                }
                if (n.key === "reklam") {
                  const pendingAdReqs = adRequests.filter((r) => (r.status || "pending") === "pending").length;
                  const parentActive = section === "reklam" || section === "reklam-talepleri" || section === "indirim-kodlari" || section === "uye-banner";
                  return (
                    <div key={n.key} className="flex flex-col gap-1">
                      <button
                        onClick={() => {
                          setOpenMenu((p) => p === "reklam" ? null : "reklam");
                        }}
                        data-testid="admin-nav-reklam"
                        className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium whitespace-nowrap transition-colors ${parentActive ? "bg-gradient-to-r from-[#C15B6B] to-[#9E2A3B] text-white shadow" : "text-[#5b4d4f] hover:bg-[#FBF5F0]"}`}
                      >
                        <Icon className="w-4.5 h-4.5" /> {n.label}
                        <ChevronDown className={`w-4 h-4 ml-auto transition-transform ${(openMenu === "reklam") ? "rotate-180" : ""}`} />
                      </button>
                      {(openMenu === "reklam") && (
                        <div className="flex flex-col gap-1 pl-4 ml-4 border-l border-[#F0DDE0]" data-testid="reklam-subnav">
                          <button
                            onClick={() => setSection("reklam")}
                            data-testid="admin-nav-reklam-ayarlari"
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left transition-all ${section === "reklam" ? "bg-[#FBEEF0] text-[#9E2A3B] font-medium" : "text-[#7C6E70] hover:bg-[#FBF0F2] hover:text-[#9E2A3B]"}`}
                          >
                            <Crown className="w-3.5 h-3.5" /> Reklam Ayarları
                          </button>
                          <button
                            onClick={() => setSection("reklam-talepleri")}
                            data-testid="admin-nav-reklam-talepleri"
                            className={`flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-sm text-left transition-all ${section === "reklam-talepleri" ? "bg-[#FBEEF0] text-[#9E2A3B] font-medium" : "text-[#7C6E70] hover:bg-[#FBF0F2] hover:text-[#9E2A3B]"}`}
                          >
                            <span className="flex items-center gap-2"><Megaphone className="w-3.5 h-3.5" /> Reklam Talepleri</span>
                            {pendingAdReqs > 0 && <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-[#3b5bdb] text-white">{pendingAdReqs}</span>}
                          </button>
                          <button
                            onClick={() => setSection("indirim-kodlari")}
                            data-testid="admin-nav-indirim-kodlari"
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left transition-all ${section === "indirim-kodlari" ? "bg-[#FBEEF0] text-[#9E2A3B] font-medium" : "text-[#7C6E70] hover:bg-[#FBF0F2] hover:text-[#9E2A3B]"}`}
                          >
                            <Tag className="w-3.5 h-3.5" /> İndirim Kodları
                          </button>
                          <button
                            onClick={() => setSection("uye-banner")}
                            data-testid="admin-nav-uye-banner"
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left transition-all ${section === "uye-banner" ? "bg-[#FBEEF0] text-[#9E2A3B] font-medium" : "text-[#7C6E70] hover:bg-[#FBF0F2] hover:text-[#9E2A3B]"}`}
                          >
                            <ImagePlus className="w-3.5 h-3.5" /> Üye Paneli Reklamları
                          </button>
                        </div>
                      )}
                    </div>
                  );
                }
                if (n.key === "icerik") {
                  const parentActive = section === "icerik";
                  return (
                    <div key={n.key} className="flex flex-col gap-1">
                      <button
                        onClick={() => {
                          setOpenMenu((p) => p === "icerik" ? null : "icerik");
                        }}
                        data-testid="admin-nav-icerik"
                        className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium whitespace-nowrap transition-colors ${parentActive ? "bg-gradient-to-r from-[#C15B6B] to-[#9E2A3B] text-white shadow" : "text-[#5b4d4f] hover:bg-[#FBF5F0]"}`}
                      >
                        <Icon className="w-4.5 h-4.5" /> {n.label}
                        <ChevronDown className={`w-4 h-4 ml-auto transition-transform ${(openMenu === "icerik") ? "rotate-180" : ""}`} />
                      </button>
                      {(openMenu === "icerik") && (
                        <div className="flex flex-col gap-1 pl-4 ml-4 border-l border-[#F0DDE0]" data-testid="icerik-subnav">
                          <button
                            onClick={() => setSection("icerik")}
                            data-testid="admin-nav-rehber-yazilar"
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left transition-all ${section === "icerik" ? "bg-[#FBEEF0] text-[#9E2A3B] font-medium" : "text-[#7C6E70] hover:bg-[#FBF0F2] hover:text-[#9E2A3B]"}`}
                          >
                            <Newspaper className="w-3.5 h-3.5" /> Rehber Yazılar
                          </button>
                        </div>
                      )}
                    </div>
                  );
                }

                if (n.key === "finans") {
                  const parentActive = section === "odemeler" || section === "odeme-al";
                  return (
                    <div key={n.key} className="flex flex-col gap-1">
                      <button
                        onClick={() => {
                          setOpenMenu((p) => p === "finans" ? null : "finans");
                        }}
                        data-testid="admin-nav-finans"
                        className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium whitespace-nowrap transition-colors ${parentActive ? "bg-gradient-to-r from-[#C15B6B] to-[#9E2A3B] text-white shadow" : "text-[#5b4d4f] hover:bg-[#FBF5F0]"}`}
                      >
                        <Icon className="w-4.5 h-4.5" /> {n.label}
                        <ChevronDown className={`w-4 h-4 ml-auto transition-transform ${(openMenu === "finans") ? "rotate-180" : ""}`} />
                      </button>
                      {(openMenu === "finans") && (
                        <div className="flex flex-col gap-1 pl-4 ml-4 border-l border-[#F0DDE0]" data-testid="finans-subnav">
                          <button
                            onClick={() => setSection("odemeler")}
                            data-testid="admin-nav-odemeler"
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left transition-all ${section === "odemeler" ? "bg-[#FBEEF0] text-[#9E2A3B] font-medium" : "text-[#7C6E70] hover:bg-[#FBF0F2] hover:text-[#9E2A3B]"}`}
                          >
                            <CreditCard className="w-3.5 h-3.5" /> Ödemeler
                          </button>
                          <button
                            onClick={() => setSection("odeme-al")}
                            data-testid="admin-nav-odeme-al"
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left transition-all ${section === "odeme-al" ? "bg-[#FBEEF0] text-[#9E2A3B] font-medium" : "text-[#7C6E70] hover:bg-[#FBF0F2] hover:text-[#9E2A3B]"}`}
                          >
                            <Tag className="w-3.5 h-3.5" /> Ödeme Al
                          </button>
                        </div>
                      )}
                    </div>
                  );
                }
                if (n.key === "ayarlar") {
                  const parentActive = section === "ayarlar" || section === "kullanicilar" || section === "islem" || section === "kategori-ayarlari" || section === "danisman";
                  return (
                    <div key={n.key} className="flex flex-col gap-1">
                      <button
                        onClick={() => {
                          setOpenMenu((p) => p === "ayarlar" ? null : "ayarlar");
                        }}
                        data-testid="admin-nav-ayarlar"
                        className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium whitespace-nowrap transition-colors ${parentActive ? "bg-gradient-to-r from-[#C15B6B] to-[#9E2A3B] text-white shadow" : "text-[#5b4d4f] hover:bg-[#FBF5F0]"}`}
                      >
                        <Icon className="w-4.5 h-4.5" /> {n.label}
                        <ChevronDown className={`w-4 h-4 ml-auto transition-transform ${(openMenu === "ayarlar") ? "rotate-180" : ""}`} />
                      </button>
                      {(openMenu === "ayarlar") && (
                        <div className="flex flex-col gap-1 pl-4 ml-4 border-l border-[#F0DDE0]" data-testid="ayarlar-subnav">
                          <button
                            onClick={() => setSection("ayarlar")}
                            data-testid="admin-nav-genel-ayarlar"
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left transition-all ${section === "ayarlar" ? "bg-[#FBEEF0] text-[#9E2A3B] font-medium" : "text-[#7C6E70] hover:bg-[#FBF0F2] hover:text-[#9E2A3B]"}`}
                          >
                            <SettingsIcon className="w-3.5 h-3.5" /> Genel Ayarlar
                          </button>
                          <button
                            onClick={() => setSection("kategori-ayarlari")}
                            data-testid="admin-nav-kategori-ayarlari"
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left transition-all ${section === "kategori-ayarlari" ? "bg-[#FBEEF0] text-[#9E2A3B] font-medium" : "text-[#7C6E70] hover:bg-[#FBF0F2] hover:text-[#9E2A3B]"}`}
                          >
                            <Tag className="w-3.5 h-3.5" /> Kategori Ayarları
                          </button>
                          <button
                            onClick={() => setSection("danisman")}
                            data-testid="admin-nav-danisman"
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left transition-all ${section === "danisman" ? "bg-[#FBEEF0] text-[#9E2A3B] font-medium" : "text-[#7C6E70] hover:bg-[#FBF0F2] hover:text-[#9E2A3B]"}`}
                          >
                            <MessageCircle className="w-3.5 h-3.5" /> Danışman
                          </button>
                          <button
                            onClick={() => setSection("kullanicilar")}
                            data-testid="admin-nav-kullanicilar"
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left transition-all ${section === "kullanicilar" ? "bg-[#FBEEF0] text-[#9E2A3B] font-medium" : "text-[#7C6E70] hover:bg-[#FBF0F2] hover:text-[#9E2A3B]"}`}
                          >
                            <Users className="w-3.5 h-3.5" /> Kullanıcılar
                          </button>
                          <button
                            onClick={() => setSection("islem")}
                            data-testid="admin-nav-islem"
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left transition-all ${section === "islem" ? "bg-[#FBEEF0] text-[#9E2A3B] font-medium" : "text-[#7C6E70] hover:bg-[#FBF0F2] hover:text-[#9E2A3B]"}`}
                          >
                            <Clock className="w-3.5 h-3.5" /> İşlem Kaydı
                          </button>
                        </div>
                      )}
                    </div>
                  );
                }
                return (
                  <button
                    key={n.key}
                    onClick={() => setSection(n.key)}
                    data-testid={`admin-nav-${n.key}`}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium whitespace-nowrap transition-colors ${active ? "bg-gradient-to-r from-[#C15B6B] to-[#9E2A3B] text-white shadow" : "text-[#5b4d4f] hover:bg-[#FBF5F0]"}`}
                  >
                    <Icon className="w-4.5 h-4.5" /> {n.label}
                    {n.key === "anasayfa" && (requests.length + pendingClaims.length + adRequests.filter((r) => (r.status || "pending") === "pending").length) > 0 && (
                      <span className={`ml-auto text-[11px] px-1.5 py-0.5 rounded-full ${active ? "bg-white/25 text-white" : "bg-[#3b5bdb] text-white"}`}>{requests.length + pendingClaims.length + adRequests.filter((r) => (r.status || "pending") === "pending").length}</span>
                    )}
                  </button>
                );
              })}
            </div>
          </aside>

          {/* MAIN */}
          <div className="flex-1 min-w-0">

        {section === "ziyaretler" && <SiteVisits headers={headers} />}
        {["sms-raporu", "sms-toplu", "sms-ayarlar"].includes(section) && <SmsReport tab={section} headers={headers} settings={<>
        {/* Netgsm SMS Ayarları */}
        <div className="bg-white rounded-2xl border border-[#F0DDE0] p-5" data-testid="admin-sms-settings">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-11 h-11 rounded-xl bg-[#FBEEF0] flex items-center justify-center shrink-0">
              <MessageCircle className="w-6 h-6 text-[#9E2A3B]" />
            </div>
            <div>
              <h3 className="font-display text-lg text-[#2A2224]">SMS Bildirim Ayarları (Netgsm)</h3>
              <p className="text-sm text-[#7C6E70] max-w-xl">
                Bir üye firmadan teklif istediğinde, firmanın panelinde girdiği "SMS Bildirim Numarası"na otomatik SMS gönderilir. Netgsm API bilgilerinizi girin; şifre şifrelenerek saklanır.
                {sms.configured && <span className="text-[#1EBE5A] font-medium"> · Yapılandırılmış ({sms.passwordMasked})</span>}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <label className="text-sm text-[#5b4d4f]">Kullanıcı Kodu (Usercode)</label>
              <Input data-testid="admin-sms-usercode" value={sms.usercode} onChange={(e) => setSms((p) => ({ ...p, usercode: e.target.value }))} placeholder="850..." className="border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm text-[#5b4d4f]">API Şifresi</label>
              <Input data-testid="admin-sms-password" type="password" value={sms.password} onChange={(e) => setSms((p) => ({ ...p, password: e.target.value }))} placeholder={sms.configured ? "Değiştirmek için yeni değer girin" : "••••••"} className="border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm text-[#5b4d4f]">Gönderici Başlığı (msgheader)</label>
              <Input data-testid="admin-sms-msgheader" value={sms.msgheader} onChange={(e) => setSms((p) => ({ ...p, msgheader: e.target.value }))} placeholder="Onaylı başlık" className="border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
            </div>
          </div>
          <label className="flex items-center gap-2 mt-4 cursor-pointer w-fit" data-testid="admin-sms-enabled">
            <input type="checkbox" checked={sms.enabled} onChange={(e) => setSms((p) => ({ ...p, enabled: e.target.checked }))} className="w-4 h-4 accent-[#9E2A3B]" />
            <span className="text-sm text-[#5b4d4f]">SMS bildirimleri aktif</span>
          </label>
          <div className="space-y-1.5 mt-4">
            <label className="text-sm text-[#5b4d4f]">SMS Metni</label>
            <textarea
              data-testid="admin-sms-template"
              value={sms.template}
              onChange={(e) => setSms((p) => ({ ...p, template: e.target.value }))}
              rows={3}
              placeholder="düğünsenin.com dan yeni bir teklif talebi var lütfen hızlıca yanıt verin"
              className="w-full rounded-xl border border-[#F0DDE0] bg-white px-3 py-2.5 text-sm text-[#2A2224] outline-none focus:border-[#C15B6B] resize-y"
            />
            <div className="flex flex-wrap items-center gap-1.5 text-xs text-[#9c8f90]">
              <span>Değişkenler (tıklayıp ekleyin):</span>
              {[
                { v: "{firma}", d: "Firma adı" },
                { v: "{ad}", d: "Müşteri adı" },
                { v: "{telefon}", d: "Müşteri telefonu" },
                { v: "{tarih}", d: "Etkinlik tarihi" },
                { v: "{mesaj}", d: "Müşteri mesajı" },
              ].map((t) => (
                <button
                  key={t.v}
                  type="button"
                  title={t.d}
                  data-testid={`admin-sms-var-${t.v.replace(/[{}]/g, "")}`}
                  onClick={() => setSms((p) => ({ ...p, template: `${p.template || ""}${t.v}` }))}
                  className="px-2 py-0.5 rounded-md border border-[#E7D5DA] bg-[#FBF0F2] text-[#9E2A3B] font-mono hover:bg-[#F6E2E6] transition-colors"
                >
                  {t.v}
                </button>
              ))}
            </div>
            <p className="text-xs text-[#9c8f90]">Örn: "düğünsenin.com: {`{firma}`} için {`{ad}`} ({`{telefon}`}) teklif istedi. Tarih: {`{tarih}`}". Boş bırakılırsa varsayılan metin kullanılır.</p>
          </div>
          <div className="flex flex-wrap items-end gap-3 mt-4">
            <button onClick={saveSms} disabled={savingSms} data-testid="admin-sms-save"
              className="flex items-center gap-2 px-5 h-11 rounded-xl bg-gradient-to-r from-[#C15B6B] to-[#9E2A3B] text-white text-sm font-medium hover:shadow-md transition-all disabled:opacity-70">
              {savingSms ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} SMS Ayarlarını Kaydet
            </button>
            {sms.configured && (
              <div className="flex items-end gap-2">
                <div className="space-y-1.5">
                  <label className="text-xs text-[#9c8f90]">Test numarası</label>
                  <Input data-testid="admin-sms-test-phone" value={smsTestPhone} onChange={(e) => setSmsTestPhone(e.target.value)} placeholder="05XX..." className="border-[#F0DDE0] h-11 w-40" />
                </div>
                <button onClick={testSms} data-testid="admin-sms-test-btn" className="flex items-center gap-2 px-4 h-11 rounded-xl border border-[#E7A9B0] text-[#9E2A3B] text-sm font-medium hover:bg-[#FBF0F2] transition-colors">
                  <Send className="w-4 h-4" /> Test SMS Gönder
                </button>
              </div>
            )}
          </div>
        </div>
        </>} />}

        {(section === "uyeler" || section === "uyeler-yeni") && (
          <div data-testid="admin-members">
            <div className="flex items-center justify-between gap-3 mb-5">
              <div>
                <h2 className="font-display text-2xl font-bold text-[#2A2224]">{isYeniMembers ? "Yeni Eklenen Üyeler" : "Üye Yönetimi"}</h2>
                <p className="text-sm text-[#7C6E70]">{isYeniMembers ? `Son ${30} günde kaydolan üyeler` : "Kayıtlı ziyaretçi üyeleri ve favorileri"}</p>
              </div>
              <span className="text-sm text-[#7C6E70] bg-[#FBEEF0] text-[#9E2A3B] px-3 py-1.5 rounded-full font-medium" data-testid="admin-members-count">{filteredMembers.length} / {members.length} üye</span>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <div className="relative flex-1">
                <Search className="w-4 h-4 text-[#b6a9ab] absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  value={memberQuery}
                  onChange={(e) => setMemberQuery(e.target.value)}
                  placeholder="Ad soyad veya telefon ara..."
                  data-testid="admin-member-search"
                  className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-[#F0DDE0] text-sm focus:outline-none focus:border-[#9E2A3B]"
                />
              </div>
              <Select value={memberCity} onValueChange={setMemberCity}>
                <SelectTrigger className="w-full sm:w-52 border-[#F0DDE0]" data-testid="admin-member-city-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="hepsi">Tüm şehirler</SelectItem>
                  {memberCityOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
              <button
                onClick={exportMembersCsv}
                disabled={filteredMembers.length === 0}
                data-testid="admin-member-export"
                className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-[#9E2A3B] text-white text-sm font-medium hover:bg-[#84222f] transition-colors disabled:opacity-50 shrink-0"
              >
                <Download className="w-4 h-4" /> CSV İndir
              </button>
            </div>

            {membersLoading ? (
              <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-[#9E2A3B]" /></div>
            ) : filteredMembers.length === 0 ? (
              <div className="bg-white rounded-2xl border border-[#F0DDE0] py-16 text-center text-[#7C6E70]">{members.length === 0 ? "Henüz kayıtlı üye yok." : "Aramanıza uygun üye bulunamadı."}</div>
            ) : (
              <div className="bg-white rounded-2xl border border-[#F0DDE0] overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-[#FBF5F0] text-[#7C6E70] text-xs uppercase">
                        <th className="text-left px-4 py-3 font-semibold">Ad Soyad</th>
                        <th className="text-left px-4 py-3 font-semibold">Telefon</th>
                        <th className="text-left px-4 py-3 font-semibold">Şehir</th>
                        <th className="text-left px-4 py-3 font-semibold">Kayıt Tarihi</th>
                        <th className="text-center px-4 py-3 font-semibold">Favori</th>
                        <th className="text-right px-4 py-3 font-semibold">Detay</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredMembers.map((m) => (
                        <tr key={m.id} onClick={() => openMemberDetail(m)} data-testid={`admin-member-row-${m.id}`} className="border-t border-[#F5E9EB] hover:bg-[#FDFAF8] cursor-pointer">
                          <td className="px-4 py-3 font-medium text-[#2A2224]">{m.name || "—"}</td>
                          <td className="px-4 py-3 text-[#5b4d4f]">{m.phone || "—"}</td>
                          <td className="px-4 py-3 text-[#5b4d4f]">{m.city || "—"}</td>
                          <td className="px-4 py-3 text-[#5b4d4f]" data-testid={`admin-member-date-${m.id}`}>{m.createdAt ? new Date(m.createdAt).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" }) : "—"}</td>
                          <td className="px-4 py-3 text-center">
                            <span className="inline-flex items-center gap-1 text-[#9E2A3B] font-medium"><Heart className="w-3.5 h-3.5" fill="currentColor" /> {m.favoritesCount}</span>
                          </td>
                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            <button
                              onClick={(e) => { e.stopPropagation(); openMemberDetail(m); }}
                              data-testid={`admin-member-detail-${m.id}`}
                              className="text-xs px-3 py-1.5 rounded-lg border border-[#F0DDE0] text-[#9E2A3B] hover:bg-[#FBF0F2]"
                            >
                              Detay →
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); deleteMember(m); }}
                              data-testid={`admin-member-delete-${m.id}`}
                              title="Üyeyi sil"
                              className="ml-2 inline-flex items-center text-xs px-2.5 py-1.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 transition-colors"
                            >
                              <Trash2 className="w-3.5 h-3.5 mr-1" /> Sil
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {section === "anasayfa" && (
        <div className="space-y-6">
        {/* Stats — tıklanabilir */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          <StatCard icon={Building2} label="Toplam İşletme" value={stats.total} color="#9E2A3B" onClick={() => setHomePanel((p) => p === "total" ? null : "total")} active={homePanel === "total"} />
          <StatCard icon={CheckCircle2} label="Yayında" value={stats.approved} color="#2E7D5B" onClick={() => setHomePanel((p) => p === "approved" ? null : "approved")} active={homePanel === "approved"} />
          <StatCard icon={Clock} label="Beklemede" value={stats.pending} color="#C6A15B" onClick={() => setHomePanel((p) => p === "pending" ? null : "pending")} active={homePanel === "pending"} />
          <StatCard icon={Crown} label="Öne Çıkan" value={stats.featured} color="#C6A15B" onClick={() => setHomePanel((p) => p === "featured" ? null : "featured")} active={homePanel === "featured"} />
          <StatCard icon={Sparkles} label="Öne Çıkarma Talebi" value={requests.length} color="#3b5bdb" onClick={() => setHomePanel((p) => p === "requests" ? null : "requests")} active={homePanel === "requests"} badge={requests.length} />
          <StatCard icon={Megaphone} label="Reklam Talepleri" value={adRequests.filter((r) => (r.status || "pending") === "pending").length} color="#5b7be0" onClick={() => setHomePanel((p) => p === "adrequests" ? null : "adrequests")} active={homePanel === "adrequests"} badge={adRequests.filter((r) => (r.status || "pending") === "pending").length} />
          <StatCard icon={BadgeCheck} label="Sahiplenme Talebi" value={pendingClaims.length} color="#2E8B57" onClick={() => setHomePanel((p) => p === "claims" ? null : "claims")} active={homePanel === "claims"} badge={pendingClaims.length} />
        </div>

        {homePanel && <p className="text-sm text-[#7C6E70] -mb-2">Kartı tekrar tıklayarak kapatabilirsiniz.</p>}

        {/* Reklam Talepleri paneli */}
        {homePanel === "requests" && (
          <div className="bg-white rounded-2xl border border-[#E3CE9E] p-5" data-testid="home-panel-requests">
            <div className="flex items-center gap-2 mb-4">
              <Crown className="w-5 h-5 text-[#C6A15B]" />
              <h3 className="font-display text-lg text-[#2A2224]">Öne Çıkarma (Reklam) Talepleri</h3>
              <span className="ml-1 px-2 py-0.5 rounded-full bg-[#F6EAD0] text-[#8a6d1f] text-xs font-semibold">{requests.length}</span>
            </div>
            {requests.length === 0 ? (
              <p className="text-sm text-[#7C6E70] py-6 text-center">Bekleyen reklam talebi yok.</p>
            ) : (
            <div className="space-y-3">
              {requests.map((f) => (
                <div key={f.id} data-testid={`home-req-${f.id}`} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 rounded-xl bg-[#FBF3E4] border border-[#efdcb8]">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-lg bg-cover bg-center bg-[#eee] shrink-0" style={{ backgroundImage: f.cover ? `url(${f.cover})` : "none" }} />
                    <div className="min-w-0">
                      <p className="font-medium text-[#2A2224] truncate">{f.name}</p>
                      <p className="text-xs text-[#7C6E70]">{f.city}{f.district ? ` · ${f.district}` : ""} · Tel: {f.phone}</p>
                    </div>
                    <span className="px-2.5 py-1 rounded-full bg-white border border-[#e3ce9e] text-[#8a6d1f] text-xs font-medium shrink-0">{f.featurePlan || "1-ay"}</span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => approveFeature(f)} className="flex items-center gap-1.5 px-4 h-9 rounded-lg bg-gradient-to-r from-[#C6A15B] to-[#a9843f] text-white text-sm font-medium hover:shadow-md transition-all">
                      <CheckCircle2 className="w-4 h-4" /> Onayla & Öne Çıkar
                    </button>
                    <button onClick={() => rejectRequest(f)} className="px-4 h-9 rounded-lg border border-[#d8b0b5] text-[#9E2A3B] text-sm font-medium hover:bg-[#F6E4E6] transition-colors">
                      Reddet
                    </button>
                  </div>
                </div>
              ))}
            </div>
            )}
          </div>
        )}

        {/* Google Reklam Talepleri paneli */}
        {homePanel === "adrequests" && (
          <div className="bg-white rounded-2xl border border-[#d7e0f7] p-5" data-testid="home-panel-adrequests">
            <div className="flex items-center gap-2 mb-4">
              <Megaphone className="w-5 h-5 text-[#3b5bdb]" />
              <h3 className="font-display text-lg text-[#2A2224]">Reklam Talepleri</h3>
              <span className="ml-1 px-2 py-0.5 rounded-full bg-[#EEF2FB] text-[#3b5bdb] text-xs font-semibold">{adRequests.filter((r) => (r.status || "pending") === "pending").length} bekleyen</span>
              <button onClick={() => setSection("reklam-talepleri")} className="ml-auto text-xs text-[#9E2A3B] hover:underline">Tümünü Yönet →</button>
            </div>
            {adRequests.length === 0 ? (
              <p className="text-sm text-[#7C6E70] py-6 text-center">Henüz reklam talebi yok.</p>
            ) : (
            <div className="space-y-3">
              {adRequests.map((r) => {
                const st = r.status || "pending";
                const badge = st === "done" ? { c: "bg-[#E7F6EC] text-[#237045] border-[#B9E3C6]", t: "Tamamlandı" }
                  : st === "rejected" ? { c: "bg-[#FBEEF0] text-[#9E2A3B] border-[#E7A9B0]", t: "Reddedildi" }
                  : { c: "bg-[#FBF3E4] text-[#8a6d1f] border-[#E3CE9E]", t: "Bekliyor" };
                return (
                <div key={r.id} data-testid={`home-adreq-${r.id}`} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 rounded-xl border border-[#F0DDE0]">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium text-[#2A2224]"><span className="text-[#9E2A3B]">{r.firm_name || "Firma"}</span>{r.firm_phone ? <span className="text-[#a8999b]"> · {r.firm_phone}</span> : null}</p>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${badge.c}`}>{badge.t}</span>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${r.kind === "member" ? "bg-[#FBEEF0] text-[#9E2A3B] border-[#E7A9B0]" : r.kind === "instagram" ? "bg-[#FBEAF4] text-[#B5278E] border-[#F0C5E1]" : "bg-[#EEF2FB] text-[#3b5bdb] border-[#c9d6f5]"}`}>{r.kind === "member" ? "Üye Paneli" : r.kind === "instagram" ? "Instagram" : "Google"}</span>
                    </div>
                    <p className="text-sm text-[#5b4d4f] mt-0.5">{r.budget ? `Aylık bütçe: ${r.budget} ₺` : "Bütçe belirtilmedi"}{r.firm_city ? ` · ${r.firm_city}` : ""}</p>
                    {r.notes && <p className="text-xs text-[#7C6E70] mt-0.5">{r.notes}</p>}
                  </div>
                  {st === "pending" && (
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => markAdRequest(r, "done")} data-testid={`home-adreq-done-${r.id}`} className="flex items-center gap-1.5 px-4 h-9 rounded-lg bg-gradient-to-r from-[#5b7be0] to-[#3b5bdb] text-white text-sm font-medium hover:shadow-md transition-all"><CheckCircle2 className="w-4 h-4" /> Tamamlandı</button>
                    <button onClick={() => markAdRequest(r, "rejected")} data-testid={`home-adreq-reject-${r.id}`} className="px-4 h-9 rounded-lg border border-[#d8b0b5] text-[#9E2A3B] text-sm font-medium hover:bg-[#F6E4E6] transition-colors">Reddet</button>
                  </div>
                  )}
                </div>
                );
              })}
            </div>
            )}
          </div>
        )}

        {/* Sahiplenme Talepleri paneli */}
        {homePanel === "claims" && (
          <div className="bg-white rounded-2xl border border-[#B9E3C6] p-5" data-testid="home-panel-claims">
            <div className="flex items-center gap-2 mb-4">
              <BadgeCheck className="w-5 h-5 text-[#2E8B57]" />
              <h3 className="font-display text-lg text-[#2A2224]">Sahiplenme Talepleri</h3>
              <span className="ml-1 px-2 py-0.5 rounded-full bg-[#E7F6EC] text-[#237045] text-xs font-semibold">{pendingClaims.length}</span>
              <button onClick={() => setSection("sahiplenme")} className="ml-auto text-xs text-[#9E2A3B] hover:underline">Tümünü Yönet →</button>
            </div>
            {pendingClaims.length === 0 ? (
              <p className="text-sm text-[#7C6E70] py-6 text-center">Bekleyen sahiplenme talebi yok.</p>
            ) : (
            <div className="space-y-3">
              {pendingClaims.map((r) => (
                <div key={r.id} data-testid={`home-claim-${r.id}`} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 rounded-xl bg-[#F2FaF5] border border-[#cdead8]">
                  <div className="min-w-0">
                    <p className="font-medium text-[#2A2224]">
                      <span className="text-[#9E2A3B]">{r.firm_name || "Firma"}</span>
                      <span className="text-[#a8999b]"> · {r.firm_phone}</span>
                    </p>
                    <p className="text-sm text-[#5b4d4f] mt-0.5">Talep edilen ilan: <span className="font-medium">{r.venue_name}</span>{r.city ? ` · ${[r.district, r.city].filter(Boolean).join(", ")}` : ""}</p>
                    <a href={`/mekan/${r.venue_slug}`} target="_blank" rel="noreferrer" className="text-xs text-[#9E2A3B] hover:underline inline-flex items-center gap-1 mt-1"><Eye className="w-3.5 h-3.5" /> İlanı Gör</a>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => approveClaim(r)} data-testid={`home-claim-approve-${r.id}`} className="flex items-center gap-1.5 px-4 h-9 rounded-lg bg-gradient-to-r from-[#2E8B57] to-[#237045] text-white text-sm font-medium hover:shadow-md transition-all">
                      <CheckCircle2 className="w-4 h-4" /> Onayla
                    </button>
                    <button onClick={() => rejectClaim(r)} data-testid={`home-claim-reject-${r.id}`} className="px-4 h-9 rounded-lg border border-[#E7A9B0] text-[#9E2A3B] text-sm font-medium hover:bg-[#FBF0F2] transition-colors">
                      Reddet
                    </button>
                  </div>
                </div>
              ))}
            </div>
            )}
          </div>
        )}

        {/* İşletme listesi panelleri (total/approved/pending/featured) */}
        {["total", "approved", "pending", "featured"].includes(homePanel) && (() => {
          const titleMap = { total: "Tüm İşletmeler", approved: "Yayındaki İşletmeler", pending: "Beklemedeki İşletmeler", featured: "Öne Çıkan İşletmeler" };
          const list = firms.filter((f) =>
            homePanel === "total" ? true
            : homePanel === "approved" ? f.status === "approved"
            : homePanel === "pending" ? f.status === "pending"
            : f.featured
          );
          return (
            <div className="bg-white rounded-2xl border border-[#F0DDE0] p-5" data-testid={`home-panel-${homePanel}`}>
              <div className="flex items-center gap-2 mb-4">
                <Building2 className="w-5 h-5 text-[#9E2A3B]" />
                <h3 className="font-display text-lg text-[#2A2224]">{titleMap[homePanel]}</h3>
                <span className="ml-1 px-2 py-0.5 rounded-full bg-[#FBF0F2] text-[#9E2A3B] text-xs font-semibold">{list.length}</span>
              </div>
              {list.length === 0 ? (
                <p className="text-sm text-[#7C6E70] py-6 text-center">Kayıt yok.</p>
              ) : (
                <div className="space-y-2 max-h-[480px] overflow-y-auto pr-1">
                  {list.map((f) => (
                    <button key={f.id} onClick={() => navigate(`/patron/firma/${f.id}`)} data-testid={`home-firm-${f.id}`}
                      className="w-full text-left flex items-center gap-3 p-2.5 rounded-xl border border-[#F0DDE0] hover:bg-[#FDFAF8] transition-colors">
                      <div className="w-10 h-10 rounded-lg bg-[#F6E4E6] overflow-hidden shrink-0">
                        {f.cover ? <img src={f.cover} alt="" loading="lazy" className="w-full h-full object-cover" /> : <Building2 className="w-5 h-5 text-[#C15B6B] m-2.5" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-[#2A2224] truncate flex items-center gap-1.5">{f.name}{f.featured && <Star className="w-3.5 h-3.5 text-[#C6A15B]" fill="#C6A15B" />}</p>
                        <p className="text-xs text-[#7C6E70] truncate">{catName(f.category)}{f.city ? ` · ${[f.district, f.city].filter(Boolean).join(", ")}` : ""}</p>
                      </div>
                      {f.status === "pending"
                        ? <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#FBF3E3] text-[#9a7b2e] text-xs shrink-0"><Clock className="w-3 h-3" /> Beklemede</span>
                        : <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#E7F3EC] text-[#2E7D5B] text-xs shrink-0"><CheckCircle2 className="w-3 h-3" /> Yayında</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })()}
        </div>
        )}

        {section === "ayarlar" && (
        <div className="space-y-6">
        {/* Sıralama Ayarları */}
        <div className="bg-white rounded-2xl border border-[#F0DDE0] p-5">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-xl bg-[#EEF2FB] flex items-center justify-center shrink-0">
                <Shuffle className="w-6 h-6 text-[#3b5bdb]" />
              </div>
              <div>
                <h3 className="font-display text-lg text-[#2A2224]">Sıralama Ayarları</h3>
                <p className="text-sm text-[#7C6E70] max-w-xl">
                  İşletmeler kendi kategorisi ve ilinde belirli aralıklarla otomatik yer değiştirir. Böylece her firma sırayla üst sıralarda görünür.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="min-w-[170px]">
                <Select value={rotationHours} onValueChange={setRotationHours}>
                  <SelectTrigger className="bg-[#FBF5F0] border-[#F0DDE0] h-11 text-[#2A2224]">
                    <Clock className="w-4 h-4 text-[#C15B6B] mr-1" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Her 1 saatte</SelectItem>
                    <SelectItem value="3">Her 3 saatte</SelectItem>
                    <SelectItem value="6">Her 6 saatte</SelectItem>
                    <SelectItem value="12">Her 12 saatte</SelectItem>
                    <SelectItem value="24">Her 24 saatte (günlük)</SelectItem>
                    <SelectItem value="72">Her 3 günde</SelectItem>
                    <SelectItem value="168">Her hafta</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <button onClick={saveRotation} disabled={savingRot}
                className="flex items-center gap-2 px-5 h-11 rounded-xl bg-gradient-to-r from-[#C15B6B] to-[#9E2A3B] text-white text-sm font-medium hover:shadow-md transition-all disabled:opacity-70">
                {savingRot ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                Kaydet
              </button>
              <button onClick={shuffleNow} disabled={shuffling}
                className="flex items-center gap-2 px-5 h-11 rounded-xl border border-[#3b5bdb] text-[#3b5bdb] text-sm font-medium hover:bg-[#EEF2FB] transition-colors disabled:opacity-70">
                {shuffling ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                Şimdi Karıştır
              </button>
            </div>
          </div>
        </div>

        {/* Üyelik Ayarları */}
        <div className="bg-white rounded-2xl border border-[#F0DDE0] p-5">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-11 h-11 rounded-xl bg-[#FBF3E4] flex items-center justify-center shrink-0">
              <Crown className="w-6 h-6 text-[#C6A15B]" />
            </div>
            <div>
              <h3 className="font-display text-lg text-[#2A2224]">Üyelik Ayarları</h3>
              <p className="text-sm text-[#7C6E70] max-w-xl">Firma panelinde gösterilecek üyelik fiyatı, süresi ve premium fotoğraf limiti. Ücretsiz üyeler en fazla 5 fotoğraf ekleyebilir ve "Öne Çıkma" talebi gönderemez.</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-sm text-[#5b4d4f]">Düğün Mekanları Üyelik Fiyatı (₺)</label>
              <Input data-testid="admin-membership-price-mekan" type="number" value={membership.priceMekan} onChange={(e) => setMembership((p) => ({ ...p, priceMekan: e.target.value }))} placeholder="Örn. 799" className="border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
              <p className="text-[11px] text-[#a8999b]">Düğün Salonları, Kır Düğünü, Oteller, Sosyal Tesisler, Kulüpler, Kına/Söz, Evlendirme Daireleri</p>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm text-[#5b4d4f]">Düğün Firmaları Üyelik Fiyatı (₺)</label>
              <Input data-testid="admin-membership-price-firma" type="number" value={membership.priceFirma} onChange={(e) => setMembership((p) => ({ ...p, priceFirma: e.target.value }))} placeholder="Örn. 499" className="border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
              <p className="text-[11px] text-[#a8999b]">Organizasyon, Fotoğrafçı, Gelinlik, Damatlık, Orkestra/DJ, Catering ve diğer firma kategorileri</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
            <div className="space-y-1.5">
              <label className="text-sm text-[#5b4d4f]">Üyelik Süresi (ay)</label>
              <Input data-testid="admin-membership-duration" type="number" value={membership.durationMonths} onChange={(e) => setMembership((p) => ({ ...p, durationMonths: e.target.value }))} placeholder="12" className="border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm text-[#5b4d4f]">Premium Fotoğraf Limiti</label>
              <Input data-testid="admin-membership-imglimit" type="number" value={membership.premiumImageLimit} onChange={(e) => setMembership((p) => ({ ...p, premiumImageLimit: e.target.value }))} placeholder="20" className="border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
            <div className="space-y-1.5">
              <label className="text-sm text-[#5b4d4f]">2. Ek Kategori Ücreti (₺)</label>
              <Input data-testid="admin-membership-extracat" type="number" value={membership.extraCategoryPrice} onChange={(e) => setMembership((p) => ({ ...p, extraCategoryPrice: e.target.value }))} placeholder="Örn. 199" className="border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm text-[#5b4d4f]">3. Ek Kategori Ücreti (₺)</label>
              <Input data-testid="admin-membership-extracat2" type="number" value={membership.extraCategoryPrice2} onChange={(e) => setMembership((p) => ({ ...p, extraCategoryPrice2: e.target.value }))} placeholder="Boş = 2. ücret" className="border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm text-[#5b4d4f]">4. Ek Kategori Ücreti (₺)</label>
              <Input data-testid="admin-membership-extracat3" type="number" value={membership.extraCategoryPrice3} onChange={(e) => setMembership((p) => ({ ...p, extraCategoryPrice3: e.target.value }))} placeholder="Boş = 2. ücret" className="border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
            </div>
          </div>
          <p className="text-xs text-[#7C6E70] mt-2">Bir işletme en fazla 3 ek kategori ekleyebilir (ana kategori 1. kategoridir). Her ek kategori için farklı (örn. daha düşük) ücret belirleyebilirsiniz; 3. ve 4. boş bırakılırsa 2. ücret uygulanır. Süre üyelik süresiyle aynıdır.</p>
          <button onClick={saveMembership} disabled={savingMem} data-testid="admin-membership-save"
            className="mt-4 flex items-center gap-2 px-5 h-11 rounded-xl bg-gradient-to-r from-[#C6A15B] to-[#a9843f] text-white text-sm font-medium hover:shadow-md transition-all disabled:opacity-70">
            {savingMem ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            Üyelik Ayarlarını Kaydet
          </button>
        </div>

        {/* iyzico Ödeme Ayarları */}
        <div className="bg-white rounded-2xl border border-[#F0DDE0] p-5">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-11 h-11 rounded-xl bg-[#E9F7EF] flex items-center justify-center shrink-0">
              <Lock className="w-6 h-6 text-[#1EBE5A]" />
            </div>
            <div>
              <h3 className="font-display text-lg text-[#2A2224]">iyzico Ödeme Ayarları</h3>
              <p className="text-sm text-[#7C6E70] max-w-xl">
                iyzico API bilgilerinizi girin. Bilgiler şifrelenerek saklanır ve hiçbir yerde tekrar gösterilmez.
                {iyzico.configured && <span className="text-[#1EBE5A] font-medium"> · Şu an yapılandırılmış ({iyzico.apiKeyMasked})</span>}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <label className="text-sm text-[#5b4d4f]">Ortam</label>
              <Select value={iyzico.environment} onValueChange={(v) => setIyzico((p) => ({ ...p, environment: v }))}>
                <SelectTrigger data-testid="admin-iyzico-env" className="border-[#F0DDE0] h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sandbox">Test (Sandbox)</SelectItem>
                  <SelectItem value="production">Canlı (Production)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm text-[#5b4d4f]">API Key</label>
              <Input data-testid="admin-iyzico-apikey" value={iyzico.apiKey} onChange={(e) => setIyzico((p) => ({ ...p, apiKey: e.target.value }))} placeholder={iyzico.configured ? "Değiştirmek için yeni değer girin" : "sandbox-..."} className="border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm text-[#5b4d4f]">Secret Key</label>
              <Input data-testid="admin-iyzico-secret" type="password" value={iyzico.secretKey} onChange={(e) => setIyzico((p) => ({ ...p, secretKey: e.target.value }))} placeholder={iyzico.configured ? "Değiştirmek için yeni değer girin" : "sandbox-..."} className="border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
            </div>
          </div>
          <button onClick={saveIyzico} disabled={savingIyz} data-testid="admin-iyzico-save"
            className="mt-4 flex items-center gap-2 px-5 h-11 rounded-xl bg-gradient-to-r from-[#C15B6B] to-[#9E2A3B] text-white text-sm font-medium hover:shadow-md transition-all disabled:opacity-70">
            {savingIyz ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            iyzico Bilgilerini Kaydet
          </button>
        </div>

        {/* Admin bilgi */}
        <div className="bg-white rounded-2xl border border-[#F0DDE0] p-5">
          <h3 className="font-display text-lg text-[#2A2224] mb-1">Yönetici Bilgileri</h3>
          <p className="text-sm text-[#7C6E70]">Kullanıcı adı: <b>patron</b> · Şifre değişikliği için geliştirici ile iletişime geçebilirsiniz.</p>
        </div>
        </div>
        )}

        {section === "firma-panel-ayarlari" && (
        <div className="space-y-4" data-testid="firma-panel-ayarlari-section">
          <div>
            <h3 className="font-display text-xl text-[#2A2224]">Firma Panel Ayarları</h3>
            <p className="text-sm text-[#7C6E70] mt-1">Firmaların panelinde kullanabileceği ayarlar.</p>
          </div>

          <div className="bg-white rounded-2xl border border-[#F0DDE0] p-5">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-11 h-11 rounded-xl bg-[#F6E4E6] flex items-center justify-center shrink-0">
                <Tag className="w-6 h-6 text-[#9E2A3B]" />
              </div>
              <div>
                <h3 className="font-display text-lg text-[#2A2224]">Düğün Mekanları — Özellik / Hizmet Listesi</h3>
                <p className="text-sm text-[#7C6E70] max-w-xl">Bu özellikler yalnızca <strong>Düğün Mekanları</strong> grubundaki firmaların panelinde görünür. İstediğiniz kadar özellik ekleyip kaldırabilirsiniz.</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mb-4" data-testid="admin-feature-list">
              {featureOptions.length === 0 && <span className="text-sm text-[#a8999b]">Henüz özellik yok.</span>}
              {featureOptions.map((f) => (
                <span key={f} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#FBF0F2] text-[#9E2A3B] text-sm border border-[#F0DDE0]">
                  {f}
                  <button onClick={() => removeFeature(f)} disabled={savingFeat} data-testid={`feature-remove-${f}`} className="hover:text-[#6f1d29]"><X className="w-3.5 h-3.5" /></button>
                </span>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                data-testid="admin-feature-input"
                value={newFeature}
                onChange={(e) => setNewFeature(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addFeature(); } }}
                placeholder="Yeni özellik yazın (örn. Havuz Başı) ve Ekle'ye basın"
                className="border-[#F0DDE0] focus-visible:ring-[#C15B6B]"
              />
              <button onClick={addFeature} disabled={savingFeat || !newFeature.trim()} data-testid="admin-feature-add"
                className="flex items-center justify-center gap-2 px-5 h-10 rounded-xl bg-gradient-to-r from-[#C15B6B] to-[#9E2A3B] text-white text-sm font-medium hover:shadow-md transition-all disabled:opacity-60 shrink-0">
                {savingFeat ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Ekle
              </button>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-[#F0DDE0] p-5" data-testid="firma-panel-ayarlari-firma-card">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-11 h-11 rounded-xl bg-[#EEF2FB] flex items-center justify-center shrink-0">
                <Tag className="w-6 h-6 text-[#3b5bdb]" />
              </div>
              <div>
                <h3 className="font-display text-lg text-[#2A2224]">Düğün Firmaları — Özellik / Hizmet Listesi</h3>
                <p className="text-sm text-[#7C6E70] max-w-xl">Bu özellikler yalnızca <strong>Düğün Firmaları</strong> grubundaki firmaların (fotoğrafçı, organizasyon vb.) panelinde görünür.</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mb-4" data-testid="admin-feature-list-firma">
              {featureOptionsFirma.length === 0 && <span className="text-sm text-[#a8999b]">Henüz özellik yok.</span>}
              {featureOptionsFirma.map((f) => (
                <span key={f} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#EEF2FB] text-[#3b5bdb] text-sm border border-[#d7e0f7]">
                  {f}
                  <button onClick={() => removeFeatureFirma(f)} disabled={savingFeatFirma} data-testid={`feature-firma-remove-${f}`} className="hover:text-[#27409c]"><X className="w-3.5 h-3.5" /></button>
                </span>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                data-testid="admin-feature-firma-input"
                value={newFeatureFirma}
                onChange={(e) => setNewFeatureFirma(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addFeatureFirma(); } }}
                placeholder="Yeni özellik yazın (örn. Drone ile Çekim) ve Ekle'ye basın"
                className="border-[#F0DDE0] focus-visible:ring-[#3b5bdb]"
              />
              <button onClick={addFeatureFirma} disabled={savingFeatFirma || !newFeatureFirma.trim()} data-testid="admin-feature-firma-add"
                className="flex items-center justify-center gap-2 px-5 h-10 rounded-xl bg-gradient-to-r from-[#5b7be0] to-[#3b5bdb] text-white text-sm font-medium hover:shadow-md transition-all disabled:opacity-60 shrink-0">
                {savingFeatFirma ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Ekle
              </button>
            </div>
          </div>
        </div>
        )}

        {section === "isletmeler" && (
        <div className="space-y-6">
        {!isViewer && (
          <div className="flex justify-end">
            <AdminAddFirm headers={headers} toast={toast} />
          </div>
        )}
        {!isViewer && <DeleteAllFirms headers={headers} toast={toast} onDone={load} count={stats?.total ?? firms.length} />}
        {firms.length >= 500 && (
          <p className="text-xs text-[#7C6E70] -mt-3" data-testid="firms-limit-note">Listede en yeni 500 işletme gösteriliyor. Diğerlerini görmek için arama, kategori veya şehir filtresini kullanın.</p>
        )}
        {/* Filters */}
        <div className="bg-white rounded-2xl border border-[#F0DDE0] p-4 flex flex-col sm:flex-row gap-3">
          <div className="flex-1 flex items-center gap-2 px-3 rounded-xl bg-[#FBF5F0]">
            <Search className="w-5 h-5 text-[#C15B6B]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="İsim veya telefon ile ara..." className="w-full bg-transparent h-11 outline-none text-[#2A2224] placeholder:text-[#a8999b]" />
          </div>
          <div className="min-w-[200px]">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="bg-[#FBF5F0] border-0 h-11 text-[#2A2224]">
                <Filter className="w-4 h-4 text-[#C15B6B] mr-1" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="hepsi">Tüm Durumlar</SelectItem>
                <SelectItem value="approved">Yayında</SelectItem>
                <SelectItem value="pending">Beklemede</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[190px]">
            <Select value={catFilter} onValueChange={setCatFilter}>
              <SelectTrigger className="bg-[#FBF5F0] border-0 h-11 text-[#2A2224]">
                <SelectValue placeholder="Kategori" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="hepsi">Tüm Kategoriler</SelectItem>
                {siteCategories.map((c) => (
                  <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[160px]">
            <Select value={cityFilterA} onValueChange={setCityFilterA}>
              <SelectTrigger className="bg-[#FBF5F0] border-0 h-11 text-[#2A2224]">
                <SelectValue placeholder="Şehir" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="hepsi">Tüm Şehirler</SelectItem>
                {adminCityOptions.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[150px]">
            <Select value={featFilter} onValueChange={setFeatFilter}>
              <SelectTrigger className="bg-[#FBF5F0] border-0 h-11 text-[#2A2224]">
                <SelectValue placeholder="Öne Çıkan" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="hepsi">Tümü</SelectItem>
                <SelectItem value="featured">Öne Çıkanlar</SelectItem>
                <SelectItem value="normal">Öne Çıkmayanlar</SelectItem>
                <SelectItem value="eksik">Eksik Bilgili</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {!isViewer && (
          <BulkApproveBar headers={headers} toast={toast} pendingIds={pendingVisibleIds} selected={selectedFirms} clearSelected={() => setSelectedFirms([])} onDone={load} />
        )}

        {/* Table */}
        <div className="bg-white rounded-2xl border border-[#F0DDE0] overflow-hidden">
          {loading ? (
            <div className="py-20 flex justify-center"><Loader2 className="w-7 h-7 text-[#9E2A3B] animate-spin" /></div>
          ) : visibleFirms.length === 0 ? (
            <div className="py-20 text-center text-[#7C6E70]">
              <Building2 className="w-10 h-10 mx-auto mb-3 text-[#E7A9B0]" />
              Kayıtlı işletme bulunamadı.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#FBF0F2] text-[#7C6E70] text-left">
                    {!isViewer && (
                      <th className="pl-4 py-3 w-8">
                        <input type="checkbox" checked={allVisibleSelected} onChange={() => setSelectedFirms(allVisibleSelected ? [] : visibleFirms.map((f) => f.id))} data-testid="firms-select-all" className="w-4 h-4 accent-[#9E2A3B] cursor-pointer" />
                      </th>
                    )}
                    <th className="px-4 py-3 font-medium">İşletme</th>
                    <th className="px-4 py-3 font-medium hidden md:table-cell">Kategori</th>
                    <th className="px-4 py-3 font-medium hidden lg:table-cell">Konum</th>
                    <th className="px-4 py-3 font-medium hidden lg:table-cell">Telefon</th>
                    <th className="px-4 py-3 font-medium hidden xl:table-cell">Üyelik Tarihi</th>
                    <th className="px-4 py-3 font-medium">Durum</th>
                    <th className="px-4 py-3 font-medium text-right">İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleFirms.map((f) => (
                    <tr key={f.id} onClick={() => navigate(`/patron/firma/${f.id}`)} data-testid={`firm-row-${f.id}`} className={`border-t border-[#F0DDE0] hover:bg-[#FDFAF8] cursor-pointer ${selectedFirms.includes(f.id) ? "bg-[#FDF3F4]" : ""}`}>
                      {!isViewer && (
                        <td className="pl-4 py-3 w-8" onClick={(e) => e.stopPropagation()}>
                          <input type="checkbox" checked={selectedFirms.includes(f.id)} onChange={() => toggleSel(f.id)} data-testid={`firm-select-${f.id}`} className="w-4 h-4 accent-[#9E2A3B] cursor-pointer" />
                        </td>
                      )}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-11 h-11 rounded-lg bg-[#F6E4E6] overflow-hidden shrink-0">
                            {f.cover ? <img src={f.cover} alt="" loading="lazy" className="w-full h-full object-cover" /> : <Building2 className="w-5 h-5 text-[#C15B6B] m-3" />}
                          </div>
                          <div>
                            <p className="font-medium text-[#2A2224] flex items-center flex-wrap gap-1.5">
                              {f.name}
                              {f.featured && <Star className="w-3.5 h-3.5 text-[#C6A15B]" fill="#C6A15B" />}
                              {f.missing?.length > 0 && (
                                <span title={`Eksik: ${f.missing.join(", ")}`} data-testid={`firm-missing-${f.id}`} className="inline-flex items-start gap-1 px-2 py-0.5 rounded-md bg-[#FFF1E6] text-[#B4561F] text-[11px] font-medium leading-snug">
                                  <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" /> Eksik: {f.missing.join(", ")}
                                </span>
                              )}
                            </p>
                            <p className="text-xs text-[#a8999b]">{f.imageCount} foto{f.priceFrom ? ` • ${formatPrice(f.priceFrom)}` : ""}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell text-[#5b4d4f]">{catName(f.category)}</td>
                      <td className="px-4 py-3 hidden lg:table-cell text-[#5b4d4f]">{[f.district, f.city].filter(Boolean).join(", ")}</td>
                      <td className="px-4 py-3 hidden lg:table-cell text-[#5b4d4f]">{f.phone}</td>
                      <td className="px-4 py-3 hidden xl:table-cell text-[#5b4d4f]">{f.createdAt ? new Date(f.createdAt).toLocaleDateString("tr-TR") : "—"}</td>
                      <td className="px-4 py-3">
                        {f.status === "pending" ? (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#FBF3E3] text-[#9a7b2e] text-xs"><Clock className="w-3 h-3" /> Beklemede</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#E7F3EC] text-[#2E7D5B] text-xs"><CheckCircle2 className="w-3 h-3" /> Yayında</span>
                        )}
                      </td>
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <FirmActionsMenu f={f} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        </div>
        )}

        {section === "danisman" && (
        <div className="space-y-6" data-testid="danisman-section">
          <div className="bg-white rounded-2xl border border-[#F0DDE0] p-5 max-w-2xl">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-11 h-11 rounded-xl bg-[#E7F7EE] flex items-center justify-center shrink-0">
                <MessageCircle className="w-6 h-6 text-[#1FA855]" />
              </div>
              <div>
                <h3 className="font-display text-lg text-[#2A2224]">Danışman WhatsApp</h3>
                <p className="text-sm text-[#7C6E70]">Buraya girdiğiniz WhatsApp numarası, anasayfadaki <strong>"Hemen Başla"</strong> butonuna bağlanır. Ziyaretçiler bu butona tıklayınca doğrudan WhatsApp üzerinden size ulaşır. Boş bırakılırsa buton varsayılan davranışını korur.</p>
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs text-[#7C6E70]">WhatsApp Numarası</label>
              <Input
                data-testid="advisor-whatsapp-input"
                value={advisorWa}
                onChange={(e) => setAdvisorWa(e.target.value)}
                placeholder="Örn. 0555 123 45 67"
                disabled={isViewer}
                className="border-[#F0DDE0] focus-visible:ring-[#C15B6B]"
              />
              <p className="text-xs text-[#A99C9E]">Numarayı ülke kodu olmadan (0 ile) veya tam wa.me bağlantısı olarak girebilirsiniz.</p>
            </div>
            {!isViewer && (
              <button onClick={saveAdvisor} disabled={advisorBusy} data-testid="advisor-save-button" className="mt-4 flex items-center gap-2 px-5 h-10 rounded-xl bg-gradient-to-r from-[#1FA855] to-[#128C3E] text-white text-sm font-medium hover:shadow-md transition-all disabled:opacity-70">
                {advisorBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Kaydet
              </button>
            )}
          </div>
        </div>
        )}

        {section === "kategori-ayarlari" && (
        <div className="space-y-6" data-testid="kategori-ayarlari-section">
          <div className="bg-white rounded-2xl border border-[#F0DDE0] p-5">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-11 h-11 rounded-xl bg-[#FBEEF0] flex items-center justify-center shrink-0">
                <Tag className="w-6 h-6 text-[#9E2A3B]" />
              </div>
              <div>
                <h3 className="font-display text-lg text-[#2A2224]">Kategori Ayarları</h3>
                <p className="text-sm text-[#7C6E70] max-w-2xl">Sitedeki kategorileri buradan ekleyip düzenleyebilirsiniz. Değişiklikler menülerde, anasayfada ve arama alanında anında görünür. URL kısayolu (slug) addan otomatik oluşturulur.</p>
              </div>
            </div>

            {!isViewer && (
            <div className="rounded-xl border border-[#F0DDE0] bg-[#FBF5F0] p-4 mb-5" data-testid="category-form">
              <p className="text-sm font-medium text-[#2A2224] mb-3">{catForm.id ? "Kategoriyi Düzenle" : "Yeni Kategori Ekle"}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="space-y-1.5">
                  <label className="text-xs text-[#7C6E70]">Kategori Adı</label>
                  <Input data-testid="category-name-input" value={catForm.name} onChange={(e) => setCatForm((p) => ({ ...p, name: e.target.value }))} placeholder="Örn. Düğün Salonları" className="bg-white border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs text-[#7C6E70]">Grup</label>
                  <Select value={catForm.group} onValueChange={(v) => setCatForm((p) => ({ ...p, group: v }))}>
                    <SelectTrigger data-testid="category-group-select" className="bg-white border-[#F0DDE0]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mekan">Düğün Mekanları</SelectItem>
                      <SelectItem value="firma">Düğün Firmaları</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs text-[#7C6E70]">Görsel URL (opsiyonel)</label>
                  <Input data-testid="category-image-input" value={catForm.image} onChange={(e) => setCatForm((p) => ({ ...p, image: e.target.value }))} placeholder="https://..." className="bg-white border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs text-[#7C6E70]">Sıra</label>
                  <Input data-testid="category-order-input" type="number" value={catForm.order} onChange={(e) => setCatForm((p) => ({ ...p, order: e.target.value }))} placeholder="0" className="bg-white border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
                </div>
              </div>
              <div className="flex items-center gap-2 mt-4">
                <button onClick={saveCategory} disabled={catBusy} data-testid="category-save-button" className="flex items-center gap-2 px-5 h-10 rounded-xl bg-gradient-to-r from-[#C15B6B] to-[#9E2A3B] text-white text-sm font-medium hover:shadow-md transition-all disabled:opacity-70">
                  {catBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : catForm.id ? <Save className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                  {catForm.id ? "Güncelle" : "Kategori Ekle"}
                </button>
                {catForm.id && (
                  <button onClick={resetCatForm} data-testid="category-cancel-button" className="px-4 h-10 rounded-xl border border-[#F0DDE0] text-[#7C6E70] text-sm hover:bg-[#FBF0F2] transition-colors">İptal</button>
                )}
              </div>
            </div>
            )}

            <div className="rounded-xl border border-[#F0DDE0] overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#FBF0F2] text-[#7C6E70] text-left">
                      <th className="px-4 py-3 font-medium">Kategori</th>
                      <th className="px-4 py-3 font-medium hidden sm:table-cell">Grup</th>
                      <th className="px-4 py-3 font-medium hidden md:table-cell">URL Kısayolu</th>
                      <th className="px-4 py-3 font-medium hidden lg:table-cell">Sıra</th>
                      <th className="px-4 py-3 font-medium text-right">İşlemler</th>
                    </tr>
                  </thead>
                  <tbody>
                    {siteCategories.length === 0 ? (
                      <tr><td colSpan={5} className="py-12 text-center text-[#7C6E70]">Kategori bulunamadı.</td></tr>
                    ) : siteCategories.map((c) => (
                      <tr key={c.id || c.slug} data-testid={`category-row-${c.slug}`} className="border-t border-[#F0DDE0] hover:bg-[#FDFAF8]">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-[#F6E4E6] overflow-hidden shrink-0">
                              {c.image ? <img src={c.image} alt="" className="w-full h-full object-cover" /> : <Tag className="w-4 h-4 text-[#C15B6B] m-3" />}
                            </div>
                            <span className="font-medium text-[#2A2224]">{c.name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 hidden sm:table-cell">
                          <span className={`inline-flex px-2.5 py-1 rounded-full text-xs ${c.group === "mekan" ? "bg-[#EEF2FB] text-[#3b5bdb]" : "bg-[#FBF3E4] text-[#8a6d1f]"}`}>{c.group === "mekan" ? "Mekan" : "Firma"}</span>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell text-[#7C6E70]">{c.slug}</td>
                        <td className="px-4 py-3 hidden lg:table-cell text-[#5b4d4f]">{c.order ?? 0}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1.5">
                            <Link to={`/kategori/${c.slug}`} className="p-2 rounded-lg text-[#9E2A3B] hover:bg-[#F6E4E6] transition-colors" title="Sitede gör"><Eye className="w-4 h-4" /></Link>
                            {!isViewer && (
                              <>
                                <button onClick={() => editCategory(c)} data-testid={`category-edit-${c.slug}`} className="p-2 rounded-lg text-[#3b5bdb] hover:bg-[#EEF2FB] transition-colors" title="Düzenle"><Pencil className="w-4 h-4" /></button>
                                <button onClick={() => deleteCategory(c)} data-testid={`category-delete-${c.slug}`} className="p-2 rounded-lg text-[#c0392b] hover:bg-[#FBEEF0] transition-colors" title="Sil"><Trash2 className="w-4 h-4" /></button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        )}

        {section === "yeni" && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#C6A15B]" />
            <h3 className="font-display text-xl text-[#2A2224]">Yeni Eklenen İşletmeler</h3>
          </div>
          <p className="text-sm text-[#7C6E70]">Son 7 gün içinde siteye kayıt olan işletmeler (en yeniden eskiye). 7 günü geçenler listeden otomatik çıkar.</p>
          <FirmsTable list={firms.filter((f) => f.createdAt && new Date(f.createdAt).getTime() >= Date.now() - 7 * 24 * 60 * 60 * 1000)} />
        </div>
        )}

        {section === "reklam" && (
        <div className="space-y-6">
          <div>
            <div className="flex items-center gap-2">
              <Megaphone className="w-5 h-5 text-[#C6A15B]" />
              <h3 className="font-display text-xl text-[#2A2224]">Reklam Yönetimi</h3>
            </div>
            <p className="text-sm text-[#7C6E70] mt-1">Öne çıkma taleplerini onaylayın ve reklamlı (öne çıkan) işletmeleri yönetin.</p>
          </div>

          {/* Paket Fiyatları */}
          <div className="bg-white rounded-2xl border border-[#E3CE9E] p-5">
            <h4 className="font-medium text-[#2A2224] mb-1 flex items-center gap-2"><Crown className="w-4 h-4 text-[#C6A15B]" /> Ana Sayfada Öne Çık</h4>
            <p className="text-sm text-[#7C6E70] mb-4">Buraya girdiğiniz fiyatlar tüm üye işletmelerin panelinde görünür (₺).</p>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {[["1-ay", "1 Aylık"], ["3-ay", "3 Aylık"], ["6-ay", "6 Aylık"], ["12-ay", "1 Yıllık"]].map(([key, label]) => (
                <div key={key}>
                  <label className="text-xs text-[#7C6E70] mb-1 block">{label} (₺)</label>
                  <input type="number" value={adPrices[key]} onChange={(e) => setAdPrices({ ...adPrices, [key]: e.target.value })} placeholder="0" className="w-full h-11 px-3 rounded-xl bg-[#FBF5F0] outline-none text-[#2A2224]" />
                </div>
              ))}
            </div>
            <button onClick={saveAdPrices} disabled={savingAd} className="mt-4 flex items-center gap-2 px-5 h-11 rounded-xl bg-gradient-to-r from-[#C6A15B] to-[#a9843f] text-white text-sm font-medium hover:shadow-md transition-all disabled:opacity-70">
              {savingAd ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Fiyatları Kaydet
            </button>
          </div>

          {/* Kampanya — Ana Sayfada Öne Çık */}
          <div className="bg-white rounded-2xl border border-[#d7e0f7] p-5" data-testid="campaign-featured">
            <div className="flex items-center justify-between gap-3 mb-1">
              <h4 className="font-medium text-[#2A2224] flex items-center gap-2"><Tag className="w-4 h-4 text-[#3b5bdb]" /> Ana Sayfada Öne Çık — Kampanya</h4>
              <label className="flex items-center gap-2 text-sm text-[#5b4d4f]">
                <input type="checkbox" checked={campaigns.featured.enabled} onChange={(e) => setCampaigns((p) => ({ ...p, featured: { ...p.featured, enabled: e.target.checked } }))} data-testid="campaign-featured-enabled" /> Kampanya aktif
              </label>
            </div>
            <p className="text-sm text-[#7C6E70] mb-4">Belirlediğiniz tarih aralığında bu pakete indirim uygulanır. Firma panelinde eski fiyat çizili, indirimli kampanya fiyatı gösterilir.</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-[#7C6E70] mb-1 block">Başlangıç Tarihi</label>
                <input type="date" value={campaigns.featured.start} onChange={(e) => setCampaigns((p) => ({ ...p, featured: { ...p.featured, start: e.target.value } }))} data-testid="campaign-featured-start" className="w-full h-11 px-3 rounded-xl bg-[#FBF5F0] outline-none text-[#2A2224]" />
              </div>
              <div>
                <label className="text-xs text-[#7C6E70] mb-1 block">Bitiş Tarihi</label>
                <input type="date" value={campaigns.featured.end} onChange={(e) => setCampaigns((p) => ({ ...p, featured: { ...p.featured, end: e.target.value } }))} data-testid="campaign-featured-end" className="w-full h-11 px-3 rounded-xl bg-[#FBF5F0] outline-none text-[#2A2224]" />
              </div>
              <div>
                <label className="text-xs text-[#7C6E70] mb-1 block">İndirim (%)</label>
                <input type="number" min="0" max="100" value={campaigns.featured.percent} onChange={(e) => setCampaigns((p) => ({ ...p, featured: { ...p.featured, percent: e.target.value } }))} data-testid="campaign-featured-percent" placeholder="örn. 25" className="w-full h-11 px-3 rounded-xl bg-[#FBF5F0] outline-none text-[#2A2224]" />
              </div>
            </div>
            <button onClick={() => saveCampaign("featured")} disabled={savingCampaign === "featured"} data-testid="campaign-featured-save" className="mt-4 flex items-center gap-2 px-5 h-11 rounded-xl bg-gradient-to-r from-[#5b7be0] to-[#3b5bdb] text-white text-sm font-medium hover:shadow-md transition-all disabled:opacity-70">
              {savingCampaign === "featured" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Kampanyayı Kaydet
            </button>
          </div>

          {/* Vurgulu İlan (Firman Görünür Olsun) Fiyatları */}
          <div className="bg-white rounded-2xl border border-[#E3CE9E] p-5" data-testid="highlight-prices-card">
            <h4 className="font-medium text-[#2A2224] mb-1 flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#C6A15B]" /> Firman Görünür Olsun</h4>
            <p className="text-sm text-[#7C6E70] mb-4">Firmanın kategori sayfasında kalın yazı + altın çerçeveyle öne çıkması için ödediği paket fiyatları (₺). Ödeme yapılınca otomatik aktif olur.</p>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {[["1-ay", "1 Aylık"], ["3-ay", "3 Aylık"], ["6-ay", "6 Aylık"], ["12-ay", "1 Yıllık"]].map(([key, label]) => (
                <div key={key}>
                  <label className="text-xs text-[#7C6E70] mb-1 block">{label} (₺)</label>
                  <input type="number" value={hlPrices[key]} onChange={(e) => setHlPrices({ ...hlPrices, [key]: e.target.value })} data-testid={`highlight-price-input-${key}`} placeholder="0" className="w-full h-11 px-3 rounded-xl bg-[#FBF5F0] outline-none text-[#2A2224]" />
                </div>
              ))}
            </div>
            <button onClick={saveHlPrices} disabled={savingHl} data-testid="save-highlight-prices" className="mt-4 flex items-center gap-2 px-5 h-11 rounded-xl bg-gradient-to-r from-[#C6A15B] to-[#a9843f] text-white text-sm font-medium hover:shadow-md transition-all disabled:opacity-70">
              {savingHl ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Fiyatları Kaydet
            </button>
          </div>

          {/* Kampanya — Firman Görünür Olsun */}
          <div className="bg-white rounded-2xl border border-[#d7e0f7] p-5" data-testid="campaign-highlight">
            <div className="flex items-center justify-between gap-3 mb-1">
              <h4 className="font-medium text-[#2A2224] flex items-center gap-2"><Tag className="w-4 h-4 text-[#3b5bdb]" /> Firman Görünür Olsun — Kampanya</h4>
              <label className="flex items-center gap-2 text-sm text-[#5b4d4f]">
                <input type="checkbox" checked={campaigns.highlight.enabled} onChange={(e) => setCampaigns((p) => ({ ...p, highlight: { ...p.highlight, enabled: e.target.checked } }))} data-testid="campaign-highlight-enabled" /> Kampanya aktif
              </label>
            </div>
            <p className="text-sm text-[#7C6E70] mb-4">Belirlediğiniz tarih aralığında bu pakete indirim uygulanır. Firma panelinde eski fiyat çizili, indirimli kampanya fiyatı gösterilir.</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-[#7C6E70] mb-1 block">Başlangıç Tarihi</label>
                <input type="date" value={campaigns.highlight.start} onChange={(e) => setCampaigns((p) => ({ ...p, highlight: { ...p.highlight, start: e.target.value } }))} data-testid="campaign-highlight-start" className="w-full h-11 px-3 rounded-xl bg-[#FBF5F0] outline-none text-[#2A2224]" />
              </div>
              <div>
                <label className="text-xs text-[#7C6E70] mb-1 block">Bitiş Tarihi</label>
                <input type="date" value={campaigns.highlight.end} onChange={(e) => setCampaigns((p) => ({ ...p, highlight: { ...p.highlight, end: e.target.value } }))} data-testid="campaign-highlight-end" className="w-full h-11 px-3 rounded-xl bg-[#FBF5F0] outline-none text-[#2A2224]" />
              </div>
              <div>
                <label className="text-xs text-[#7C6E70] mb-1 block">İndirim (%)</label>
                <input type="number" min="0" max="100" value={campaigns.highlight.percent} onChange={(e) => setCampaigns((p) => ({ ...p, highlight: { ...p.highlight, percent: e.target.value } }))} data-testid="campaign-highlight-percent" placeholder="örn. 25" className="w-full h-11 px-3 rounded-xl bg-[#FBF5F0] outline-none text-[#2A2224]" />
              </div>
            </div>
            <button onClick={() => saveCampaign("highlight")} disabled={savingCampaign === "highlight"} data-testid="campaign-highlight-save" className="mt-4 flex items-center gap-2 px-5 h-11 rounded-xl bg-gradient-to-r from-[#5b7be0] to-[#3b5bdb] text-white text-sm font-medium hover:shadow-md transition-all disabled:opacity-70">
              {savingCampaign === "highlight" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Kampanyayı Kaydet
            </button>
          </div>

          {/* Bekleyen talepler */}
          <div className="bg-white rounded-2xl border border-[#E3CE9E] p-5">
            <h4 className="font-medium text-[#2A2224] mb-3 flex items-center gap-2">
              Bekleyen Talepler
              <span className="px-2 py-0.5 rounded-full bg-[#F6EAD0] text-[#8a6d1f] text-xs font-semibold">{requests.length}</span>
            </h4>
            {requests.length === 0 ? (
              <p className="text-sm text-[#7C6E70]">Şu an bekleyen reklam talebi yok.</p>
            ) : (
              <div className="space-y-3">
                {requests.map((f) => (
                  <div key={f.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 rounded-xl bg-[#FBF3E4] border border-[#efdcb8]">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-lg bg-cover bg-center bg-[#eee] shrink-0" style={{ backgroundImage: f.cover ? `url(${f.cover})` : "none" }} />
                      <div className="min-w-0">
                        <p className="font-medium text-[#2A2224] truncate">{f.name}</p>
                        <p className="text-xs text-[#7C6E70]">{f.city}{f.district ? ` · ${f.district}` : ""} · Tel: {f.phone}</p>
                      </div>
                      <span className="px-2.5 py-1 rounded-full bg-white border border-[#e3ce9e] text-[#8a6d1f] text-xs font-medium shrink-0">{f.featurePlan || "1-ay"}</span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button onClick={() => approveFeature(f)} className="flex items-center gap-1.5 px-4 h-9 rounded-lg bg-gradient-to-r from-[#C6A15B] to-[#a9843f] text-white text-sm font-medium hover:shadow-md transition-all">
                        <CheckCircle2 className="w-4 h-4" /> Onayla & Öne Çıkar
                      </button>
                      <button onClick={() => rejectRequest(f)} className="px-4 h-9 rounded-lg border border-[#d8b0b5] text-[#9E2A3B] text-sm font-medium hover:bg-[#F6E4E6] transition-colors">Reddet</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Öne çıkan işletmeler */}
          <div className="bg-white rounded-2xl border border-[#F0DDE0] p-5">
            <h4 className="font-medium text-[#2A2224] mb-3 flex items-center gap-2">
              <Crown className="w-4 h-4 text-[#C6A15B]" /> Öne Çıkan İşletmeler
              <span className="px-2 py-0.5 rounded-full bg-[#F6E4E6] text-[#9E2A3B] text-xs font-semibold">{featuredFirms.length}</span>
            </h4>
            {featuredFirms.length === 0 ? (
              <p className="text-sm text-[#7C6E70]">Henüz öne çıkan işletme yok.</p>
            ) : (
              <div className="space-y-3">
                {featuredFirms.map((f) => (
                  <div key={f.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 rounded-xl bg-[#FBF5F0] border border-[#F0DDE0]">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-lg bg-cover bg-center bg-[#eee] shrink-0" style={{ backgroundImage: f.cover ? `url(${f.cover})` : "none" }} />
                      <div className="min-w-0">
                        <p className="font-medium text-[#2A2224] truncate">{f.name}</p>
                        <p className="text-xs text-[#7C6E70]">
                          {f.city}{f.district ? ` · ${f.district}` : ""} · {f.featuredUntil ? `Bitiş: ${new Date(f.featuredUntil).toLocaleDateString("tr-TR")}` : "Süresiz"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Select onValueChange={(v) => setFeaturedDays(f, v === "sinirsiz" ? null : Number(v))}>
                        <SelectTrigger className="bg-white border-[#F0DDE0] h-9 w-[140px] text-sm"><SelectValue placeholder="Süre uzat" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="30">30 gün</SelectItem>
                          <SelectItem value="90">90 gün</SelectItem>
                          <SelectItem value="180">180 gün</SelectItem>
                          <SelectItem value="365">1 yıl</SelectItem>
                          <SelectItem value="sinirsiz">Süresiz</SelectItem>
                        </SelectContent>
                      </Select>
                      <button onClick={() => toggleFeatured(f)} className="px-4 h-9 rounded-lg border border-[#d8b0b5] text-[#9E2A3B] text-sm font-medium hover:bg-[#F6E4E6] transition-colors">Kaldır</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        )}

        {section === "uye-banner" && (
        <div className="space-y-6" data-testid="admin-uye-banner">
          <div>
            <h2 className="font-display text-2xl text-[#2A2224]">Üye Paneli Reklamları</h2>
            <p className="text-sm text-[#7C6E70] mt-1">Buraya eklediğiniz reklam banner'ları üyelerin kendi panellerinde (/uyelik) gösterilir. Görsele tıklanınca verdiğiniz bağlantıya gider.</p>
          </div>

          {/* Karışma Hızı */}
          <div className="bg-white rounded-2xl border border-[#F0DDE0] p-5" data-testid="banner-shuffle-settings">
            <h3 className="font-medium text-[#2A2224] mb-1 flex items-center gap-2"><Shuffle className="w-5 h-5 text-[#9E2A3B]" /> Karışma Hızı</h3>
            <p className="text-sm text-[#7C6E70] mb-4">Üye panelinde <strong>3'ten fazla</strong> reklam olduğunda görünen reklamlar kaç saniyede bir karışsın?</p>
            <div className="flex flex-wrap items-center gap-2">
              {[5, 10, 15, 20, 30].map((s) => (
                <button key={s} onClick={() => saveShuffleSecs(s)} disabled={savingShuffle}
                  data-testid={`banner-shuffle-${s}`}
                  className={`px-4 h-11 rounded-xl text-sm font-medium border transition-all disabled:opacity-60 ${shuffleSecs === s ? "bg-[#9E2A3B] text-white border-[#9E2A3B] shadow-sm" : "bg-white text-[#5b4d4f] border-[#F0DDE0] hover:border-[#E7A9B0]"}`}>
                  {s} sn
                </button>
              ))}
              {savingShuffle && <Loader2 className="w-4 h-4 animate-spin text-[#9E2A3B]" />}
              <span className="text-xs text-[#9c8f90] ml-1" data-testid="banner-shuffle-current">Şu an: {shuffleSecs} saniye</span>
            </div>
          </div>

          {/* Performans grafiği */}
          {banners.length > 0 && (() => {
            const totalViews = banners.reduce((a, b) => a + (b.views || 0), 0);
            const totalClicks = banners.reduce((a, b) => a + (b.clicks || 0), 0);
            const overallCtr = totalViews ? ((totalClicks / totalViews) * 100).toFixed(1) : "0.0";
            const chartData = [...banners]
              .sort((a, b) => (b.views || 0) - (a.views || 0))
              .slice(0, 10)
              .map((b) => ({
                name: (b.title || "Reklam").length > 14 ? (b.title || "Reklam").slice(0, 14) + "…" : (b.title || "Reklam"),
                "Görüntülenme": b.views || 0,
                "Tıklama": b.clicks || 0,
              }));
            return (
              <div className="bg-white rounded-2xl border border-[#F0DDE0] p-5" data-testid="banner-stats-chart">
                <h3 className="font-medium text-[#2A2224] mb-1 flex items-center gap-2"><MousePointerClick className="w-5 h-5 text-[#9E2A3B]" /> Reklam Performansı</h3>
                <p className="text-sm text-[#7C6E70] mb-4">Üye panelindeki reklamların görüntülenme ve tıklama sayıları (en çok görüntülenen 10 reklam).</p>
                <div className="grid grid-cols-3 gap-px bg-[#F0DDE0] rounded-xl overflow-hidden mb-5" data-testid="banner-stats-totals">
                  {[
                    { label: "Toplam Görüntülenme", val: totalViews.toLocaleString("tr-TR"), color: "#3b5bdb" },
                    { label: "Toplam Tıklama", val: totalClicks.toLocaleString("tr-TR"), color: "#2E8B57" },
                    { label: "Ortalama TO", val: `%${overallCtr}`, color: "#9E2A3B" },
                  ].map((s) => (
                    <div key={s.label} className="bg-white px-3 py-4 text-center">
                      <p className="text-xs text-[#7C6E70]">{s.label}</p>
                      <p className="text-xl font-semibold mt-0.5" style={{ color: s.color }}>{s.val}</p>
                    </div>
                  ))}
                </div>
                <div style={{ width: "100%", height: 320 }}>
                  <ResponsiveContainer>
                    <BarChart data={chartData} margin={{ top: 8, right: 8, left: -12, bottom: 48 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#F0DDE0" vertical={false} />
                      <XAxis dataKey="name" angle={-30} textAnchor="end" interval={0} tick={{ fontSize: 11, fill: "#7C6E70" }} height={60} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#7C6E70" }} />
                      <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #F0DDE0", fontSize: 12 }} cursor={{ fill: "#FBF0F2" }} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Bar dataKey="Görüntülenme" fill="#3b5bdb" radius={[4, 4, 0, 0]} maxBarSize={38} />
                      <Bar dataKey="Tıklama" fill="#2E8B57" radius={[4, 4, 0, 0]} maxBarSize={38} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            );
          })()}

          {/* Yeni banner ekle */}
          <div className="bg-white rounded-2xl border border-[#F0DDE0] p-5">
            <h3 className="font-medium text-[#2A2224] mb-4 flex items-center gap-2"><ImagePlus className="w-5 h-5 text-[#9E2A3B]" /> Yeni Reklam Ekle</h3>
            <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-5">
              <div>
                <input ref={bannerFileRef} type="file" accept="image/*" className="hidden" onChange={uploadBannerImage} data-testid="banner-file-input" />
                <button onClick={() => bannerFileRef.current?.click()} disabled={bannerUploading} data-testid="banner-upload-btn"
                  className="w-full aspect-[16/6] rounded-xl border-2 border-dashed border-[#E7A9B0] text-[#9E2A3B] hover:bg-[#FBF0F2] transition-colors flex flex-col items-center justify-center gap-1 overflow-hidden">
                  {bannerForm.image ? (
                    <img src={bannerForm.image.startsWith("http") ? bannerForm.image : `${process.env.REACT_APP_BACKEND_URL}${bannerForm.image}`} alt="önizleme" className="w-full h-full object-cover" />
                  ) : bannerUploading ? (
                    <Loader2 className="w-6 h-6 animate-spin" />
                  ) : (
                    <><ImagePlus className="w-6 h-6" /><span className="text-xs">Banner görseli yükle</span></>
                  )}
                </button>
                <p className="text-[11px] text-[#9c8f90] mt-1.5">Önerilen: geniş (yatay) görsel, max 6MB.</p>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="text-sm text-[#5b4d4f] mb-1 block">Başlık (opsiyonel)</label>
                  <Input value={bannerForm.title} onChange={(e) => setBannerForm((p) => ({ ...p, title: e.target.value }))} data-testid="banner-title-input" placeholder="Örn. Yaz Kampanyası" className="border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
                </div>
                <div>
                  <label className="text-sm text-[#5b4d4f] mb-1 block">Bağlantı (tıklanınca gidilecek adres)</label>
                  <Input value={bannerForm.link} onChange={(e) => setBannerForm((p) => ({ ...p, link: e.target.value }))} data-testid="banner-link-input" placeholder="https://..." className="border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
                </div>
                <div>
                  <label className="text-sm text-[#5b4d4f] mb-1 block">Hedef İl</label>
                  <select value={bannerForm.city} onChange={(e) => setBannerForm((p) => ({ ...p, city: e.target.value }))} data-testid="banner-city-select"
                    className="w-full h-11 px-3 rounded-xl bg-[#FBF5F0] border border-[#F0DDE0] outline-none text-[#2A2224] text-sm focus:ring-2 focus:ring-[#C15B6B]">
                    <option value="">Tüm İller (herkese göster)</option>
                    {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <p className="text-[11px] text-[#9c8f90] mt-1">Reklam, seçtiğiniz ildeki üyelere gösterilir. "Tüm İller" seçilirse herkese gösterilir.</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm text-[#5b4d4f] mb-1 block">Başlangıç Tarihi</label>
                    <input type="date" value={bannerForm.start} onChange={(e) => setBannerForm((p) => ({ ...p, start: e.target.value }))} data-testid="banner-start-input"
                      className="w-full h-11 px-3 rounded-xl bg-[#FBF5F0] border border-[#F0DDE0] outline-none text-[#2A2224] text-sm focus:ring-2 focus:ring-[#C15B6B]" />
                  </div>
                  <div>
                    <label className="text-sm text-[#5b4d4f] mb-1 block">Bitiş Tarihi</label>
                    <input type="date" value={bannerForm.end} onChange={(e) => setBannerForm((p) => ({ ...p, end: e.target.value }))} data-testid="banner-end-input"
                      className="w-full h-11 px-3 rounded-xl bg-[#FBF5F0] border border-[#F0DDE0] outline-none text-[#2A2224] text-sm focus:ring-2 focus:ring-[#C15B6B]" />
                  </div>
                  <p className="col-span-2 text-[11px] text-[#9c8f90] -mt-1">Boş bırakılırsa süre sınırı olmaz. Reklam yalnızca bu tarih aralığında üye panelinde gösterilir.</p>
                </div>
                <label className="flex items-center gap-2 text-sm text-[#5b4d4f]">
                  <input type="checkbox" checked={bannerForm.active} onChange={(e) => setBannerForm((p) => ({ ...p, active: e.target.checked }))} data-testid="banner-active-input" /> Yayında (üye panelinde görünsün)
                </label>
                <button onClick={createBanner} disabled={bannerSaving || !bannerForm.image} data-testid="banner-create-btn"
                  className="flex items-center gap-2 px-5 h-11 rounded-xl bg-gradient-to-r from-[#C15B6B] to-[#9E2A3B] text-white text-sm font-medium hover:shadow-md transition-all disabled:opacity-60">
                  {bannerSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Reklamı Ekle
                </button>
              </div>
            </div>
          </div>

          {/* Mevcut bannerlar */}
          <div className="space-y-3" data-testid="banner-list">
            {banners.length === 0 ? (
              <div className="bg-white rounded-2xl border border-[#F0DDE0] p-8 text-center text-[#7C6E70]">Henüz reklam eklenmedi.</div>
            ) : banners.map((b) => (
              <div key={b.id} data-testid={`banner-row-${b.id}`} className="bg-white rounded-2xl border border-[#F0DDE0] p-3 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="w-full sm:w-48 aspect-[16/6] rounded-lg overflow-hidden bg-[#F6E4E6] shrink-0">
                  <img src={b.image.startsWith("http") ? b.image : `${process.env.REACT_APP_BACKEND_URL}${b.image}`} alt={b.title || "banner"} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-[#2A2224]">{b.title || <span className="text-[#a8999b]">Başlıksız</span>}</p>
                  {b.link ? <a href={b.link} target="_blank" rel="noreferrer" className="text-xs text-[#9E2A3B] hover:underline break-all">{b.link}</a> : <span className="text-xs text-[#a8999b]">Bağlantı yok</span>}
                  <div className="mt-1.5 flex items-center gap-3 flex-wrap">
                    <span className={`inline-block text-[11px] px-2 py-0.5 rounded-full ${b.active ? "bg-[#E7F6EC] text-[#237045]" : "bg-[#F3EDEE] text-[#7C6E70]"}`}>{b.active ? "Yayında" : "Pasif"}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-[#EEF2FB] text-[#3b5bdb]" data-testid={`banner-city-${b.id}`}>{b.city ? b.city : "Tüm İller"}</span>
                    {(() => {
                      const today = new Date().toISOString().slice(0, 10);
                      const fmt = (d) => d ? new Date(d).toLocaleDateString("tr-TR", { day: "numeric", month: "short", year: "numeric" }) : null;
                      if (!b.start && !b.end) return <span className="text-xs text-[#9c8f90]" data-testid={`banner-schedule-${b.id}`}>Süresiz</span>;
                      let state = null;
                      if (b.start && today < b.start) state = <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#FBF3E3] text-[#9a7b2e]">Başlamadı</span>;
                      else if (b.end && today > b.end) state = <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#FBEEF0] text-[#9E2A3B]">Süresi doldu</span>;
                      return (
                        <span className="text-xs text-[#7C6E70] flex items-center gap-1" data-testid={`banner-schedule-${b.id}`}>
                          {fmt(b.start) || "…"} – {fmt(b.end) || "…"} {state}
                        </span>
                      );
                    })()}
                    <span className="text-xs text-[#7C6E70] flex items-center gap-1" data-testid={`banner-views-${b.id}`}><Eye className="w-3.5 h-3.5 text-[#3b5bdb]" /> {b.views || 0} görüntülenme</span>
                    <span className="text-xs text-[#7C6E70] flex items-center gap-1" data-testid={`banner-clicks-${b.id}`}><MousePointerClick className="w-3.5 h-3.5 text-[#2E8B57]" /> {b.clicks || 0} tıklama</span>
                    <span className="text-xs text-[#9c8f90]">TO %{b.views ? Math.round(((b.clicks || 0) / b.views) * 100) : 0}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => toggleBanner(b)} data-testid={`banner-toggle-${b.id}`} className="px-3 h-9 rounded-lg border border-[#F0DDE0] text-sm text-[#5b4d4f] hover:bg-[#FBF5F0]">{b.active ? "Pasifleştir" : "Yayınla"}</button>
                  <button onClick={() => deleteBanner(b)} data-testid={`banner-delete-${b.id}`} className="w-9 h-9 rounded-lg border border-[#E7A9B0] text-[#9E2A3B] flex items-center justify-center hover:bg-[#FBF0F2]"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
        )}


        {section === "reklam-talepleri" && (
        <div className="space-y-4" data-testid="admin-reklam-talepleri">
          <div>
            <div className="flex items-center gap-2">
              <Megaphone className="w-5 h-5 text-[#C6A15B]" />
              <h3 className="font-display text-xl text-[#2A2224]">Reklam Talepleri</h3>
            </div>
            <p className="text-sm text-[#7C6E70] mt-1">İşletmelerin gönderdiği reklam talepleri (Google ve Üye Paneli). İletişime geçtikten sonra "Tamamlandı" olarak işaretleyebilirsiniz. Üye Paneli talepleri için reklam banner'ını <strong>Reklam Yönetimi &gt; Üye Paneli Reklamları</strong>'ndan ekleyin.</p>
          </div>

          <div className="flex flex-wrap gap-1.5" data-testid="ad-req-filter">
            {[
              { k: "pending", label: "Bekleyen" },
              { k: "done", label: "Tamamlanan" },
              { k: "rejected", label: "Reddedilen" },
              { k: "all", label: "Tümü" },
            ].map((f) => (
              <button
                key={f.k}
                onClick={() => setAdReqFilter(f.k)}
                data-testid={`ad-req-filter-${f.k}`}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${adReqFilter === f.k ? "bg-[#9E2A3B] text-white shadow-sm" : "bg-white text-[#5b4d4f] border border-[#F0DDE0] hover:border-[#E7A9B0]"}`}
              >
                {f.label}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-1.5" data-testid="ad-req-kind-filter">
            <span className="text-xs text-[#9c8f90] mr-1">Tür:</span>
            {[
              { k: "all", label: "Tümü" },
              { k: "google", label: "Google" },
              { k: "instagram", label: "Instagram" },
              { k: "member", label: "Üye Paneli" },
            ].map((f) => {
              const cnt = f.k === "all" ? adRequests.length : adRequests.filter((r) => adReqKindOf(r) === f.k).length;
              return (
                <button
                  key={f.k}
                  onClick={() => setAdReqKind(f.k)}
                  data-testid={`ad-req-kind-${f.k}`}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-medium transition-all ${adReqKind === f.k ? "bg-[#2A2224] text-white" : "bg-white text-[#5b4d4f] border border-[#F0DDE0] hover:border-[#9E2A3B]"}`}
                >
                  {f.label} ({cnt})
                </button>
              );
            })}
          </div>

          {filteredAdReqs.length === 0 ? (
            <div className="py-16 text-center text-[#7C6E70] bg-white rounded-2xl border border-[#F0DDE0]" data-testid="ad-requests-empty">
              <Megaphone className="w-9 h-9 mx-auto mb-2 text-[#E7A9B0]" /> Bu filtrede reklam talebi yok.
            </div>
          ) : (
            <div className="space-y-3">
              {filteredAdReqs.map((r) => {
                const st = r.status || "pending";
                const badge = st === "done"
                  ? { c: "bg-[#E7F6EC] text-[#237045] border-[#B9E3C6]", t: "İletişime geçildi / Tamamlandı" }
                  : st === "rejected"
                  ? { c: "bg-[#FBEEF0] text-[#9E2A3B] border-[#E7A9B0]", t: "Reddedildi" }
                  : { c: "bg-[#FBF3E4] text-[#8a6d1f] border-[#E3CE9E]", t: "Bekliyor" };
                return (
                  <div key={r.id} data-testid={`ad-request-row-${r.id}`} className="bg-white rounded-2xl border border-[#F0DDE0] p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium text-[#2A2224]">
                          <span className="text-[#9E2A3B]">{r.firm_name || "Firma"}</span>
                          {r.firm_phone && <span className="text-[#a8999b]"> · {r.firm_phone}</span>}
                        </p>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${badge.c}`}>{badge.t}</span>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${r.kind === "member" ? "bg-[#FBEEF0] text-[#9E2A3B] border-[#E7A9B0]" : r.kind === "instagram" ? "bg-[#FBEAF4] text-[#B5278E] border-[#F0C5E1]" : "bg-[#EEF2FB] text-[#3b5bdb] border-[#c9d6f5]"}`} data-testid={`ad-request-kind-${r.id}`}>{r.kind === "member" ? "Üye Paneli Reklamı" : r.kind === "instagram" ? "Instagram Reklamı" : "Google Reklamı"}</span>
                      </div>
                      <p className="text-sm text-[#5b4d4f] mt-0.5">
                        {r.budget ? <span className="font-medium">Aylık bütçe: {r.budget} ₺</span> : <span className="text-[#a8999b]">Bütçe belirtilmedi</span>}
                        {r.firm_city ? ` · ${r.firm_city}` : ""}
                      </p>
                      {r.notes && <p className="text-sm text-[#7C6E70] mt-1">{r.notes}</p>}
                      <div className="flex items-center gap-3 mt-1">
                        {r.firm_slug && <a href={`/mekan/${r.firm_slug}`} target="_blank" rel="noreferrer" className="text-xs text-[#9E2A3B] hover:underline inline-flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> İlanı Gör</a>}
                        <span className="text-xs text-[#a8999b]">{r.createdAt ? new Date(r.createdAt).toLocaleString("tr-TR") : ""}</span>
                      </div>
                    </div>
                    {st === "pending" && (
                    <div className="flex items-center gap-2 shrink-0">
                      <button onClick={() => markAdRequest(r, "done")} data-testid={`ad-request-done-${r.id}`}
                        className="flex items-center gap-1.5 px-4 h-10 rounded-xl bg-gradient-to-r from-[#2E8B57] to-[#237045] text-white text-sm font-medium hover:shadow-md transition-all">
                        <CheckCircle2 className="w-4 h-4" /> İletişime Geçildi / Tamamlandı
                      </button>
                      <button onClick={() => markAdRequest(r, "rejected")} data-testid={`ad-request-reject-${r.id}`}
                        className="flex items-center gap-1.5 px-4 h-10 rounded-xl border border-[#E7A9B0] text-[#9E2A3B] text-sm font-medium hover:bg-[#FBF0F2] transition-colors">
                        <X className="w-4 h-4" /> Reddet
                      </button>
                    </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        )}

        {section === "indirim-kodlari" && (
        <div className="space-y-6" data-testid="admin-indirim-kodlari">
          <div>
            <div className="flex items-center gap-2">
              <Tag className="w-5 h-5 text-[#9E2A3B]" />
              <h3 className="font-display text-xl text-[#2A2224]">İndirim Kodları</h3>
            </div>
            <p className="text-sm text-[#7C6E70] mt-1">Her kodu istediğiniz bölüme özel tanımlayabilirsiniz (Premium, Ek Kategori, Ana Sayfada Öne Çık, Firman Görünür Olsun). Bölüm seçmezseniz kod tüm ödemelerde geçerli olur.</p>
          </div>

          <div className="bg-white rounded-2xl border border-[#F0DDE0] p-5">
            <h4 className="font-medium text-[#2A2224] mb-4">Yeni İndirim Kodu</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              <div>
                <label className="text-xs text-[#7C6E70] mb-1 block">Kod</label>
                <input value={dcForm.code} onChange={(e) => setDcForm({ ...dcForm, code: e.target.value.toUpperCase() })} data-testid="dc-code" placeholder="ör. HOSGELDIN" className="w-full h-11 px-3 rounded-xl bg-[#FBF5F0] outline-none text-[#2A2224] uppercase" />
              </div>
              <div>
                <label className="text-xs text-[#7C6E70] mb-1 block">Tip</label>
                <select value={dcForm.type} onChange={(e) => setDcForm({ ...dcForm, type: e.target.value })} data-testid="dc-type" className="w-full h-11 px-3 rounded-xl bg-[#FBF5F0] outline-none text-[#2A2224]">
                  <option value="percent">Yüzde (%)</option>
                  <option value="amount">Tutar (₺)</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-[#7C6E70] mb-1 block">Değer</label>
                <input type="number" value={dcForm.value} onChange={(e) => setDcForm({ ...dcForm, value: e.target.value })} data-testid="dc-value" placeholder={dcForm.type === "percent" ? "20" : "100"} className="w-full h-11 px-3 rounded-xl bg-[#FBF5F0] outline-none text-[#2A2224]" />
              </div>
              <div>
                <label className="text-xs text-[#7C6E70] mb-1 block">Kullanım Limiti <span className="text-[#c0b3b5]">(ops.)</span></label>
                <input type="number" value={dcForm.maxUses} onChange={(e) => setDcForm({ ...dcForm, maxUses: e.target.value })} data-testid="dc-maxuses" placeholder="sınırsız" className="w-full h-11 px-3 rounded-xl bg-[#FBF5F0] outline-none text-[#2A2224]" />
              </div>
              <div>
                <label className="text-xs text-[#7C6E70] mb-1 block">Son Kullanma <span className="text-[#c0b3b5]">(ops.)</span></label>
                <input type="date" value={dcForm.expiresAt} onChange={(e) => setDcForm({ ...dcForm, expiresAt: e.target.value })} data-testid="dc-expires" className="w-full h-11 px-3 rounded-xl bg-[#FBF5F0] outline-none text-[#2A2224]" />
              </div>
            </div>
            <div className="mt-4">
              <label className="text-xs text-[#7C6E70] mb-2 block">Geçerli Olduğu Bölümler <span className="text-[#c0b3b5]">(boş bırakılırsa tüm ödemelerde geçerli)</span></label>
              <div className="flex flex-wrap gap-2" data-testid="dc-applies">
                {[["membership", "Premium"], ["category", "Ek Kategori"], ["featured", "Ana Sayfada Öne Çık"], ["highlight", "Firman Görünür Olsun"]].map(([key, label]) => {
                  const on = dcForm.appliesTo.includes(key);
                  return (
                    <button key={key} type="button" data-testid={`dc-applies-${key}`}
                      onClick={() => setDcForm({ ...dcForm, appliesTo: on ? dcForm.appliesTo.filter((x) => x !== key) : [...dcForm.appliesTo, key] })}
                      className={`px-3.5 py-2 rounded-xl text-sm font-medium border transition-all ${on ? "bg-[#9E2A3B] text-white border-[#9E2A3B]" : "bg-white text-[#5b4d4f] border-[#F0DDE0] hover:border-[#E7A9B0]"}`}>
                      {on ? "✓ " : ""}{label}
                    </button>
                  );
                })}
              </div>
            </div>
            <button onClick={createDiscount} disabled={dcBusy} data-testid="dc-create-btn" className="mt-4 flex items-center gap-2 px-5 h-11 rounded-xl bg-gradient-to-r from-[#C15B6B] to-[#9E2A3B] text-white text-sm font-medium hover:shadow-md transition-all disabled:opacity-70">
              {dcBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Tag className="w-4 h-4" />} Kod Oluştur
            </button>
          </div>

          <div className="bg-white rounded-2xl border border-[#F0DDE0] overflow-hidden">
            <div className="px-5 py-3 border-b border-[#F0DDE0]"><h4 className="font-medium text-[#2A2224]">Tanımlı Kodlar</h4></div>
            {discountCodes.length === 0 ? (
              <div className="py-14 text-center text-[#7C6E70]"><Tag className="w-9 h-9 mx-auto mb-2 text-[#E7A9B0]" /> Henüz indirim kodu yok.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#FBF0F2] text-[#7C6E70] text-left">
                      <th className="px-4 py-3 font-medium">Kod</th>
                      <th className="px-4 py-3 font-medium">İndirim</th>
                      <th className="px-4 py-3 font-medium hidden lg:table-cell">Bölümler</th>
                      <th className="px-4 py-3 font-medium">Kullanım</th>
                      <th className="px-4 py-3 font-medium hidden md:table-cell">Son Kullanma</th>
                      <th className="px-4 py-3 font-medium">Durum</th>
                      <th className="px-4 py-3 font-medium text-right">İşlem</th>
                    </tr>
                  </thead>
                  <tbody>
                    {discountCodes.map((d) => (
                      <tr key={d.id} className="border-t border-[#F0DDE0] hover:bg-[#FDFAF8]" data-testid={`dc-row-${d.id}`}>
                        <td className="px-4 py-3 font-mono font-semibold text-[#9E2A3B]">{d.code}</td>
                        <td className="px-4 py-3 text-[#2A2224]">{d.type === "percent" ? `%${d.value}` : `${d.value} ₺`}</td>
                        <td className="px-4 py-3 hidden lg:table-cell text-[#7C6E70] text-xs">{(d.appliesTo && d.appliesTo.length) ? d.appliesTo.map((x) => ({ membership: "Premium", category: "Ek Kategori", featured: "Öne Çık", highlight: "Görünür Olsun" }[x] || x)).join(", ") : "Tümü"}</td>
                        <td className="px-4 py-3 text-[#5b4d4f]">{d.usedCount || 0}{d.maxUses ? ` / ${d.maxUses}` : ""}</td>
                        <td className="px-4 py-3 hidden md:table-cell text-[#7C6E70]">{d.expiresAt ? new Date(d.expiresAt).toLocaleDateString("tr-TR") : "—"}</td>
                        <td className="px-4 py-3">
                          <button onClick={() => toggleDiscount(d)} data-testid={`dc-toggle-${d.id}`} className={`px-2.5 py-1 rounded-full text-xs font-medium ${d.active ? "bg-[#E7F6EC] text-[#237045]" : "bg-[#F0E6E8] text-[#8a6d70]"}`}>
                            {d.active ? "Aktif" : "Pasif"}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button onClick={() => deleteDiscount(d.id)} data-testid={`dc-delete-${d.id}`} title="Sil" className="w-8 h-8 rounded-lg bg-[#FBEEF0] text-[#c0392b] inline-flex items-center justify-center hover:bg-[#f6dde1]"><Trash2 className="w-4 h-4" /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="bg-white rounded-2xl border border-[#F0DDE0] overflow-hidden" data-testid="dc-report">
            <div className="px-5 py-3 border-b border-[#F0DDE0] flex items-center gap-2">
              <MousePointerClick className="w-4.5 h-4.5 text-[#9E2A3B]" />
              <h4 className="font-medium text-[#2A2224]">Kullanım Raporu</h4>
            </div>

            {dcReport && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#F0DDE0]" data-testid="dc-report-totals">
                {[
                  { label: "Kullanılan Kod", val: `${dcReport.totals.usedCodes} / ${dcReport.totals.codes}` },
                  { label: "Toplam Kullanım", val: dcReport.totals.uses },
                  { label: "Toplam İndirim", val: `${dcReport.totals.totalDiscount.toLocaleString("tr-TR")} ₺` },
                  { label: "Tahsil Edilen", val: `${dcReport.totals.totalCollected.toLocaleString("tr-TR")} ₺` },
                ].map((s) => (
                  <div key={s.label} className="bg-white px-4 py-4 text-center">
                    <p className="text-xs text-[#7C6E70]">{s.label}</p>
                    <p className="text-lg font-semibold text-[#9E2A3B] mt-0.5">{s.val}</p>
                  </div>
                ))}
              </div>
            )}

            {!dcReport || dcReport.codes.length === 0 ? (
              <div className="py-14 text-center text-[#7C6E70]"><MousePointerClick className="w-9 h-9 mx-auto mb-2 text-[#E7A9B0]" /> Henüz kullanım verisi yok.</div>
            ) : (
              <div className="divide-y divide-[#F0DDE0]">
                {dcReport.codes.map((c) => {
                  const typeLabel = { membership: "Premium", category: "Ek Kategori", featured: "Öne Çık", highlight: "Görünür Olsun" };
                  const open = !!dcReportOpen[c.id];
                  return (
                    <div key={c.id} data-testid={`dc-report-row-${c.id}`}>
                      <button
                        onClick={() => setDcReportOpen((p) => ({ ...p, [c.id]: !p[c.id] }))}
                        disabled={c.paidUses === 0}
                        data-testid={`dc-report-toggle-${c.id}`}
                        className={`w-full flex items-center gap-3 px-5 py-3.5 text-left transition-colors ${c.paidUses > 0 ? "hover:bg-[#FDFAF8] cursor-pointer" : "cursor-default"}`}
                      >
                        <span className="font-mono font-semibold text-[#9E2A3B]">{c.code}</span>
                        <span className="text-xs text-[#7C6E70]">{c.type === "percent" ? `%${c.value}` : `${c.value} ₺`}</span>
                        <span className="ml-auto flex items-center gap-4 sm:gap-6 text-sm">
                          <span className="text-[#5b4d4f]"><span className="font-semibold text-[#2A2224]">{c.paidUses}</span> kullanım</span>
                          <span className="hidden sm:inline text-[#7C6E70]">İndirim: <span className="font-medium text-[#C15B6B]">{c.totalDiscount.toLocaleString("tr-TR")} ₺</span></span>
                          <span className="text-[#7C6E70]">Tahsilat: <span className="font-medium text-[#237045]">{c.totalCollected.toLocaleString("tr-TR")} ₺</span></span>
                          {c.paidUses > 0 && <ChevronDown className={`w-4 h-4 text-[#9E2A3B] transition-transform ${open ? "rotate-180" : ""}`} />}
                        </span>
                      </button>
                      {open && c.usages.length > 0 && (
                        <div className="px-5 pb-4 overflow-x-auto" data-testid={`dc-report-detail-${c.id}`}>
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-[#7C6E70] text-left text-xs">
                                <th className="py-2 pr-4 font-medium">Firma</th>
                                <th className="py-2 pr-4 font-medium">Ödeme Türü</th>
                                <th className="py-2 pr-4 font-medium text-right">Liste Fiyatı</th>
                                <th className="py-2 pr-4 font-medium text-right">İndirim</th>
                                <th className="py-2 pr-4 font-medium text-right">Ödenen</th>
                                <th className="py-2 font-medium">Tarih</th>
                              </tr>
                            </thead>
                            <tbody>
                              {c.usages.map((u, i) => (
                                <tr key={i} className="border-t border-[#F5E8EA]">
                                  <td className="py-2 pr-4 text-[#2A2224]">{u.firm_name}</td>
                                  <td className="py-2 pr-4 text-[#5b4d4f]">{typeLabel[u.type] || u.type}{u.category ? ` · ${u.category}` : ""}</td>
                                  <td className="py-2 pr-4 text-right text-[#7C6E70]">{u.originalAmount.toLocaleString("tr-TR")} ₺</td>
                                  <td className="py-2 pr-4 text-right text-[#C15B6B]">-{u.discountAmount.toLocaleString("tr-TR")} ₺</td>
                                  <td className="py-2 pr-4 text-right font-medium text-[#237045]">{u.amount.toLocaleString("tr-TR")} ₺</td>
                                  <td className="py-2 text-[#a8999b] text-xs whitespace-nowrap">{u.paidAt ? new Date(u.paidAt).toLocaleString("tr-TR") : "—"}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        )}

        {section === "icerik" && (
        <div className="space-y-6" data-testid="admin-icerik">
          <div>
            <div className="flex items-center gap-2">
              <Newspaper className="w-5 h-5 text-[#9E2A3B]" />
              <h3 className="font-display text-xl text-[#2A2224]">İçerik Yönetimi — Rehber Yazılar</h3>
            </div>
            <p className="text-sm text-[#7C6E70] mt-1">Ana sayfadaki "Rehber Yazılar" bölümünde gösterilecek içerikleri buradan ekleyip düzenleyebilirsiniz.</p>
          </div>

          <div className="bg-white rounded-2xl border border-[#F0DDE0] p-5">
            <h4 className="font-medium text-[#2A2224] mb-4">{artForm.id ? "Yazıyı Düzenle" : "Yeni Rehber Yazı"}</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <label className="text-sm text-[#5b4d4f]">Başlık *</label>
                  <Input data-testid="article-title-input" value={artForm.title} onChange={(e) => setArtForm((f) => ({ ...f, title: e.target.value }))} placeholder="Örn. 1 Dakikada Sana Uygun Gelinliği Bul!" className="border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm text-[#5b4d4f]">Kısa Açıklama</label>
                  <Textarea data-testid="article-excerpt-input" value={artForm.excerpt} onChange={(e) => setArtForm((f) => ({ ...f, excerpt: e.target.value }))} rows={2} placeholder="Kart üzerinde görünecek kısa metin (opsiyonel)" className="border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm text-[#5b4d4f]">İçerik (Yazı Metni)</label>
                  <Textarea data-testid="article-content-input" value={artForm.content} onChange={(e) => setArtForm((f) => ({ ...f, content: e.target.value }))} rows={6} placeholder="Yazının tam metnini buraya yazın. Bu metin, otomatik oluşturulan sayfada görünür." className="border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <label className="text-sm text-[#5b4d4f]">Sayfa Bağlantısı (otomatik)</label>
                    <div data-testid="article-autolink" className="h-10 flex items-center px-3 rounded-xl bg-[#FBF5F0] border border-[#F0DDE0] text-sm text-[#7C6E70] truncate">
                      {artForm.slug ? `/rehber/${artForm.slug}` : "Kaydedince otomatik oluşturulur"}
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm text-[#5b4d4f]">Sıra</label>
                    <Input data-testid="article-order-input" type="number" value={artForm.order} onChange={(e) => setArtForm((f) => ({ ...f, order: e.target.value }))} placeholder="0" className="border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
                  </div>
                </div>
                <label className="flex items-center gap-2 text-sm text-[#5b4d4f] cursor-pointer">
                  <input data-testid="article-active-checkbox" type="checkbox" checked={artForm.active} onChange={(e) => setArtForm((f) => ({ ...f, active: e.target.checked }))} className="w-4 h-4 accent-[#9E2A3B]" />
                  Sitede yayınla (aktif)
                </label>
              </div>
              <div className="space-y-1.5">
                <label className="text-sm text-[#5b4d4f]">Görsel</label>
                <div className="rounded-xl border-2 border-dashed border-[#E7A9B0] overflow-hidden">
                  {artForm.image ? (
                    <div className="relative">
                      <img src={artForm.image} alt="önizleme" className="w-full h-44 object-cover" />
                      <button onClick={() => setArtForm((f) => ({ ...f, image: "" }))} className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/55 text-white flex items-center justify-center"><X className="w-4 h-4" /></button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center gap-2 py-10 text-[#9E2A3B] cursor-pointer hover:bg-[#FBF0F2] transition-colors" data-testid="article-image-upload">
                      <ImagePlus className="w-7 h-7" />
                      <span className="text-sm font-medium">Görsel Yükle</span>
                      <span className="text-xs text-[#7C6E70]">JPG / PNG</span>
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => artImageUpload(e.target.files?.[0])} />
                    </label>
                  )}
                </div>
                <Input data-testid="article-image-url-input" value={/^data:/.test(artForm.image) ? "" : artForm.image} onChange={(e) => setArtForm((f) => ({ ...f, image: e.target.value }))} placeholder="veya görsel URL'si yapıştırın" className="border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
              </div>
            </div>
            <div className="flex items-center gap-2 mt-4">
              <button onClick={saveArticle} disabled={artBusy} data-testid="article-save-btn"
                className="flex items-center gap-2 px-5 h-11 rounded-xl bg-gradient-to-r from-[#C15B6B] to-[#9E2A3B] text-white text-sm font-medium hover:shadow-md transition-all disabled:opacity-70">
                {artBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                {artForm.id ? "Değişiklikleri Kaydet" : "Yazıyı Ekle"}
              </button>
              {artForm.id && (
                <button onClick={resetArtForm} data-testid="article-cancel-btn" className="px-4 h-11 rounded-xl border border-[#F0DDE0] text-[#7C6E70] text-sm font-medium hover:bg-[#FBF5F0] transition-colors">İptal</button>
              )}
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-[#F0DDE0] p-5">
            <h4 className="font-medium text-[#2A2224] mb-4">Tanımlı Yazılar</h4>
            {articles.length === 0 ? (
              <div className="py-14 text-center text-[#7C6E70]"><Newspaper className="w-9 h-9 mx-auto mb-2 text-[#E7A9B0]" /> Henüz rehber yazı eklenmedi.</div>
            ) : (
              <div className="space-y-2" data-testid="article-list">
                {articles.map((a) => (
                  <div key={a.id} data-testid={`article-row-${a.id}`} className="flex items-center gap-3 rounded-xl border border-[#F0DDE0] p-3">
                    <div className="w-16 h-16 rounded-lg overflow-hidden bg-[#FBF5F0] shrink-0">
                      {a.image ? <img src={a.image} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-[#E7A9B0]"><Newspaper className="w-5 h-5" /></div>}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-[#2A2224] truncate">{a.title}</p>
                      {a.excerpt && <p className="text-xs text-[#7C6E70] truncate">{a.excerpt}</p>}
                      <span className={`inline-block mt-1 text-[11px] font-medium px-2 py-0.5 rounded-full ${a.active ? "bg-[#E7F6EC] text-[#237045]" : "bg-[#F3EDEE] text-[#7C6E70]"}`}>{a.active ? "Yayında" : "Pasif"} · sıra {a.order}</span>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={() => editArticle(a)} data-testid={`article-edit-${a.id}`} className="w-9 h-9 rounded-lg border border-[#F0DDE0] flex items-center justify-center text-[#3b5bdb] hover:bg-[#FBF0F2] transition-colors"><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => deleteArticle(a.id)} data-testid={`article-delete-${a.id}`} className="w-9 h-9 rounded-lg border border-[#F0DDE0] flex items-center justify-center text-[#9E2A3B] hover:bg-[#FBF0F2] transition-colors"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        )}


        {section === "odeme-al" && (
        <div className="space-y-6" data-testid="admin-odeme-al">
          <div>
            <div className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-[#9E2A3B]" />
              <h3 className="font-display text-xl text-[#2A2224]">Ödeme Al</h3>
            </div>
            <p className="text-sm text-[#7C6E70] mt-1">Firmadan tahsilat için ödeme linki oluşturun. Link ödendiğinde tutar "Ödemeler" listesine kaydedilir.</p>
          </div>

          {/* Yeni ödeme linki */}
          <div className="bg-white rounded-2xl border border-[#F0DDE0] p-5">
            <h4 className="font-medium text-[#2A2224] mb-4 flex items-center gap-2"><Tag className="w-4 h-4 text-[#C15B6B]" /> Yeni Ödeme Linki</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input value={payLinkForm.name} onChange={(e) => setPayLinkForm({ ...payLinkForm, name: e.target.value })} data-testid="paylink-name" placeholder="Firma / kişi adı *" className="h-11 px-3 rounded-xl bg-[#FBF5F0] outline-none text-[#2A2224] placeholder:text-[#a8999b]" />
              <input value={payLinkForm.phone} onChange={(e) => setPayLinkForm({ ...payLinkForm, phone: e.target.value })} data-testid="paylink-phone" placeholder="Telefon (WhatsApp için)" className="h-11 px-3 rounded-xl bg-[#FBF5F0] outline-none text-[#2A2224] placeholder:text-[#a8999b]" />
              <input value={payLinkForm.email} onChange={(e) => setPayLinkForm({ ...payLinkForm, email: e.target.value })} data-testid="paylink-email" placeholder="E-posta (opsiyonel)" className="h-11 px-3 rounded-xl bg-[#FBF5F0] outline-none text-[#2A2224] placeholder:text-[#a8999b]" />
              <input value={payLinkForm.amount} onChange={(e) => setPayLinkForm({ ...payLinkForm, amount: e.target.value.replace(/[^0-9.]/g, "") })} data-testid="paylink-amount" inputMode="decimal" placeholder="Tutar (₺) *" className="h-11 px-3 rounded-xl bg-[#FBF5F0] outline-none text-[#2A2224] placeholder:text-[#a8999b]" />
              <input value={payLinkForm.description} onChange={(e) => setPayLinkForm({ ...payLinkForm, description: e.target.value })} data-testid="paylink-desc" placeholder="Açıklama (ör. Premium üyelik / reklam bedeli) *" className="h-11 px-3 rounded-xl bg-[#FBF5F0] outline-none text-[#2A2224] placeholder:text-[#a8999b] sm:col-span-2" />
            </div>
            <button onClick={createPaymentLink} disabled={creatingPayLink} data-testid="paylink-create-btn" className="mt-4 flex items-center justify-center gap-2 h-11 px-6 rounded-xl bg-gradient-to-r from-[#C15B6B] to-[#9E2A3B] text-white text-sm font-medium hover:shadow-md transition-all disabled:opacity-70">
              {creatingPayLink ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />} Ödeme Linki Oluştur
            </button>

            {lastPayLink && (
              <div className="mt-4 p-4 rounded-xl bg-[#F3FAF5] border border-[#B9E3C6]" data-testid="paylink-result">
                <p className="text-sm text-[#237045] font-medium mb-2">Link hazır! Firmaya gönderin:</p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <input readOnly value={lastPayLink.url} className="flex-1 h-10 px-3 rounded-lg bg-white border border-[#B9E3C6] text-sm text-[#2A2224]" />
                  <button onClick={() => copyPayLink(lastPayLink.url)} data-testid="paylink-copy-btn" className="flex items-center justify-center gap-1.5 h-10 px-4 rounded-lg bg-white border border-[#B9E3C6] text-[#237045] text-sm font-medium hover:bg-[#EAF7EE]">
                    <Tag className="w-4 h-4" /> Kopyala
                  </button>
                  <button onClick={() => waPayLink(lastPayLink.phone, lastPayLink.url, lastPayLink.name, lastPayLink.amount)} data-testid="paylink-wa-btn" className="flex items-center justify-center gap-1.5 h-10 px-4 rounded-lg bg-[#25D366] text-white text-sm font-medium hover:brightness-95">
                    <MessageCircle className="w-4 h-4" /> WhatsApp'ta Aç
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Oluşturulan linkler */}
          <div className="bg-white rounded-2xl border border-[#F0DDE0] overflow-hidden">
            <div className="px-5 py-3 border-b border-[#F0DDE0] flex items-center justify-between">
              <h4 className="font-medium text-[#2A2224]">Oluşturulan Linkler</h4>
              <button onClick={loadPaymentLinks} className="text-[#9E2A3B] hover:text-[#7d2230]" title="Yenile"><RefreshCw className="w-4 h-4" /></button>
            </div>
            {paymentLinks.length === 0 ? (
              <div className="py-14 text-center text-[#7C6E70]"><CreditCard className="w-9 h-9 mx-auto mb-2 text-[#E7A9B0]" /> Henüz ödeme linki oluşturulmadı.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#FBF0F2] text-[#7C6E70] text-left">
                      <th className="px-4 py-3 font-medium">Firma / Kişi</th>
                      <th className="px-4 py-3 font-medium hidden md:table-cell">Açıklama</th>
                      <th className="px-4 py-3 font-medium">Tutar</th>
                      <th className="px-4 py-3 font-medium">Durum</th>
                      <th className="px-4 py-3 font-medium hidden lg:table-cell">Tarih</th>
                      <th className="px-4 py-3 font-medium text-right">İşlem</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paymentLinks.map((p) => (
                      <tr key={p.orderId} className="border-t border-[#F0DDE0] hover:bg-[#FDFAF8]" data-testid={`paylink-row-${p.orderId}`}>
                        <td className="px-4 py-3">
                          <div className="font-medium text-[#2A2224]">{p.name || "—"}</div>
                          {p.phone && <div className="text-xs text-[#a8999b]">{p.phone}</div>}
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell text-[#5b4d4f]">{p.description}</td>
                        <td className="px-4 py-3 font-medium text-[#2A2224]">{p.amount} {p.currency}</td>
                        <td className="px-4 py-3">
                          {p.status === "paid"
                            ? <span className="px-2.5 py-1 rounded-full bg-[#E7F6EC] text-[#237045] text-xs font-medium">Ödendi</span>
                            : p.status === "failed"
                            ? <span className="px-2.5 py-1 rounded-full bg-[#FBEEF0] text-[#9E2A3B] text-xs font-medium">Başarısız</span>
                            : <span className="px-2.5 py-1 rounded-full bg-[#FBF3E4] text-[#8a6d1f] text-xs font-medium">Bekliyor</span>}
                        </td>
                        <td className="px-4 py-3 hidden lg:table-cell text-[#7C6E70]">{p.createdAt ? new Date(p.createdAt).toLocaleDateString("tr-TR") : "-"}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1.5">
                            <button onClick={() => copyPayLink(p.paymentPageUrl)} title="Linki kopyala" className="w-8 h-8 rounded-lg bg-[#FBEEF0] text-[#9E2A3B] flex items-center justify-center hover:bg-[#f6dde1]"><Tag className="w-4 h-4" /></button>
                            <button onClick={() => waPayLink(p.phone, p.paymentPageUrl, p.name, p.amount)} title="WhatsApp'ta aç" className="w-8 h-8 rounded-lg bg-[#E6F9ED] text-[#1e9e4f] flex items-center justify-center hover:bg-[#d3f3df]"><MessageCircle className="w-4 h-4" /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
        )}

        {section === "kullanicilar" && (
        <div className="space-y-6">
          <div>
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-[#9E2A3B]" />
              <h3 className="font-display text-xl text-[#2A2224]">Panel Kullanıcıları</h3>
            </div>
            <p className="text-sm text-[#7C6E70] mt-1">Patron paneline giriş yapabilecek kullanıcıları buradan tanımlayın ve yönetin.</p>
          </div>

          {/* Yeni Kullanıcı Tanımla */}
          <div className="bg-white rounded-2xl border border-[#F0DDE0] p-5">
            <h4 className="font-medium text-[#2A2224] mb-4 flex items-center gap-2"><UserIcon className="w-4 h-4 text-[#C15B6B]" /> Yeni Kullanıcı Tanımla</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              <input value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} placeholder="Ad Soyad" className="h-11 px-3 rounded-xl bg-[#FBF5F0] outline-none text-[#2A2224] placeholder:text-[#a8999b]" />
              <input value={newUser.username} onChange={(e) => setNewUser({ ...newUser, username: e.target.value })} placeholder="Kullanıcı adı" className="h-11 px-3 rounded-xl bg-[#FBF5F0] outline-none text-[#2A2224] placeholder:text-[#a8999b]" />
              <input type="password" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} placeholder="Şifre" className="h-11 px-3 rounded-xl bg-[#FBF5F0] outline-none text-[#2A2224] placeholder:text-[#a8999b]" />
              <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                <SelectTrigger className="bg-[#FBF5F0] border-0 h-11 text-[#2A2224]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="full">Tam Yetki</SelectItem>
                  <SelectItem value="viewer">Sadece Görüntüleme</SelectItem>
                </SelectContent>
              </Select>
              <button onClick={createAdminUser} disabled={creatingUser} className="flex items-center justify-center gap-2 h-11 rounded-xl bg-gradient-to-r from-[#C15B6B] to-[#9E2A3B] text-white text-sm font-medium hover:shadow-md transition-all disabled:opacity-70">
                {creatingUser ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserIcon className="w-4 h-4" />} Ekle
              </button>
            </div>
          </div>

          {/* Kullanıcı listesi */}
          <div className="bg-white rounded-2xl border border-[#F0DDE0] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#FBF0F2] text-[#7C6E70] text-left">
                    <th className="px-4 py-3 font-medium">Kullanıcı</th>
                    <th className="px-4 py-3 font-medium">Kullanıcı Adı</th>
                    <th className="px-4 py-3 font-medium hidden md:table-cell">Rol</th>
                    <th className="px-4 py-3 font-medium hidden lg:table-cell">Oluşturulma</th>
                    <th className="px-4 py-3 font-medium text-right">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-[#F0DDE0] bg-[#FBF5F0]/40">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#C15B6B] to-[#9E2A3B] text-white flex items-center justify-center font-medium shrink-0"><ShieldCheck className="w-4 h-4" /></div>
                        <span className="font-medium text-[#2A2224]">Ana Yönetici</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-[#5b4d4f]">patron</td>
                    <td className="px-4 py-3 hidden md:table-cell"><span className="px-2.5 py-1 rounded-full bg-[#F6E4E6] text-[#9E2A3B] text-xs">Süper Yönetici</span></td>
                    <td className="px-4 py-3 hidden lg:table-cell text-[#7C6E70]">-</td>
                    <td className="px-4 py-3 text-right text-xs text-[#a8999b]">Varsayılan</td>
                  </tr>
                  {adminUsers.map((u) => (
                    <tr key={u.id} className="border-t border-[#F0DDE0] hover:bg-[#FDFAF8]">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-[#EEF2FB] text-[#3b5bdb] flex items-center justify-center font-medium shrink-0">{(u.name || u.username || "?").charAt(0).toUpperCase()}</div>
                          <span className="font-medium text-[#2A2224]">{u.name || u.username}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-[#5b4d4f]">{u.username}</td>
                      <td className="px-4 py-3 hidden md:table-cell"><span className={`px-2.5 py-1 rounded-full text-xs ${u.role === "viewer" ? "bg-[#EFEDF6] text-[#6b5bd6]" : "bg-[#EEF2FB] text-[#3b5bdb]"}`}>{u.role === "viewer" ? "Sadece Görüntüleme" : "Tam Yetki"}</span></td>
                      <td className="px-4 py-3 hidden lg:table-cell text-[#7C6E70]">{u.createdAt ? new Date(u.createdAt).toLocaleDateString("tr-TR") : "-"}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1.5">
                          <button onClick={() => updateUserPassword(u)} title="Şifre değiştir" className="w-8 h-8 rounded-lg bg-[#F6EAD0] text-[#8a6d1f] flex items-center justify-center hover:bg-[#f0dfb8]"><Lock className="w-4 h-4" /></button>
                          <button onClick={() => deleteAdminUser(u)} title="Sil" className="w-8 h-8 rounded-lg bg-[#FBE9EA] text-[#c0392b] flex items-center justify-center hover:bg-[#f5d7d9]"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        )}

        {section === "sahiplenme" && (
        <div className="space-y-4" data-testid="admin-claim-requests">
          <div>
            <h3 className="font-display text-xl text-[#2A2224]">Sahiplenme Talepleri</h3>
            <p className="text-sm text-[#7C6E70] mt-1">Firmaların "Firmanı Sahiplen" talepleri. Onayladığınızda ilan bilgileri ilgili firma hesabına aktarılır.</p>
          </div>

          <div className="flex flex-wrap gap-1.5" data-testid="claim-filter">
            {[
              { k: "pending", label: "Bekleyen" },
              { k: "approved", label: "Onaylanan" },
              { k: "rejected", label: "Reddedilen" },
              { k: "all", label: "Tümü" },
            ].map((f) => (
              <button
                key={f.k}
                onClick={() => setClaimFilter(f.k)}
                data-testid={`claim-filter-${f.k}`}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${claimFilter === f.k ? "bg-[#9E2A3B] text-white shadow-sm" : "bg-white text-[#5b4d4f] border border-[#F0DDE0] hover:border-[#E7A9B0]"}`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {claimRequests.length === 0 ? (
            <div className="py-16 text-center text-[#7C6E70] bg-white rounded-2xl border border-[#F0DDE0]" data-testid="claim-empty">
              <BadgeCheck className="w-9 h-9 mx-auto mb-2 text-[#E7A9B0]" /> Bu filtrede talep yok.
            </div>
          ) : (
            <div className="space-y-3">
              {claimRequests.map((r) => {
                const st = r.status || "pending";
                const stBadge = st === "approved"
                  ? { c: "bg-[#E7F6EC] text-[#237045] border-[#B9E3C6]", t: "Onaylandı" }
                  : st === "rejected"
                  ? { c: "bg-[#FBEEF0] text-[#9E2A3B] border-[#E7A9B0]", t: "Reddedildi" }
                  : { c: "bg-[#FBF3E4] text-[#8a6d1f] border-[#E3CE9E]", t: "Bekliyor" };
                return (
                <div key={r.id} data-testid={`claim-row-${r.id}`} className="bg-white rounded-2xl border border-[#F0DDE0] p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium text-[#2A2224]">
                        <span className="text-[#9E2A3B]">{r.firm_name || "Firma"}</span>
                        <span className="text-[#a8999b]"> · {r.firm_phone}</span>
                      </p>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${stBadge.c}`}>{stBadge.t}</span>
                    </div>
                    <p className="text-sm text-[#5b4d4f] mt-0.5">
                      Talep edilen ilan: <span className="font-medium">{r.venue_name}</span>
                      {r.city ? ` · ${[r.district, r.city].filter(Boolean).join(", ")}` : ""}
                    </p>
                    <div className="flex items-center gap-3 mt-1">
                      <a href={`/mekan/${r.venue_slug}`} target="_blank" rel="noreferrer" className="text-xs text-[#9E2A3B] hover:underline inline-flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> İlanı Gör</a>
                      <span className="text-xs text-[#a8999b]">{r.createdAt ? new Date(r.createdAt).toLocaleString("tr-TR") : ""}</span>
                      {r.decidedAt && <span className="text-xs text-[#a8999b]">· Karar: {new Date(r.decidedAt).toLocaleString("tr-TR")}</span>}
                    </div>
                  </div>
                  {st === "pending" && (
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => approveClaim(r)} data-testid={`claim-approve-${r.id}`}
                      className="flex items-center gap-1.5 px-4 h-10 rounded-xl bg-gradient-to-r from-[#2E8B57] to-[#237045] text-white text-sm font-medium hover:shadow-md transition-all">
                      <CheckCircle2 className="w-4 h-4" /> Onayla
                    </button>
                    <button onClick={() => rejectClaim(r)} data-testid={`claim-reject-${r.id}`}
                      className="flex items-center gap-1.5 px-4 h-10 rounded-xl border border-[#E7A9B0] text-[#9E2A3B] text-sm font-medium hover:bg-[#FBF0F2] transition-colors">
                      <X className="w-4 h-4" /> Reddet
                    </button>
                  </div>
                  )}
                </div>
                );
              })}
            </div>
          )}
        </div>
        )}

        {section === "toplu-yukleme" && !isViewer && (
          <BulkFirmImport headers={headers} toast={toast} onDone={() => { setStatusFilter("pending"); setSection("isletmeler"); }} />
        )}

        {section === "ozellik-talepleri" && (
        <div className="space-y-4" data-testid="admin-feature-add-requests">
          <div>
            <h3 className="font-display text-xl text-[#2A2224]">Özellik Ekleme Talebi</h3>
            <p className="text-sm text-[#7C6E70] mt-1">Firmaların listede olmayan yeni özellik/hizmet talepleri. Onayladığınızda özellik ilgili grubun (Mekan/Firma) ortak özellik listesine eklenir ve tüm firmalar seçebilir.</p>
          </div>

          <div className="flex flex-wrap gap-1.5" data-testid="feat-req-filter">
            {[
              { k: "pending", label: "Bekleyen" },
              { k: "approved", label: "Onaylanan" },
              { k: "rejected", label: "Reddedilen" },
              { k: "all", label: "Tümü" },
            ].map((f) => {
              const cnt = f.k === "all" ? featureAddRequests.length : featureAddRequests.filter((r) => (r.status || "pending") === f.k).length;
              return (
                <button
                  key={f.k}
                  onClick={() => setFeatReqFilter(f.k)}
                  data-testid={`feat-req-filter-${f.k}`}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${featReqFilter === f.k ? "bg-[#9E2A3B] text-white shadow-sm" : "bg-white text-[#5b4d4f] border border-[#F0DDE0] hover:border-[#E7A9B0]"}`}
                >
                  {f.label} ({cnt})
                </button>
              );
            })}
          </div>

          {(() => {
            const filteredFeatReqs = featReqFilter === "all" ? featureAddRequests : featureAddRequests.filter((r) => (r.status || "pending") === featReqFilter);
            return filteredFeatReqs.length === 0 ? (
            <div className="py-16 text-center text-[#7C6E70] bg-white rounded-2xl border border-[#F0DDE0]" data-testid="feature-add-empty">
              <Tag className="w-9 h-9 mx-auto mb-2 text-[#E7A9B0]" /> Bu filtrede özellik talebi yok.
            </div>
          ) : (
            <div className="space-y-3">
              {filteredFeatReqs.map((r) => {
                const st = r.status || "pending";
                const stBadge = st === "approved"
                  ? { c: "bg-[#E7F6EC] text-[#237045] border-[#B9E3C6]", t: "Onaylandı" }
                  : st === "rejected"
                  ? { c: "bg-[#FBEEF0] text-[#9E2A3B] border-[#E7A9B0]", t: "Reddedildi" }
                  : { c: "bg-[#FBF3E4] text-[#8a6d1f] border-[#E3CE9E]", t: "Bekliyor" };
                const grpLabel = r.group === "firma" ? "Düğün Firmaları" : "Düğün Mekanları";
                return (
                <div key={r.id} data-testid={`feature-add-row-${r.id}`} className="bg-white rounded-2xl border border-[#F0DDE0] p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {st === "pending" ? (
                        <input
                          data-testid={`feature-add-edit-${r.id}`}
                          value={featureEdits[r.id] ?? r.feature ?? ""}
                          onChange={(e) => setFeatureEdits((p) => ({ ...p, [r.id]: e.target.value }))}
                          placeholder="Özellik adı"
                          className="h-9 px-3 rounded-lg border border-[#F0DDE0] bg-[#FBF5F0] outline-none text-sm font-medium text-[#2A2224] focus:border-[#C15B6B] min-w-[180px]"
                        />
                      ) : (
                        <p className="font-medium text-[#2A2224]">"{r.feature}"</p>
                      )}
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${stBadge.c}`}>{stBadge.t}</span>
                      <span className="text-xs font-medium px-2 py-0.5 rounded-full border border-[#E7D5A6] bg-[#FBF7EC] text-[#8a6d1f]">{grpLabel}</span>
                    </div>
                    <p className="text-sm text-[#5b4d4f] mt-1">
                      Talep eden: <span className="font-medium text-[#9E2A3B]">{r.firm_name || "Firma"}</span>
                    </p>
                    {st === "pending" && <p className="text-xs text-[#9c8f90] mt-0.5">Gerekirse özelliği düzenleyip öyle onaylayabilirsiniz.</p>}
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-xs text-[#a8999b]">{r.createdAt ? new Date(r.createdAt).toLocaleString("tr-TR") : ""}</span>
                      {r.decidedAt && <span className="text-xs text-[#a8999b]">· Karar: {new Date(r.decidedAt).toLocaleString("tr-TR")}</span>}
                    </div>
                  </div>
                  {st === "pending" && (
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => approveFeatureAddRequest(r)} data-testid={`feature-add-approve-${r.id}`}
                      className="flex items-center gap-1.5 px-4 h-10 rounded-xl bg-gradient-to-r from-[#2E8B57] to-[#237045] text-white text-sm font-medium hover:shadow-md transition-all">
                      <CheckCircle2 className="w-4 h-4" /> Onayla
                    </button>
                    <button onClick={() => rejectFeatureAddRequest(r)} data-testid={`feature-add-reject-${r.id}`}
                      className="flex items-center gap-1.5 px-4 h-10 rounded-xl border border-[#E7A9B0] text-[#9E2A3B] text-sm font-medium hover:bg-[#FBF0F2] transition-colors">
                      <X className="w-4 h-4" /> Reddet
                    </button>
                  </div>
                  )}
                </div>
                );
              })}
            </div>
          );
          })()}
        </div>
        )}

        {section === "islem" && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-[#9E2A3B]" />
            <h3 className="font-display text-xl text-[#2A2224]">İşlem Kaydı</h3>
            <span className="px-2 py-0.5 rounded-full bg-[#F6E4E6] text-[#9E2A3B] text-xs font-semibold">{activity.length}</span>
          </div>
          <p className="text-sm text-[#7C6E70]">Panelde hangi kullanıcının ne yaptığını gösteren aktivite günlüğü.</p>
          <div className="bg-white rounded-2xl border border-[#F0DDE0] overflow-hidden">
            {activity.length === 0 ? (
              <div className="py-16 text-center text-[#7C6E70]"><Clock className="w-9 h-9 mx-auto mb-2 text-[#E7A9B0]" /> Henüz kayıt yok.</div>
            ) : (
              <ul className="divide-y divide-[#F0DDE0]">
                {activity.map((a) => (
                  <li key={a.id} className="flex items-start gap-3 px-5 py-3">
                    <div className="w-9 h-9 rounded-full bg-[#F6E4E6] text-[#9E2A3B] flex items-center justify-center font-medium shrink-0 text-sm">{(a.user || "?").charAt(0).toUpperCase()}</div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-[#2A2224]"><b>{a.user}</b> · {a.action}</p>
                      {a.detail && <p className="text-xs text-[#7C6E70] truncate">{a.detail}</p>}
                    </div>
                    <span className="text-xs text-[#a8999b] shrink-0">{a.at ? new Date(a.at).toLocaleString("tr-TR") : ""}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        )}

        {section === "odemeler" && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-[#E9F7EF] flex items-center justify-center shrink-0">
              <CreditCard className="w-6 h-6 text-[#1EBE5A]" />
            </div>
            <div>
              <h3 className="font-display text-lg text-[#2A2224]">Ödemeler</h3>
              <p className="text-sm text-[#7C6E70]">Firmaların yaptığı tüm ödemeler (Premium, Ek Kategori, Vurgulu İlan, Öne Çıkma). Toplam {payments.length} ödeme.</p>
            </div>
          </div>

          {/* Filtre + Ciro */}
          <div className="flex flex-col md:flex-row md:items-end gap-3 bg-white rounded-2xl border border-[#F0DDE0] p-4">
            <div className="space-y-1.5">
              <label className="text-xs text-[#7C6E70]">Başlangıç</label>
              <input type="date" value={payFrom} onChange={(e) => setPayFrom(e.target.value)} data-testid="payments-from"
                className="h-10 px-3 rounded-lg border border-[#F0DDE0] text-sm outline-none focus:border-[#C15B6B]" />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs text-[#7C6E70]">Bitiş</label>
              <input type="date" value={payTo} onChange={(e) => setPayTo(e.target.value)} data-testid="payments-to"
                className="h-10 px-3 rounded-lg border border-[#F0DDE0] text-sm outline-none focus:border-[#C15B6B]" />
            </div>
            {(payFrom || payTo) && (
              <button onClick={() => { setPayFrom(""); setPayTo(""); }} data-testid="payments-clear"
                className="h-10 px-4 rounded-lg border border-[#F0DDE0] text-sm text-[#7C6E70] hover:bg-[#FBF0F2] transition-colors">
                Temizle
              </button>
            )}
            <div className="md:ml-auto rounded-xl bg-gradient-to-br from-[#E9F7EF] to-[#d5f0e0] px-5 py-3 text-right" data-testid="payments-total">
              <p className="text-xs text-[#2E7D5B]">Toplam Ciro{(payFrom || payTo) ? " (seçili aralık)" : ""}</p>
              <p className="font-display text-2xl font-bold text-[#1a7d4a]">{new Intl.NumberFormat("tr-TR").format(totalRevenue)} ₺</p>
              <p className="text-xs text-[#2E7D5B]">{filteredPayments.length} ödeme</p>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-[#F0DDE0] overflow-hidden">
            {payments.length === 0 ? (
              <div className="py-16 text-center text-[#7C6E70]" data-testid="payments-empty"><CreditCard className="w-9 h-9 mx-auto mb-2 text-[#E7A9B0]" /> Henüz ödeme yapan firma yok.</div>
            ) : filteredPayments.length === 0 ? (
              <div className="py-16 text-center text-[#7C6E70]" data-testid="payments-none-range"><CreditCard className="w-9 h-9 mx-auto mb-2 text-[#E7A9B0]" /> Seçilen tarih aralığında ödeme bulunamadı.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="payments-table">
                  <thead>
                    <tr className="text-left text-[#7C6E70] border-b border-[#F0DDE0]">
                      <th className="px-5 py-3 font-medium">Firma</th>
                      <th className="px-5 py-3 font-medium">Telefon</th>
                      <th className="px-5 py-3 font-medium">Tür</th>
                      <th className="px-5 py-3 font-medium">Tutar</th>
                      <th className="px-5 py-3 font-medium">Tarih</th>
                      <th className="px-5 py-3 font-medium">Üyelik Bitişi</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#F0DDE0]">
                    {filteredPayments.map((p) => (
                      <tr key={p.orderId} className="hover:bg-[#FBF5F0]">
                        <td className="px-5 py-3">
                          {p.firmSlug ? <Link to={`/mekan/${p.firmSlug}`} className="text-[#9E2A3B] font-medium hover:underline">{p.firmName}</Link> : <span className="text-[#2A2224] font-medium">{p.firmName}</span>}
                        </td>
                        <td className="px-5 py-3 text-[#5b4d4f]">{p.phone || "—"}</td>
                        <td className="px-5 py-3">
                          {(() => {
                            const map = { membership: ["Premium Üyelik", "bg-[#FBEEF0] text-[#9E2A3B]"], category: ["Ek Kategori", "bg-[#EEF2FB] text-[#3b5bdb]"], highlight: ["Vurgulu İlan", "bg-[#FBF3E4] text-[#8a6d1f]"], featured: ["Öne Çıkma", "bg-[#E7F7EE] text-[#1FA855]"] };
                            const [lbl, cls] = map[p.type] || [p.description || "Ödeme", "bg-[#F3ECEE] text-[#5b4d4f]"];
                            return <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${cls}`}>{lbl}</span>;
                          })()}
                        </td>
                        <td className="px-5 py-3 font-semibold text-[#9E2A3B]">{new Intl.NumberFormat("tr-TR").format(Number(p.amount) || 0)} ₺</td>
                        <td className="px-5 py-3 text-[#5b4d4f]">{p.paidAt ? new Date(p.paidAt).toLocaleString("tr-TR") : "—"}</td>
                        <td className="px-5 py-3 text-[#5b4d4f]">{p.premiumUntil ? new Date(p.premiumUntil).toLocaleDateString("tr-TR") : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
        )}

          </div>
        </div>
      </div>

      <Dialog open={memberDetailOpen} onOpenChange={(o) => { if (!o) { setMemberDetailOpen(false); setMemberDetail(null); } }}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="member-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display text-[#2A2224]">
              <Users className="w-5 h-5 text-[#9E2A3B]" /> {memberDetail?.name || "Üye Detayı"}
            </DialogTitle>
          </DialogHeader>
          {memberDetailLoading ? (
            <div className="py-16 flex justify-center"><Loader2 className="w-7 h-7 text-[#9E2A3B] animate-spin" /></div>
          ) : memberDetail ? (
            <div className="space-y-5" data-testid="member-detail-body">
              {/* Üst bilgi */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                <div><p className="text-xs text-[#9c8f90]">Telefon</p><p className="text-[#2A2224]">{memberDetail.phone || "—"}</p></div>
                <div><p className="text-xs text-[#9c8f90]">Şehir</p><p className="text-[#2A2224]">{memberDetail.city || "—"}</p></div>
                <div><p className="text-xs text-[#9c8f90]">Kayıt</p><p className="text-[#2A2224]">{memberDetail.createdAt ? new Date(memberDetail.createdAt).toLocaleDateString("tr-TR") : "—"}</p></div>
                <div><p className="text-xs text-[#9c8f90]">Son Aktivite</p><p className="text-[#2A2224]">{memberDetail.lastActive ? new Date(memberDetail.lastActive).toLocaleDateString("tr-TR") : "—"}</p></div>
              </div>

              {/* Aktivite kartları */}
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2" data-testid="member-detail-stats">
                {[
                  { label: "Favori", value: memberDetail.stats?.favorites, color: "#9E2A3B" },
                  { label: "Teklif", value: memberDetail.stats?.quotes, color: "#3b5bdb" },
                  { label: "Yorum", value: memberDetail.stats?.reviews, color: "#C6A15B" },
                  { label: "Araç", value: memberDetail.stats?.tools, color: "#2E8B57" },
                  { label: "Davetiye", value: memberDetail.stats?.hasInvitation ? "Var" : "Yok", color: "#C15B6B" },
                  { label: "RSVP", value: memberDetail.stats?.rsvps, color: "#7C6E70" },
                ].map((s) => (
                  <div key={s.label} className="rounded-xl border border-[#F0DDE0] p-2.5 text-center">
                    <p className="font-display text-lg" style={{ color: s.color }}>{s.value ?? 0}</p>
                    <p className="text-[11px] text-[#7C6E70]">{s.label}</p>
                  </div>
                ))}
              </div>

              {/* Teklif aldığı firmalar */}
              <div>
                <h4 className="text-sm font-semibold text-[#2A2224] mb-2 flex items-center gap-1.5"><Mail className="w-4 h-4 text-[#3b5bdb]" /> Teklif Aldığı Firmalar ({memberDetail.quotes?.length || 0})</h4>
                {(memberDetail.quotes || []).length === 0 ? (
                  <p className="text-sm text-[#9c8f90]">Henüz teklif talebi yok.</p>
                ) : (
                  <div className="space-y-2" data-testid="member-detail-quotes">
                    {memberDetail.quotes.map((q) => (
                      <div key={q.id} className="flex items-start justify-between gap-3 p-2.5 rounded-xl border border-[#F0DDE0]">
                        <div className="min-w-0">
                          <p className="font-medium text-[#2A2224] text-sm">{q.firm_name || "Firma"}</p>
                          {q.message && <p className="text-xs text-[#7C6E70] truncate">{q.message}</p>}
                        </div>
                        <span className="text-xs text-[#9c8f90] shrink-0">{q.at ? new Date(q.at).toLocaleDateString("tr-TR") : ""}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Favorilediği firmalar */}
              <div>
                <h4 className="text-sm font-semibold text-[#2A2224] mb-2 flex items-center gap-1.5"><Heart className="w-4 h-4 text-[#9E2A3B]" /> Favori Firmalar ({memberDetail.favorites?.length || 0})</h4>
                {(memberDetail.favorites || []).length === 0 ? (
                  <p className="text-sm text-[#9c8f90]">Favori firma yok.</p>
                ) : (
                  <div className="flex flex-wrap gap-2" data-testid="member-detail-favorites">
                    {memberDetail.favorites.map((f) => (
                      <a key={f.slug} href={`/mekan/${f.slug}`} target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-1.5 bg-white border border-[#F0DDE0] rounded-full px-3 py-1.5 text-xs text-[#2A2224] hover:border-[#C6A15B]">
                        <Heart className="w-3 h-3 text-[#9E2A3B]" fill="currentColor" />
                        {f.name}{f.city ? <span className="text-[#9c8f90]">· {f.city}</span> : null}
                      </a>
                    ))}
                  </div>
                )}
              </div>

              {/* Kullandığı araçlar */}
              {(memberDetail.tools || []).length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-[#2A2224] mb-2 flex items-center gap-1.5"><Sparkles className="w-4 h-4 text-[#2E8B57]" /> Kullandığı Araçlar</h4>
                  <div className="flex flex-wrap gap-2" data-testid="member-detail-tools">
                    {memberDetail.tools.map((t) => (
                      <span key={t.key} className="inline-flex items-center gap-1.5 bg-[#E7F6EC] border border-[#cdead8] rounded-full px-3 py-1.5 text-xs text-[#237045]">{t.label}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Yorumlar */}
              {(memberDetail.reviews || []).length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-[#2A2224] mb-2 flex items-center gap-1.5"><MessageCircle className="w-4 h-4 text-[#C6A15B]" /> Yorumları ({memberDetail.reviews.length})</h4>
                  <div className="space-y-2" data-testid="member-detail-reviews">
                    {memberDetail.reviews.map((r) => (
                      <div key={r.id} className="p-2.5 rounded-xl border border-[#F0DDE0]">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-medium text-[#2A2224]">{r.firm_name || r.firm_slug}</p>
                          <span className="text-xs text-[#C6A15B]">{"★".repeat(r.rating || 0)}</span>
                        </div>
                        {r.comment && <p className="text-xs text-[#7C6E70] mt-0.5">{r.comment}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p className="py-10 text-center text-[#7C6E70]">Bilgi bulunamadı.</p>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!notesFirm} onOpenChange={(o) => { if (!o) setNotesFirm(null); }}>
        <DialogContent className="sm:max-w-lg" data-testid="firm-notes-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display text-[#2A2224]">
              <StickyNote className="w-5 h-5 text-[#C6A15B]" /> Notlar — {notesFirm?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex flex-col gap-2">
              <Textarea
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                data-testid="firm-note-input"
                rows={3}
                placeholder="İşletmeyle ilgili not yazın (ör. görüşme, ödeme sözü, özel istek)..."
                className="border-[#F0DDE0] focus-visible:ring-[#C15B6B]"
              />
              <button
                onClick={addNote}
                disabled={notesBusy || !newNote.trim()}
                data-testid="firm-note-add-btn"
                className="self-end flex items-center gap-2 px-4 h-10 rounded-xl bg-gradient-to-r from-[#C15B6B] to-[#9E2A3B] text-white text-sm font-medium hover:shadow-md transition-all disabled:opacity-70"
              >
                {notesBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <StickyNote className="w-4 h-4" />} Not Ekle
              </button>
            </div>
            <div className="max-h-72 overflow-y-auto space-y-2 pr-1">
              {notesList.length === 0 ? (
                <p className="text-sm text-[#a8999b] py-6 text-center">Henüz not eklenmedi.</p>
              ) : (
                notesList.map((n) => (
                  <div key={n.id} className="rounded-xl border border-[#F0DDE0] bg-[#FBF5F0] px-3 py-2" data-testid={`firm-note-${n.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm text-[#2A2224] whitespace-pre-wrap flex-1">{n.text}</p>
                      <button onClick={() => deleteNote(n.id)} title="Sil" className="text-[#c0392b] hover:bg-[#FBE9EA] rounded p-1 shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                    <p className="text-[11px] text-[#a8999b] mt-1">{n.author} · {n.at ? new Date(n.at).toLocaleString("tr-TR") : ""}</p>
                  </div>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPanel;
